import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useMediaQuery } from '@/lib/hooks';
import {
    IconArrowLeft, IconPhoto, IconTrash, IconDeviceFloppy, IconSparkles,
    IconFlask, IconDownload, IconUpload, IconRefresh, IconPlus,
    IconGripVertical, IconCopy, IconCheck, IconWand, IconChevronDown, IconX,
    IconStarFilled, IconPencil, IconList, IconShoppingBag,
    IconUsers, IconCalendar, IconPhotoPlus, IconBraces,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
    arrayMove, SortableContext, sortableKeyboardCoordinates,
    useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CropStage from '../../user/generate-image/CropStage';
import ColorSwatchPicker from '../../../components/common/ColorSwatchPicker';
import PageHeader from '../../../components/common/PageHeader';
import SearchableSelect from '../../../components/common/SearchableSelect';
import aiTemplateService from '../../../services/aiTemplateService';
import { IMAGE_INPUT_LABELS, isImageInputLabelKey } from '../../../data/imageInputLabels';
import { REFERENCE_MODELS } from '../../../data/referenceModels';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { useAiTemplateCacheStore } from '../../../store/aiTemplateCacheStore';
import aiTemplateCategoryService from '../../../services/aiTemplateCategoryService';
import imageGenerationService from '../../../services/imageGenerationService';
import { optimizeImage } from '../../../utils/optimizeImage';
import { mergeImagesToGrid } from '../../../utils/mergeImagesToGrid';
import { isAiCapacityError } from '../../../utils/aiError';
import { todayISO, formatPromptDate, DATE_VARIANTS, dateVariantValues } from '../../../utils/promptDate';
import { slugify } from '../../../utils/slugifyBn';
import { sanitizeText } from '../../../utils/sanitize';
import { estimateTokens } from '../../../utils/estimateTokens';
import { toBnDigits } from '../../../utils/bnFormat';
import BulkTemplateCreatePanel, { MAX_BULK_IMAGES } from './BulkTemplateCreatePanel';
import { GENDER_OPTIONS, ANALYZE_FIELD_TYPE_OPTIONS } from '../../../data/aiTemplateAnalyzeOptions';

const toKey = (label) => String(label || '')
    .toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-+|-+$/g, '');

const uid = () => Math.random().toString(36).slice(2, 9);

// Break a slash-token string onto one token per line for readability:
//   "/preserveFace, /handPaintedWatercolor" → "/preserveFace,\n/handPaintedWatercolor"
// Only splits before a token boundary (", /token"), so {key} placeholders and
// colon-values like `traditionalOutfit:{dress-change}` stay on their own line intact.
const tokensToLines = (s) => String(s || '').replace(/,[ \t]*\/(?=[A-Za-z])/g, ',\n/').trim();

// camelCase a field key: "dress-change" → "dressChange", "background_color" → "backgroundColor".
const camelCaseKey = (key) => String(key || '').split(/[-_\s]+/).filter(Boolean)
    .map((w, i) => i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join('');

// Make the {field} tokens for SELF-DESCRIBING fields (dropdown/options, whose value is
// a full descriptive phrase) use a NEUTRAL descriptor keyed to the field itself —
// e.g. "/traditionalSaree:{dress-change}" → "/dressChange:{dress-change}".
// The AI tokenizer bakes a specific value (traditionalSaree) into the token wrapping a
// dropdown field. At generation the user's real choice REPLACES the placeholder, so the
// hardcoded descriptor fights the selected value (the dress stays a saree even after
// picking salwar kameez). Keying the descriptor to the field lets the choice drive it.
// NOTE: only pass self-describing keys here — a colorpicker token like
// "/abstractBotanicalBackground:{background-color}" carries a real style (the value is
// only a colour), so its descriptor must be kept.
const alignFieldTokens = (text, keys) => {
    let out = String(text || '');
    for (const key of keys || []) {
        const camel = camelCaseKey(key);
        if (!camel) continue;
        const esc = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Replace an existing "/anyDescriptor:{key}" (colon optional) with "/camel:{key}".
        out = out.replace(new RegExp(`/[A-Za-z0-9]+\\s*:?\\s*\\{${esc}\\}`, 'g'), `/${camel}:{${key}}`);
        // Give a bare "{key}" (no leading descriptor) one too.
        out = out.replace(new RegExp(`(^|[\\s,\\n])\\{${esc}\\}`, 'g'), `$1/${camel}:{${key}}`);
    }
    return out;
};

// Turn a free "avoid" list (comma / newline separated phrases) into slash tokens,
// one per line:  "blurry face, extra fingers" → "/blurryFace,\n/extraFingers"
const toSlashTokenList = (text) => {
    const seen = new Set();
    const tokens = [];
    for (const part of String(text || '').split(/[,\n]+/)) {
        const words = part.replace(/[^a-zA-Z0-9\s]/g, ' ').split(/\s+/).filter(Boolean);
        if (!words.length) continue;
        const camel = words.map((w, i) => i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join('');
        const tok = '/' + camel;
        if (seen.has(tok)) continue;
        seen.add(tok);
        tokens.push(tok);
    }
    return tokens.join(',\n');
};

// ── Shared design tokens ───────────────────────────────────────────────────
const inputCls = "w-full px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40";
const inputXsCls = "w-full px-2 py-1.5 border border-zinc-200 dark:border-zinc-700 rounded-lg text-xs bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-primary/40";
const labelCls = "block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1";
const labelXsCls = "block text-xs font-medium mb-0.5 text-zinc-600 dark:text-zinc-400";
const cardCls = "bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-4 sm:p-5 space-y-4";
const cardTitleCls = "text-base font-semibold text-zinc-800 dark:text-zinc-100";
const btnPrimaryCls = "inline-flex items-center justify-center gap-1.5 px-5 py-2 bg-primary hover:bg-primary/90 disabled:opacity-60 text-white text-sm font-semibold rounded-lg transition-colors";
const btnSecondaryCls = "inline-flex items-center justify-center gap-1.5 px-4 py-2 border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 text-sm rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors";

// ── Sortable dynamic-field card ────────────────────────────────────────────
// One draggable row inside a dropdown/options choices editor. Reordering here changes
// the choice order everywhere — the saved template AND the user-facing dropdown render
// in this exact order — and the FIRST choice is the pre-selected default.
function SortableChoiceRow({ id, isDefault, children }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 2 : undefined,
    };
    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`flex items-start gap-1 ${isDefault ? 'rounded-lg bg-emerald-50/70 dark:bg-emerald-900/15 border border-emerald-200 dark:border-emerald-800/50 p-1' : ''}`}
        >
            <button
                type="button"
                {...attributes}
                {...listeners}
                title="Drag to reorder"
                className="mt-1.5 flex-shrink-0 cursor-grab active:cursor-grabbing touch-none text-zinc-300 hover:text-zinc-500 dark:text-zinc-600 dark:hover:text-zinc-400"
            >
                <IconGripVertical size={12} />
            </button>
            <div className="flex-1 min-w-0 flex flex-col gap-0.5">{children}</div>
        </div>
    );
}

// ── Master-detail field list row (drag to reorder, click to edit) ─────────
// ── Field JSON copy/paste ───────────────────────────────────────────────────
// A field's shareable JSON is ONLY these props — `id` and `_keyTouched` stay
// local to the editing session. Both serialize and parse go through this
// whitelist so every field produces (and accepts) the same pattern.
const FIELD_JSON_KEYS = [
    'label', 'labelBn', 'key', 'type', 'placeholder', 'placeholderBn',
    'required', 'charLimit', 'minLength', 'minValue', 'maxValue',
    'choices', 'defaultColor', 'suggestions', 'dateAuto', 'dateLang', 'dateVariants',
];
const FIELD_JSON_TYPES = ['text', 'textarea', 'number', 'dropdown', 'options', 'colorpicker', 'date'];

// Fresh-field defaults — shared by add/quick-add and by JSON apply so a pasted
// JSON fully REPLACES the field (props the JSON omits reset to defaults).
const EMPTY_FIELD_BASE = {
    label: '', labelBn: '', key: '', type: 'text',
    placeholder: '', placeholderBn: '',
    required: false, charLimit: 50, minLength: 0,
    minValue: null, maxValue: null,
    choices: [], defaultColor: '#000000',
    suggestions: [], dateAuto: true, dateLang: 'bn', dateVariants: ['date-en-bn'],
};

const fieldToJson = (field) => {
    const out = {};
    FIELD_JSON_KEYS.forEach(k => { if (field[k] !== undefined) out[k] = field[k]; });
    return JSON.stringify(out, null, 2);
};

// Returns { data } (whitelisted props only) or { error: true } when the text
// isn't field JSON — random JSON without a valid `type` is rejected.
const parseFieldJson = (text) => {
    let obj;
    try { obj = JSON.parse(text); } catch { return { error: true }; }
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return { error: true };
    if (!FIELD_JSON_TYPES.includes(obj.type)) return { error: true };
    const data = {};
    FIELD_JSON_KEYS.forEach(k => { if (obj[k] !== undefined) data[k] = obj[k]; });
    if (typeof (data.key ?? '') !== 'string' || typeof (data.label ?? '') !== 'string') return { error: true };
    if (data.choices !== undefined && !Array.isArray(data.choices)) return { error: true };
    if (data.suggestions !== undefined && !Array.isArray(data.suggestions)) return { error: true };
    return { data };
};

function SortableFieldListItem({ field, selected, onSelect, onDelete, labelLang }) {
    const { t } = useTranslation();
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.id });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1, zIndex: isDragging ? 1 : undefined };
    const label = ((labelLang === 'bn' && field.labelBn) ? field.labelBn : field.label) || field.label || field.labelBn || field.key || '\u2026';
    return (
        <div
            ref={setNodeRef}
            style={style}
            onClick={onSelect}
            className={`group w-full flex items-center gap-1.5 rounded-lg border px-2.5 py-2 cursor-pointer transition-colors ${selected
                ? 'border-primary/50 bg-orange-50 dark:bg-orange-900/15'
                : 'border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 hover:border-primary/40'}`}
        >
            <span {...attributes} {...listeners} onClick={e => e.stopPropagation()} className="cursor-grab touch-none text-zinc-300 dark:text-zinc-600 flex-shrink-0">
                <IconGripVertical size={13} />
            </span>
            <span className="flex-1 min-w-0">
                <span className="flex items-center justify-between gap-1">
                    <span className="text-[13px] font-semibold text-zinc-800 dark:text-zinc-100 truncate">{label}</span>
                    <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-primary/10 text-primary dark:bg-primary/20 flex-shrink-0">{field.type}</span>
                </span>
                <span className="block text-[11px] font-mono text-zinc-400 dark:text-zinc-500 truncate">{field.key}</span>
            </span>
            {onDelete && (
                <button
                    type="button"
                    onClick={e => { e.stopPropagation(); onDelete(); }}
                    className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 p-1 rounded text-zinc-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-opacity flex-shrink-0"
                    title={t('aiTemplateForm.deleteFieldTooltip')}
                >
                    <IconTrash size={13} />
                </button>
            )}
        </div>
    );
}

function SortableFieldCard({ field, idx, copiedKey, setCopiedKey, updateField, removeField, onApplyJson, prompt = '', onSync, syncing = false, onSuggestChoice, labelLang = 'en' }) {
    const { t } = useTranslation();
    const [collapsed, setCollapsed] = useState(false);
    const [suggesting, setSuggesting] = useState(false);
    // Field JSON panel — textarea is prefilled with this field's JSON (for copying)
    // and accepts another field's JSON to apply here. Refreshed on every open.
    const [jsonOpen, setJsonOpen] = useState(false);
    const [jsonText, setJsonText] = useState('');
    const [jsonError, setJsonError] = useState(false);
    const [jsonCopied, setJsonCopied] = useState(false);
    const toggleJson = () => {
        setJsonOpen(o => {
            if (!o) { setJsonText(fieldToJson(field)); setJsonError(false); }
            return !o;
        });
    };
    const copyJson = async () => {
        try { await navigator.clipboard.writeText(jsonText); } catch { /* ignore */ }
        setJsonCopied(true);
        setTimeout(() => setJsonCopied(false), 1500);
    };
    const applyJson = () => {
        const { data, error } = parseFieldJson(jsonText);
        if (error) { setJsonError(true); return; }
        setJsonError(false);
        onApplyJson?.(field.id, data);
        setJsonText(JSON.stringify(data, null, 2));
        notifications.show({ message: t('aiTemplateForm.fieldJsonApplied'), color: 'green' });
    };
    const handleSuggestChoice = async () => {
        if (suggesting || !onSuggestChoice) return;
        setSuggesting(true);
        try { await onSuggestChoice(field); } finally { setSuggesting(false); }
    };
    // Is this field's {key} already present in the prompt?
    const inPrompt = !!(field.key && prompt.includes(`{${field.key}}`));
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.id });
    const dragStyle = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        zIndex: isDragging ? 1 : undefined,
    };

    const isTextLike = field.type === 'text' || field.type === 'textarea';
    const isTextarea = field.type === 'textarea';
    const isNumber   = field.type === 'number';
    const hasChoices = field.type === 'dropdown' || field.type === 'options';
    const isColor    = field.type === 'colorpicker';
    const isDate     = field.type === 'date';

    // Drag-to-reorder the dropdown/options choices. A small pointer distance keeps the
    // grip from stealing clicks meant for the inputs. Order is persisted as-is.
    const choiceSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
    const onChoiceDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const from = Number(String(active.id).replace('choice-', ''));
        const to = Number(String(over.id).replace('choice-', ''));
        if (Number.isInteger(from) && Number.isInteger(to)) {
            updateField(field.id, 'choices', arrayMove(field.choices || [], from, to));
        }
    };

    return (
        <div
            ref={setNodeRef}
            style={dragStyle}
            className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-lg p-3"
        >
            {/* Header */}
            <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2">
                    <div
                        {...attributes}
                        {...listeners}
                        className="cursor-grab flex items-center touch-none text-zinc-400"
                    >
                        <IconGripVertical size={14} />
                    </div>
                    <span className="text-xs font-semibold text-zinc-500 dark:text-zinc-400">{t('aiTemplateForm.fieldN', { n: idx + 1 })}</span>
                    {field.required && (
                        <span className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">{t('aiTemplateForm.required')}</span>
                    )}
                    <span className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary dark:bg-primary/20">{field.type}</span>
                    {/* Prompt sync status: badge when {key} is in the prompt, else a Sync button */}
                    {field.key && (inPrompt ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                            <IconCheck size={11} /> {t('aiTemplateForm.synced')}
                        </span>
                    ) : (
                        <button
                            type="button"
                            onClick={() => onSync?.(field)}
                            disabled={syncing || !prompt.trim()}
                            title={!prompt.trim() ? t('aiTemplateForm.syncNoPrompt') : t('aiTemplateForm.syncTooltip')}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-primary hover:bg-primary/90 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {syncing
                                ? <span className="w-3 h-3 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                                : <IconRefresh size={11} />}
                            {t('aiTemplateForm.sync')}
                        </button>
                    ))}
                    {collapsed && (field.label || field.key) && (
                        <span className="text-xs text-zinc-500 dark:text-zinc-400 truncate max-w-[160px]">— {field.label || field.key}</span>
                    )}
                </div>
                <div className="flex items-center gap-0.5">
                    <button
                        type="button"
                        onClick={toggleJson}
                        title={t('aiTemplateForm.fieldJsonTooltip')}
                        className={`p-1 rounded transition-colors ${jsonOpen
                            ? 'text-primary bg-primary/10'
                            : 'text-zinc-400 hover:text-primary hover:bg-zinc-100 dark:hover:bg-zinc-800'}`}
                    >
                        <IconBraces size={14} />
                    </button>
                    <button
                        type="button"
                        onClick={() => setCollapsed(c => !c)}
                        className="p-1 rounded text-zinc-400 hover:text-primary hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                        aria-label={collapsed ? t('aiTemplateForm.expand') : t('aiTemplateForm.collapse')}
                    >
                        <IconChevronDown size={15} className={`transition-transform ${collapsed ? '-rotate-90' : ''}`} />
                    </button>
                    <button
                        onClick={() => removeField(field.id)}
                        className="p-1 rounded text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                        <IconTrash size={13} />
                    </button>
                </div>
            </div>

            {/* Field JSON — copy this field's config, or paste another field's JSON */}
            {jsonOpen && (
                <div className="mb-2 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/60 p-2.5">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                        <span className="text-xs font-semibold text-zinc-700 dark:text-zinc-200">{t('aiTemplateForm.fieldJsonTitle')}</span>
                        <div className="flex items-center gap-1.5">
                            <button
                                type="button"
                                onClick={copyJson}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                            >
                                {jsonCopied ? <IconCheck size={12} className="text-emerald-500" /> : <IconCopy size={12} />}
                                {jsonCopied ? t('aiTemplateForm.fieldJsonCopied') : t('aiTemplateForm.fieldJsonCopy')}
                            </button>
                            <button
                                type="button"
                                onClick={applyJson}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-lg bg-primary hover:bg-primary/90 text-white transition-colors"
                            >
                                <IconCheck size={12} /> {t('aiTemplateForm.fieldJsonApply')}
                            </button>
                        </div>
                    </div>
                    <textarea
                        value={jsonText}
                        onChange={e => { setJsonText(e.target.value); setJsonError(false); }}
                        spellCheck={false}
                        className={`w-full h-44 px-2.5 py-2 rounded-lg text-[11px] font-mono leading-relaxed bg-white dark:bg-zinc-900 text-zinc-800 dark:text-zinc-100 border focus:outline-none focus:ring-2 focus:ring-primary/40 resize-y ${jsonError ? 'border-red-400' : 'border-zinc-200 dark:border-zinc-700'}`}
                    />
                    <p className={`mt-1 text-[11px] ${jsonError ? 'text-red-500 font-medium' : 'text-zinc-500 dark:text-zinc-400'}`}>
                        {jsonError ? t('aiTemplateForm.fieldJsonInvalid') : t('aiTemplateForm.fieldJsonHint')}
                    </p>
                </div>
            )}

            {!collapsed && (<>
            {/* Row 1: Label + Key */}
            <div className="flex gap-2 mb-2">
                <div className="flex-1">
                    <label className="block text-xs font-medium mb-0.5 text-zinc-600 dark:text-zinc-400">
                        {t('aiTemplateForm.labelShownToUser')}
                        <span className="ml-1 text-primary font-semibold">({labelLang === 'bn' ? t('aiTemplateForm.langBn') : t('aiTemplateForm.langEn')})</span>
                    </label>
                    <input
                        type="text"
                        placeholder={t('aiTemplateForm.labelPlaceholder')}
                        className={inputXsCls}
                        value={labelLang === 'bn' ? (field.labelBn || '') : (field.label || '')}
                        onChange={e => updateField(field.id, labelLang === 'bn' ? 'labelBn' : 'label', e.target.value)}
                    />
                </div>
                <div className="flex-1">
                    <label className="block text-xs font-medium mb-0.5 text-zinc-600 dark:text-zinc-400">{t('aiTemplateForm.keyUseInPrompt')}</label>
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="player_name"
                            className={inputXsCls + (field.key ? ' pr-8' : '')}
                            value={field.key}
                            onChange={e => updateField(field.id, 'key', e.target.value)}
                        />
                        {field.key && (
                            <button
                                title={copiedKey === field.id ? t('aiTemplateForm.copied') : t('aiTemplateForm.copyKey', { key: field.key })}
                                className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded transition-colors ${copiedKey === field.id ? 'text-green-600' : 'text-primary'} hover:bg-zinc-100 dark:hover:bg-zinc-700`}
                                onClick={() => {
                                    navigator.clipboard.writeText(`{${field.key}}`);
                                    setCopiedKey(field.id);
                                    setTimeout(() => setCopiedKey(null), 1500);
                                }}
                            >
                                {copiedKey === field.id ? <IconCheck size={11} /> : <IconCopy size={11} />}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Row 2: Type + Placeholder / Color */}
            <div className="flex gap-2 mb-2">
                <div className="flex-1">
                    <label className="block text-xs font-medium mb-0.5 text-zinc-600 dark:text-zinc-400">{t('aiTemplateForm.type')}</label>
                    <Select value={field.type} onValueChange={v => updateField(field.id, 'type', v)}>
                        <SelectTrigger className="w-full h-9 text-xs">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="text">{t('aiTemplateForm.typeText')}</SelectItem>
                            <SelectItem value="textarea">{t('aiTemplateForm.typeLongText')}</SelectItem>
                            <SelectItem value="number">{t('aiTemplateForm.typeNumber')}</SelectItem>
                            <SelectItem value="dropdown">{t('aiTemplateForm.typeDropdown')}</SelectItem>
                            <SelectItem value="colorpicker">{t('aiTemplateForm.typeColorPicker')}</SelectItem>
                            <SelectItem value="options">{t('aiTemplateForm.typeOptionsRadio')}</SelectItem>
                            <SelectItem value="date">{t('aiTemplateForm.typeDate')}</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                {(isTextLike || isNumber) && (
                    <div className="flex-1">
                        <label className="block text-xs font-medium mb-0.5 text-zinc-600 dark:text-zinc-400">{t('aiTemplateForm.placeholderHint')}</label>
                        {isTextarea ? (
                            <textarea
                                rows={3}
                                placeholder={t('aiTemplateForm.placeholderTextHint')}
                                className={inputXsCls + ' resize-y min-h-[64px]'}
                                value={field.placeholder || ''}
                                onChange={e => updateField(field.id, 'placeholder', e.target.value)}
                            />
                        ) : (
                            <input
                                type="text"
                                placeholder={isNumber ? t('aiTemplateForm.placeholderNumberHint') : t('aiTemplateForm.placeholderTextHint')}
                                className={inputXsCls}
                                value={field.placeholder || ''}
                                onChange={e => updateField(field.id, 'placeholder', e.target.value)}
                            />
                        )}
                    </div>
                )}
                {isColor && (
                    <div className="flex-1">
                        <label className="block text-xs font-medium mb-0.5 text-zinc-600 dark:text-zinc-400">{t('aiTemplateForm.defaultColor')}</label>
                        <div className="flex gap-1.5 items-center">
                            <input
                                type="color"
                                className="w-8 h-7 border border-zinc-200 dark:border-zinc-700 rounded cursor-pointer"
                                value={field.defaultColor || '#000000'}
                                onChange={e => updateField(field.id, 'defaultColor', e.target.value)}
                            />
                            <input
                                type="text"
                                className={inputXsCls + ' flex-1'}
                                value={field.defaultColor || '#000000'}
                                onChange={e => updateField(field.id, 'defaultColor', e.target.value)}
                                placeholder="#000000"
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Preset swatches (colorpicker) */}
            {isColor && (
                <div className="mb-2 p-2 rounded-lg bg-orange-50/60 dark:bg-orange-900/10 border border-primary/30">
                    <p className="text-xs font-semibold text-primary mb-1.5">{t('aiTemplateForm.presetSwatches')}</p>
                    <div className="flex flex-wrap gap-1.5 items-center">
                        {(field.choices || []).map((c, ci) => (
                            <div key={ci} className="relative group">
                                <input
                                    type="color"
                                    className="w-7 h-7 rounded border border-zinc-200 dark:border-zinc-700 cursor-pointer p-0 bg-transparent"
                                    value={c.value || '#000000'}
                                    onChange={e => {
                                        const u = [...(field.choices || [])];
                                        u[ci] = { label: e.target.value, value: e.target.value };
                                        updateField(field.id, 'choices', u);
                                    }}
                                />
                                <button
                                    className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() => updateField(field.id, 'choices', (field.choices || []).filter((_, i) => i !== ci))}
                                >
                                    <IconTrash size={8} />
                                </button>
                            </div>
                        ))}
                        <button
                            className="w-7 h-7 rounded border border-dashed border-primary/50 text-primary flex items-center justify-center hover:bg-primary/10 transition-colors"
                            onClick={() => updateField(field.id, 'choices', [...(field.choices || []), { label: field.defaultColor || '#000000', value: field.defaultColor || '#000000' }])}
                            title={t('aiTemplateForm.addSwatch')}
                        >
                            <IconPlus size={12} />
                        </button>
                        <button
                            onClick={handleSuggestChoice}
                            disabled={suggesting || !prompt.trim()}
                            title={!prompt.trim() ? t('aiTemplateForm.writePromptFirstTooltip') : t('aiTemplateForm.aiSuggestChoiceTooltip')}
                            className="w-7 h-7 rounded border border-dashed border-primary/50 text-primary flex items-center justify-center hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {suggesting
                                ? <span className="w-3 h-3 border-2 border-primary/40 border-t-primary rounded-full animate-spin" />
                                : <IconSparkles size={12} />}
                        </button>
                    </div>
                    <p className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-1.5">{t('aiTemplateForm.usersPickSwatch')}</p>
                </div>
            )}

            {/* Date settings: auto (today) toggle + output language */}
            {isDate && (
                <div className="mb-2 p-2 rounded-lg bg-orange-50/60 dark:bg-orange-900/10 border border-primary/30 space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            className="w-3.5 h-3.5 rounded accent-primary"
                            checked={field.dateAuto !== false}
                            onChange={e => updateField(field.id, 'dateAuto', e.currentTarget.checked)}
                        />
                        <span className="text-xs font-semibold text-primary">{t('aiTemplateForm.dateAuto')}</span>
                    </label>
                    <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
                        {field.dateAuto !== false ? t('aiTemplateForm.dateAutoHelp') : t('aiTemplateForm.dateManualHelp')}
                    </p>
                    {field.dateAuto !== false ? (
                        /* Auto mode: pick which date variables to expose — each is a
                           {key} the admin can place in the prompt; today's date in
                           that format is substituted at generation. */
                        <div>
                            <label className="block text-xs font-medium mb-1 text-zinc-600 dark:text-zinc-400">{t('aiTemplateForm.dateVariantsLabel')}</label>
                            <div className="flex flex-wrap gap-1.5">
                                {DATE_VARIANTS.map(v => {
                                    const cur = field.dateVariants || ['date-en-bn'];
                                    const selected = cur.includes(v.key);
                                    return (
                                        <button
                                            key={v.key}
                                            type="button"
                                            onClick={() => updateField(field.id, 'dateVariants', selected ? cur.filter(k => k !== v.key) : [...cur, v.key])}
                                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium border transition-colors ${selected
                                                ? 'border-primary bg-primary/10 text-primary'
                                                : 'border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'}`}
                                        >
                                            <span className="font-mono">{`{${v.key}}`}</span>
                                            <span className="opacity-80">{v.format(todayISO())}</span>
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-1">{t('aiTemplateForm.dateVariantsHint')}</p>
                        </div>
                    ) : (
                    <div className="flex gap-2 items-end">
                        <div className="flex-1">
                            <label className="block text-xs font-medium mb-0.5 text-zinc-600 dark:text-zinc-400">{t('aiTemplateForm.dateLanguage')}</label>
                            <Select value={field.dateLang || 'bn'} onValueChange={v => updateField(field.id, 'dateLang', v)}>
                                <SelectTrigger className="w-full h-9 text-xs">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="bn">{t('aiTemplateForm.langBn')}</SelectItem>
                                    <SelectItem value="en">{t('aiTemplateForm.langEn')}</SelectItem>
                                    <SelectItem value="ar">{t('aiTemplateForm.langAr')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <p className="flex-1 text-[11px] text-zinc-500 dark:text-zinc-400 pb-2">
                            {t('aiTemplateForm.dateTodayPreview', { date: formatPromptDate(todayISO(), field.dateLang || 'bn') })}
                        </p>
                    </div>
                    )}
                </div>
            )}

            {/* Validation: text / textarea */}
            {isTextLike && (
                <div className="flex gap-2 mb-2">
                    <div className="flex-1">
                        <label className="block text-xs font-medium mb-0.5 text-zinc-600 dark:text-zinc-400">{t('aiTemplateForm.minLength')}</label>
                        <input
                            type="number"
                            placeholder={t('aiTemplateForm.noMin')}
                            min={0}
                            className={inputXsCls}
                            value={field.minLength ?? ''}
                            onChange={e => updateField(field.id, 'minLength', e.target.value === '' ? null : Number(e.target.value))}
                        />
                    </div>
                    <div className="flex-1">
                        <label className="block text-xs font-medium mb-0.5 text-zinc-600 dark:text-zinc-400">{t('aiTemplateForm.maxChars')}</label>
                        <input
                            type="number"
                            placeholder={t('aiTemplateForm.noLimit')}
                            min={1}
                            className={inputXsCls}
                            value={field.charLimit ?? ''}
                            onChange={e => updateField(field.id, 'charLimit', e.target.value === '' ? null : Number(e.target.value))}
                        />
                    </div>
                </div>
            )}

            {/* Predefined suggestions (text/textarea): tappable quick-fill values */}
            {isTextLike && (
                <div className="mb-2 p-2 rounded-lg bg-orange-50/60 dark:bg-orange-900/10 border border-primary/30">
                    <p className="text-xs font-semibold text-primary mb-1.5">{t('aiTemplateForm.predefinedSuggestions')}</p>
                    <div className="flex flex-col gap-1">
                        {(field.suggestions || []).map((s, si) => (
                            <div key={si} className="flex gap-1 items-center">
                                <input
                                    type="text"
                                    placeholder={t('aiTemplateForm.suggestionPlaceholder')}
                                    className={inputXsCls + ' flex-1'}
                                    value={s}
                                    onChange={e => {
                                        const u = [...(field.suggestions || [])];
                                        u[si] = e.target.value;
                                        updateField(field.id, 'suggestions', u);
                                    }}
                                />
                                <button
                                    className="p-1 rounded text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex-shrink-0"
                                    onClick={() => updateField(field.id, 'suggestions', (field.suggestions || []).filter((_, i) => i !== si))}
                                >
                                    <IconTrash size={11} />
                                </button>
                            </div>
                        ))}
                    </div>
                    <button
                        className="mt-1.5 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                        onClick={() => updateField(field.id, 'suggestions', [...(field.suggestions || []), ''])}
                    >
                        <IconPlus size={11} /> {t('aiTemplateForm.addSuggestion')}
                    </button>
                    <p className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-1">{t('aiTemplateForm.predefinedSuggestionsHelp')}</p>
                </div>
            )}

            {/* Validation: number */}
            {isNumber && (
                <div className="flex gap-2 mb-2">
                    <div className="flex-1">
                        <label className="block text-xs font-medium mb-0.5 text-zinc-600 dark:text-zinc-400">{t('aiTemplateForm.minValue')}</label>
                        <input
                            type="number"
                            placeholder={t('aiTemplateForm.noMin')}
                            className={inputXsCls}
                            value={field.minValue ?? ''}
                            onChange={e => updateField(field.id, 'minValue', e.target.value === '' ? null : Number(e.target.value))}
                        />
                    </div>
                    <div className="flex-1">
                        <label className="block text-xs font-medium mb-0.5 text-zinc-600 dark:text-zinc-400">{t('aiTemplateForm.maxValue')}</label>
                        <input
                            type="number"
                            placeholder={t('aiTemplateForm.noMax')}
                            className={inputXsCls}
                            value={field.maxValue ?? ''}
                            onChange={e => updateField(field.id, 'maxValue', e.target.value === '' ? null : Number(e.target.value))}
                        />
                    </div>
                </div>
            )}

            {/* Choices editor */}
            {hasChoices && (
                <div className="mb-2 p-2 rounded-lg bg-orange-50/60 dark:bg-orange-900/10 border border-primary/30">
                    <p className="text-xs font-semibold text-primary mb-1.5">
                        {field.type === 'options' ? t('aiTemplateForm.optionsRadioChoices') : t('aiTemplateForm.dropdownItems')}
                    </p>
                    <div className="flex flex-col gap-1">
                        {(field.choices || []).length === 0 && (
                            <p className="text-xs text-zinc-500 dark:text-zinc-400 italic">{t('aiTemplateForm.noChoicesYet')}</p>
                        )}
                        {/* Column headers: Bangla · English · Value */}
                        {(field.choices || []).length > 0 && (
                            <div className="flex gap-1 px-0.5">
                                <span className="flex-1 text-[10px] font-medium text-zinc-400 dark:text-zinc-500">{labelLang === 'bn' ? t('aiTemplateForm.choiceColBangla') : t('aiTemplateForm.choiceColEnglish')}</span>
                                <span className="flex-1 text-[10px] font-medium text-zinc-400 dark:text-zinc-500">{t('aiTemplateForm.choiceColValue')}</span>
                                <span className="w-[19px] flex-shrink-0" />
                            </div>
                        )}
                        <DndContext sensors={choiceSensors} collisionDetection={closestCenter} onDragEnd={onChoiceDragEnd}>
                            <SortableContext items={(field.choices || []).map((_, i) => `choice-${i}`)} strategy={verticalListSortingStrategy}>
                        {(field.choices || []).map((choice, ci) => (
                            <SortableChoiceRow key={ci} id={`choice-${ci}`} isDefault={ci === 0}>
                            {/* First choice is the pre-selected default in the user panel */}
                            {ci === 0 && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 px-0.5">
                                    <IconStarFilled size={9} /> {t('aiTemplateForm.defaultChoice')}
                                </span>
                            )}
                            <div className="flex gap-1 items-center">
                                {/* Label for the active language only (toggle in the header) */}
                                {labelLang === 'bn' ? (
                                    <input
                                        type="text"
                                        placeholder={t('aiTemplateForm.choiceLabelBnPlaceholder')}
                                        className={inputXsCls + ' flex-1'}
                                        value={choice.labelBn || ''}
                                        onChange={e => {
                                            const updated = [...(field.choices || [])];
                                            updated[ci] = { ...updated[ci], labelBn: e.target.value };
                                            updateField(field.id, 'choices', updated);
                                        }}
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        placeholder={t('aiTemplateForm.choiceLabelPlaceholder')}
                                        className={inputXsCls + ' flex-1'}
                                        value={choice.label}
                                        onChange={e => {
                                            const lbl = e.target.value;
                                            const updated = [...(field.choices || [])];
                                            // Only auto-derive the prompt value while it is still EMPTY (a brand-new
                                            // choice). Once it has content — typed by the admin OR filled by AI — leave
                                            // it alone so editing the label never changes the prompt value.
                                            const keepValue = updated[ci]._valueTouched || (updated[ci].value || '').trim();
                                            updated[ci] = { ...updated[ci], label: lbl, value: keepValue ? updated[ci].value : toKey(lbl) };
                                            updateField(field.id, 'choices', updated);
                                        }}
                                    />
                                )}
                                {/* Value — appended to the English prompt (can be detailed) */}
                                <input
                                    type="text"
                                    placeholder={t('aiTemplateForm.choiceValuePlaceholder')}
                                    className={inputXsCls + ' flex-1'}
                                    value={choice.value}
                                    onChange={e => {
                                        const updated = [...(field.choices || [])];
                                        updated[ci] = { ...updated[ci], value: e.target.value, _valueTouched: true };
                                        updateField(field.id, 'choices', updated);
                                    }}
                                />
                                <button
                                    className="p-1 rounded text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex-shrink-0"
                                    onClick={() => updateField(field.id, 'choices', (field.choices || []).filter((_, i) => i !== ci))}
                                >
                                    <IconTrash size={11} />
                                </button>
                            </div>
                            </SortableChoiceRow>
                        ))}
                            </SortableContext>
                        </DndContext>
                    </div>
                    <div className="mt-1.5 flex items-center gap-3">
                        <button
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                            onClick={() => updateField(field.id, 'choices', [...(field.choices || []), { label: '', labelBn: '', value: '' }])}
                        >
                            <IconPlus size={11} /> {t('aiTemplateForm.addChoice')}
                        </button>
                        <button
                            onClick={handleSuggestChoice}
                            disabled={suggesting || !prompt.trim()}
                            title={!prompt.trim() ? t('aiTemplateForm.writePromptFirstTooltip') : t('aiTemplateForm.aiSuggestChoiceTooltip')}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-primary/10 hover:bg-primary/20 text-primary dark:bg-primary/20 dark:hover:bg-primary/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {suggesting
                                ? <span className="w-3 h-3 border-2 border-primary/40 border-t-primary rounded-full animate-spin" />
                                : <IconSparkles size={11} />}
                            {t('aiTemplateForm.aiSuggestChoice')}
                        </button>
                    </div>
                </div>
            )}

            {/* Required toggle */}
            <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                    type="checkbox"
                    className="w-3.5 h-3.5 rounded accent-primary"
                    checked={field.required}
                    onChange={e => updateField(field.id, 'required', e.currentTarget.checked)}
                />
                <span className="text-xs text-zinc-600 dark:text-zinc-400">{t('aiTemplateForm.required')}</span>
            </label>
            </>)}
        </div>
    );
}

// ── Image size options ─────────────────────────────────────────────────────
// Multi-image templates: sensible cap on total images a user can upload.
const MAX_IMAGE_INPUTS = 6;
// Image inputs are [{ label, required }]. Normalise old string[] data too.
const normalizeImageInputs = (arr) => (Array.isArray(arr) ? arr : []).map(v =>
    (typeof v === 'object' && v !== null)
        ? { label: v.label ?? '', required: v.required !== false }
        : { label: v ?? '', required: true }
);

const IMAGE_SIZES = [
    { value: 'auto',           label: 'Auto (match user photo)' },
    { value: 'portrait_2x3',   label: 'Portrait (1024×1536)' },
    { value: 'square_1x1',     label: 'Square (1024×1024)' },
    { value: 'landscape_3x2',  label: 'Landscape (1536×1024)' },
];


// ── Quick-add field presets ────────────────────────────────────────────────
// Each preset is built on an existing type (colorpicker / options / dropdown)
// pre-loaded with sensible choices, so all existing logic keeps working. Shapes
// mirror what the AI analysis generates: field + choices carry BOTH label
// (English) and labelBn (Bangla); option values are rich descriptive prompt
// text (not bare words); colorpicker swatch labels are Bangla colour names.
const swatchChoices = (pairs) => pairs.map(([label, value]) => ({ label, value }));
const DRESS_COLORS = [
    ['সাদা', '#FFFFFF'], ['কালো', '#000000'], ['লাল', '#C0392B'], ['নীল', '#2E5AAC'],
    ['আকাশি', '#4AA3DF'], ['সবুজ', '#27AE60'], ['টিল', '#16A085'], ['হলুদ', '#F1C40F'],
    ['গোলাপি', '#E84393'], ['বেগুনি', '#8E44AD'], ['অফ-হোয়াইট', '#F5F0E6'], ['ধূসর', '#95A5A6'],
];
const HAIR_COLORS = [
    ['কুচকুচে কালো', '#1C1C1C'], ['গাঢ় বাদামি', '#3B2A23'], ['বাদামি', '#6B4226'], ['হালকা বাদামি', '#A0724A'],
    ['সোনালি', '#E6C288'], ['তামাটে', '#7A3B2E'], ['লালচে', '#B33A2B'], ['রুপালি', '#B0B0B0'],
];

const FIELD_PRESETS = [
    { id: 'dress-color',     label: 'Dress Color', build: () => ({ label: 'Dress Color', labelBn: 'জামার রং', key: 'dress-color', type: 'colorpicker', defaultColor: '#FFFFFF', choices: swatchChoices(DRESS_COLORS) }) },
    { id: 'hair-color',      label: 'Hair Color',  build: () => ({ label: 'Hair Color', labelBn: 'চুলের রং', key: 'hair-color', type: 'colorpicker', defaultColor: '#3B2A23', choices: swatchChoices(HAIR_COLORS) }) },
    { id: 'pose-style',      label: 'Pose Style',  build: () => ({ label: 'Pose Style', labelBn: 'পোজ', key: 'pose-style', type: 'options', choices: [
        { label: 'Standing',          labelBn: 'দাঁড়ানো',           value: 'standing upright with a confident, relaxed posture, facing the camera naturally' },
        { label: 'Sitting',           labelBn: 'বসা',                value: 'sitting comfortably in a natural relaxed pose, hands resting easily' },
        { label: 'Close-up Portrait', labelBn: 'ক্লোজ-আপ পোর্ট্রেট', value: 'close-up portrait framed from the chest up, face clearly in sharp focus' },
        { label: 'Side Profile',      labelBn: 'সাইড প্রোফাইল',      value: 'elegant side-profile pose with the head turned to show the face in profile' },
        { label: 'Candid',            labelBn: 'ক্যান্ডিড',          value: 'natural candid pose captured mid-moment, looking slightly away from the camera' },
    ] }) },
    { id: 'background-scene', label: 'Background', build: () => ({ label: 'Background Scene', labelBn: 'ব্যাকগ্রাউন্ড', key: 'background-scene', type: 'dropdown', choices: [
        { label: 'Studio Backdrop', labelBn: 'স্টুডিও ব্যাকড্রপ',   value: 'clean professional studio backdrop with a smooth seamless gradient and soft even lighting' },
        { label: 'Outdoor Garden',  labelBn: 'আউটডোর বাগান',        value: 'lush outdoor garden with rich greenery and softly blurred flowers behind the subject' },
        { label: 'Urban Street',    labelBn: 'শহরের রাস্তা',        value: 'stylish urban street scene with softly blurred city buildings and warm ambient light' },
        { label: 'Beach',           labelBn: 'সমুদ্র সৈকত',         value: 'sunny beach with golden sand, gentle waves and a bright sky softly blurred behind' },
        { label: 'Plain Gradient',  labelBn: 'প্লেইন গ্রেডিয়েন্ট', value: 'minimal plain gradient backdrop with a smooth colour transition and no distracting elements' },
        { label: 'Indoor Room',     labelBn: 'ঘরের ভেতর',           value: 'cozy tastefully decorated indoor room with warm lighting, softly blurred behind the subject' },
    ] }) },
    { id: 'expression',      label: 'Expression', build: () => ({ label: 'Expression', labelBn: 'মুখের ভাব', key: 'expression', type: 'options', choices: [
        { label: 'Smiling', labelBn: 'হাসিমুখ',   value: 'warm genuine smile with bright, friendly eyes' },
        { label: 'Serious', labelBn: 'গম্ভীর',    value: 'calm serious expression with composed, confident eyes' },
        { label: 'Candid',  labelBn: 'ক্যান্ডিড', value: 'natural candid expression caught mid-moment, relaxed and unposed' },
        { label: 'Joyful',  labelBn: 'আনন্দিত',   value: 'joyful laughing expression full of energy and delight' },
    ] }) },
];

// ── Wizard step indicator ──────────────────────────────────────────────────
function WizardStepper({ active, inline, steps = [
    { label: 'Write Prompt', desc: 'Write prompt & settings' },
    { label: 'Review Result', desc: 'Check generated thumbnail' },
    { label: 'Template Details', desc: 'AI fills in the rest' },
] }) {
    return (
        <div className={`flex items-start gap-0 ${inline ? 'w-full max-w-md' : 'mb-8 max-w-2xl mx-auto'}`}>
            {steps.map((step, i) => (
                <div key={i} className="flex items-start flex-1 min-w-0">
                    <div className="flex flex-col items-center flex-shrink-0">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-colors ${i < active
                            ? 'bg-primary border-primary text-white'
                            : i === active
                                ? 'border-primary text-primary bg-white dark:bg-zinc-900'
                                : 'border-zinc-200 dark:border-zinc-700 text-zinc-400 bg-white dark:bg-zinc-900'}`}
                        >
                            {i < active ? <IconCheck size={14} /> : i + 1}
                        </div>
                    </div>
                    {i < steps.length - 1 && (
                        <div className={`flex-1 h-0.5 mt-4 mx-1 transition-colors ${i < active ? 'bg-primary' : 'bg-zinc-200 dark:bg-zinc-700'}`} />
                    )}
                </div>
            ))}
        </div>
    );
}

// ── Numbered step badge for the guided create/edit form ───────────────────
function StepBadge({ n }) {
    return (
        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-[13px] font-bold flex items-center justify-center">
            {n}
        </span>
    );
}

// ── Collapsible form section (numbered header + chevron toggle) ────────────
function CollapsibleSection({ stepNo, title, subtitle, action, defaultOpen = true, children }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700">
            <div className="flex items-start justify-between gap-2 px-4 py-3">
                <button
                    type="button"
                    onClick={() => setOpen(o => !o)}
                    className="flex items-center gap-2.5 min-w-0 flex-1 text-left"
                >
                    {stepNo && <StepBadge n={stepNo} />}
                    <div className="min-w-0">
                        <span className="font-semibold text-sm text-zinc-900 dark:text-zinc-100">{title}</span>
                        {subtitle && <p className="text-xs text-zinc-500 dark:text-zinc-400">{subtitle}</p>}
                    </div>
                </button>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {open && action}
                    <button
                        type="button"
                        onClick={() => setOpen(o => !o)}
                        className="p-1 rounded-lg text-zinc-400 hover:text-primary hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                        aria-label={open ? t('aiTemplateForm.collapseSection') : t('aiTemplateForm.expandSection')}
                    >
                        <IconChevronDown size={18} className={`transition-transform ${open ? '' : '-rotate-90'}`} />
                    </button>
                </div>
            </div>
            {open && <div className="px-4 pb-4">{children}</div>}
        </div>
    );
}

export default function AiTemplateFormPage() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;
    const location = useLocation();
    const [searchParams] = useSearchParams();

    // "Resolve mode" — arrived here from the Negative Feedback page to fix a specific
    // report. The issue steers the AI prompt re-analysis; running it marks the report
    // solved. Rich context comes via router state; the id also survives a refresh in
    // the query string.
    const [resolveCtx] = useState(() => location.state?.resolveFeedback || null);
    const [resolveDone, setResolveDone] = useState(false);
    const resolveFeedbackId = resolveCtx?.id || (searchParams.get('resolveFeedback') ? Number(searchParams.get('resolveFeedback')) : null);
    const resolveGuidance = resolveCtx ? `${resolveCtx.reason || ''}${resolveCtx.note ? ' — ' + resolveCtx.note : ''}`.trim() : '';

    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [analyzingFields, setAnalyzingFields] = useState(false);
    const [syncingFieldId, setSyncingFieldId] = useState(null);
    const [analyzingBasic, setAnalyzingBasic] = useState(false);
    const [analyzingNegative, setAnalyzingNegative] = useState(false);
    // Which language the field-LABEL inputs edit (bn/en). Options already have both columns;
    // this switcher lets an admin fill the field label in both languages via one input.
    // Default the label-language toggle to whatever app language the user is using.
    const [labelLang, setLabelLang] = useState(i18n.language === 'bn' ? 'bn' : 'en');
    // AI "add a field from an instruction" modal (near the prompt box)
    const [addFieldModalOpen, setAddFieldModalOpen] = useState(false);
    const [addFieldInstruction, setAddFieldInstruction] = useState('');
    const [addingField, setAddingField] = useState(false);
    // Enhance-prompt-from-thumbnail (same modal, second action)
    const [enhancingPrompt, setEnhancingPrompt] = useState(false);
    const [tokenizingPrompt, setTokenizingPrompt] = useState(false);
    // The {key} placeholder chips under the prompt are collapsible.
    const [keyChipsOpen, setKeyChipsOpen] = useState(false);
    const [settingThumbnail, setSettingThumbnail] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [optimizingCount, setOptimizingCount] = useState(0);

    // Wizard step: 0 = prompt+test only (create), 1 = review result, 2 = full form
    const [wizardStep, setWizardStep] = useState(isEdit ? 2 : 0);

    // Creation mode (create only): 'choose' = pick a path, 'image' = upload → AI
    // reverse-engineers the template, 'prompt' = write prompt → generate (classic).
    const [creationMode, setCreationMode] = useState(isEdit ? 'prompt' : 'choose');
    // Bulk path: images queued for automatic draft creation (max MAX_BULK_IMAGES).
    const [bulkFiles, setBulkFiles] = useState(null);
    // Images pasted while still on the "how to start" screen — held until the admin picks
    // which path to use them for (Upload Image vs Product Image).
    const [pastedImages, setPastedImages] = useState(null);
    const [analyzingImage, setAnalyzingImage] = useState(false);

    // Crop + rotate stage for the image-path upload (same modal as the user panel).
    const isMobile = useMediaQuery('(max-width: 768px)');
    const [cropSrc, setCropSrc] = useState(null);
    const [cropName, setCropName] = useState('');
    const [cropTarget, setCropTarget] = useState(null); // 'imagePath' | 'testImage' | 'testPick'
    const cropQueueRef = useRef([]); // further test photos waiting to go through the crop stage (multi-select upload)
    // Temporary crop memory for test photos: preview object-URL → { original File, crop % area }. Lets a tile show the
    // CROPPED image while a click re-opens the crop stage with the ORIGINAL image and the previous selection.
    const cropMemoRef = useRef({});
    const recropRef = useRef(null);            // { previewUrl } of the photo being re-cropped ('testReplace')
    const [cropInitial, setCropInitial] = useState(null); // starting selection handed to CropStage when re-opening
    const pendingCropFileRef = useRef(null);

    // ── Form fields ────────────────────────────────────────────────────────
    const [nameEn, setNameEn] = useState('');
    const [nameBn, setNameBn] = useState('');
    const [slug, setSlug] = useState('');
    const [categoryId, setCategoryId] = useState(null);
    const [prompt, setPrompt] = useState('');
    const [negativePrompt, setNegativePrompt] = useState('');
    const [status, setStatus] = useState('draft');
    const [sortOrder, setSortOrder] = useState(0);
    // Free templates skip credit deduction on generation. Default paid (false).
    const [isFree, setIsFree] = useState(false);
    // Visibility scope — ON (default) = visible in every country; OFF = Bangladesh-only.
    // Backend stores NULL for pre-feature templates, which also counts as global.
    const [isGlobal, setIsGlobal] = useState(true);
    const [userNote, setUserNote] = useState(''); // optional admin note shown to users under the upload box
    const [updatedAt, setUpdatedAt] = useState(null);
    const [watermarkEnabled, setWatermarkEnabled] = useState(true);
    const [watermarkX, setWatermarkX] = useState(80);
    const [watermarkY, setWatermarkY] = useState(88);
    const [requireUserImage, setRequireUserImage] = useState(true);
    const [imageSize, setImageSize] = useState('portrait_2x3');
    const [allowMultipleImages, setAllowMultipleImages] = useState(false);
    const [requiredImageCount, setRequiredImageCount] = useState(1);
    const [imageLabels, setImageLabels] = useState([]);
    // Audience tags (male/female/children/product) → gallery gender filter + generate badge.
    const [genders, setGenders] = useState([]);

    const [thumbnailUrl, setThumbnailUrl] = useState(null);

    // ── Dynamic fields ─────────────────────────────────────────────────────
    const [fields, setFields] = useState([]);

    const fieldSensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleFieldDragEnd = useCallback((event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        setFields(prev => {
            const oldIdx = prev.findIndex(f => f.id === active.id);
            const newIdx = prev.findIndex(f => f.id === over.id);
            return arrayMove(prev, oldIdx, newIdx);
        });
    }, []);

    const addField = () => setFields(prev => [...prev, {
        id: uid(), ...EMPTY_FIELD_BASE, _keyTouched: false,
    }]);

    // Quick-add a ready-made preset field (dress color, pose style, …).
    const addPresetField = (preset) => setFields(prev => [...prev, {
        id: uid(), ...EMPTY_FIELD_BASE, _keyTouched: true, ...preset.build(),
    }]);

    const removeField = (fid) => setFields(prev => prev.filter(f => f.id !== fid));

    // Replace a field with pasted field-JSON. Built on the clean base so props the
    // JSON omits reset to defaults instead of leaking from the old config.
    const applyFieldJson = (fid, data) => setFields(prev => prev.map(f =>
        f.id === fid ? { id: f.id, ...EMPTY_FIELD_BASE, ...data, _keyTouched: true } : f
    ));

    // ── Tag handlers (optional) ────────────────────────────────────────────
    const addTag = (raw) => {
        const name = String(raw || '').trim().replace(/,+$/, '').slice(0, 60);
        if (!name) return;
        setTemplateTags(prev => prev.some(t => t.toLowerCase() === name.toLowerCase()) ? prev : [...prev, name]);
        setTagInput('');
    };
    const removeTag = (name) => setTemplateTags(prev => prev.filter(t => t !== name));
    // Merge AI-suggested tags into the list, de-duplicated (case-insensitive).
    const mergeTags = (incoming) => setTemplateTags(prev => {
        const next = [...prev];
        (incoming || []).forEach(raw => {
            const name = String(raw || '').trim().replace(/^#+/, '').replace(/,+$/, '').slice(0, 60);
            if (name && !next.some(t => t.toLowerCase() === name.toLowerCase())) next.push(name);
        });
        return next;
    });

    // ── Image input handlers (multi-image: label + required + delete) ───────
    const addImageInput = () => setImageLabels(prev => prev.length >= MAX_IMAGE_INPUTS ? prev : [...prev, { label: '', required: true }]);
    const removeImageInput = (i) => setImageLabels(prev => prev.filter((_, idx) => idx !== i));
    const updateImageInput = (i, patch) => setImageLabels(prev => prev.map((x, idx) => idx === i ? { ...x, ...patch } : x));

    // Image-input label: pick a preset key from the file, OR write a custom label by hand.
    // `manualLabelIdx` tracks which rows the admin switched to manual entry; a saved
    // literal (non-key) value also renders as manual so old custom labels stay editable.
    const [manualLabelIdx, setManualLabelIdx] = useState({});
    const setLabelValue = (idx, value) => {
        if (idx === 0 && !allowMultipleImages) setImageLabels([{ label: value, required: true }]);
        else updateImageInput(idx, { label: value });
    };
    const renderImageLabelControl = (idx, item) => {
        const val = item?.label || '';
        const manual = !!manualLabelIdx[idx] || (!!val && !isImageInputLabelKey(val));
        if (manual) {
            return (
                <div className="flex items-center gap-1.5 flex-1">
                    <input
                        type="text"
                        className={inputCls + ' flex-1'}
                        placeholder={t('aiTemplateForm.imageInputLabelPlaceholder')}
                        value={val}
                        onChange={e => setLabelValue(idx, e.target.value)}
                    />
                    <button
                        type="button"
                        title={t('aiTemplateForm.chooseFromList')}
                        onClick={() => { setManualLabelIdx(m => { const n = { ...m }; delete n[idx]; return n; }); setLabelValue(idx, ''); }}
                        className="p-2 rounded-lg border border-zinc-200 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400 hover:text-primary hover:border-primary transition-colors flex-shrink-0"
                    >
                        <IconList size={16} />
                    </button>
                </div>
            );
        }
        return (
            <div className="flex items-center gap-1.5 flex-1">
                <div className="flex-1">
                    <Select value={val || undefined} onValueChange={v => setLabelValue(idx, v)}>
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder={t('aiTemplateForm.selectImageLabel')} />
                        </SelectTrigger>
                        <SelectContent>
                            {IMAGE_INPUT_LABELS.map(x => (
                                <SelectItem key={x.key} value={x.key}>{labelLang === 'bn' ? x.bn : x.en}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <button
                    type="button"
                    title={t('aiTemplateForm.writeManually')}
                    onClick={() => { setManualLabelIdx(m => ({ ...m, [idx]: true })); setLabelValue(idx, ''); }}
                    className="p-2 rounded-lg border border-zinc-200 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400 hover:text-primary hover:border-primary transition-colors flex-shrink-0"
                >
                    <IconPencil size={16} />
                </button>
            </div>
        );
    };

    const updateField = (fid, prop, value) => setFields(prev => prev.map(f => {
        if (f.id !== fid) return f;
        const updated = { ...f, [prop]: value };
        if (prop === 'label' && !f._keyTouched) updated.key = toKey(value);
        if (prop === 'key') { updated._keyTouched = true; updated.key = toKey(value); }
        return updated;
    }));

    // ── Thumbnail ──────────────────────────────────────────────────────────
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [thumbnailPreview, setThumbnailPreview] = useState(null);
    const [existingThumbnail, setExistingThumbnail] = useState(null);
    // Edit view: active tab of the main card ('prompt' = prompt + dynamic fields, 'basic' = basic info + image settings)
    // and whether the big thumbnail is collapsed — both purely visual, save nothing.
    const [detailsTab, setDetailsTab] = useState('prompt');
    const [thumbCollapsed, setThumbCollapsed] = useState(false);
    const [rawImageFile, setRawImageFile] = useState(null);
    const [rawExtraImageFiles, setRawExtraImageFiles] = useState([]);
    const [existingRawImage, setExistingRawImage] = useState(null);
    const [existingRawImages, setExistingRawImages] = useState([]);
    // Extra example images (beyond the thumbnail) used only to help the AI analysis
    // detect what varies across similar images (e.g. different poses).
    const [analysisExtraFiles, setAnalysisExtraFiles] = useState([]);
    const [analysisExtraPreviews, setAnalysisExtraPreviews] = useState([]);
    // Which uploaded image is shown large on the left. 0 = the primary/thumbnail,
    // 1..n = analysisExtraPreviews[previewIndex - 1]. Clicking a thumbnail swaps it.
    const [previewIndex, setPreviewIndex] = useState(0);
    // Optional free-text the admin can add alongside the example image(s) to steer
    // the AI analysis (e.g. "make the background a studio", "keep saree traditional").
    const [analysisNote, setAnalysisNote] = useState('');
    // Admin-picked field kinds the analyzer should turn into dynamic fields.
    const [analysisFieldTypes, setAnalysisFieldTypes] = useState([]);
    // The extra-images + description are secondary — hidden behind a collapse that is
    // closed by default so the panel stays clean; admin opens it only when needed.
    const [analysisOptionsOpen, setAnalysisOptionsOpen] = useState(false);
    const addMorePathInputRef = useRef(null);
    const thumbnailInputRef = useRef(null);
    const promptRef = useRef(null);
    // Stores cursor position captured on mousedown of chip buttons, before focus
    // leaves the textarea (blur resets selectionStart to 0 in many browsers).
    const promptCursorRef = useRef(null);
    useEffect(() => {
        const el = promptRef.current;
        if (!el) return;
        // Preserve textarea's internal scroll and the page scroll so that
        // setting height:'auto' (needed for shrinking) doesn't jump the viewport.
        const elScrollTop = el.scrollTop;
        const pageScrollY = window.scrollY;
        el.style.height = 'auto';
        el.style.height = Math.min(el.scrollHeight, window.innerHeight * 0.5) + 'px';
        el.scrollTop = elScrollTop;
        window.scrollTo({ top: pageScrollY });
    }, [prompt]);
    const [copiedKey, setCopiedKey] = useState(null);
    // Master-detail Dynamic Fields: which field is open in the editor pane.
    const [activeFieldId, setActiveFieldId] = useState(null);

    // ── Categories ─────────────────────────────────────────────────────────
    const [knownCategories, setKnownCategories] = useState([]);

    // ── Tags (optional) ────────────────────────────────────────────────────
    const [templateTags, setTemplateTags] = useState([]); // string[]
    const [tagInput, setTagInput] = useState('');
    const [allTags, setAllTags] = useState([]); // suggestions: string[]

    // ── Test generate ──────────────────────────────────────────────────────
    const [testFile, setTestFile] = useState(null);
    const [testPreview, setTestPreview] = useState(null);
    const [testExtraFiles, setTestExtraFiles] = useState([]);
    const [testExtraPreviews, setTestExtraPreviews] = useState([]);
    const [testGenerating, setTestGenerating] = useState(false);
    const [testProgress, setTestProgress] = useState(0);
    const [testResult, setTestResult] = useState(null);
    const [testFieldValues, setTestFieldValues] = useState({});
    // Pre-select each dynamic field's first option (colorpicker → default colour or first
    // swatch, dropdown/options → first choice) so the admin's test thumbnail reflects a
    // complete look by default. Only seeds keys the admin hasn't touched yet.
    useEffect(() => {
        setTestFieldValues(prev => {
            let changed = false;
            const next = { ...prev };
            for (const f of fields) {
                if (!f?.key) continue;
                // Dropdown / options: ALWAYS keep the first choice pre-selected. Re-sync to
                // the first choice whenever the current value isn't one of the choices —
                // that covers both the initial (unset) case AND a stale value left behind
                // when the choices change (e.g. after the values are re-tokenised). The
                // admin can still pick another choice; a valid pick is preserved.
                if (f.type === 'dropdown' || f.type === 'options') {
                    const vals = (f.choices || []).map(c => c?.value).filter(Boolean);
                    if (vals.length && !vals.includes(next[f.key])) { next[f.key] = vals[0]; changed = true; }
                    continue;
                }
                if (next[f.key] !== undefined) continue; // other types: seed once, when untouched
                let def = '';
                if (f.type === 'colorpicker') def = f.defaultColor || (f.choices?.[0]?.value ?? '');
                else if (f.type === 'date') def = todayISO();
                else if (f.type === 'text' || f.type === 'textarea') def = (f.placeholder || '').trim();
                else if (f.type === 'number') {
                    const ph = (f.placeholder || '').trim();
                    if (ph && !Number.isNaN(Number(ph))) def = ph;
                }
                if (def) { next[f.key] = def; changed = true; }
            }
            return changed ? next : prev;
        });
    }, [fields]);
    const testProgressTimer = useRef(null);
    const testFileInputRef = useRef(null);
    const imagePathInputRef = useRef(null);
    // Separate ref for the choose-step file input. It must not share a ref object
    // with the image-step input (below) — a shared ref can end up pointing at the
    // detached input after the branches swap, so the card opens the OS picker but
    // its onChange never fires (picker-opens-then-nothing, no error).
    const chooseImageInputRef = useRef(null);
    const productImageInputRef = useRef(null);
    const productModelInputRef = useRef(null);
    const bulkImageInputRef = useRef(null);
    // Product mode uses two dedicated dropzones: the product image and a reference
    // design image (the finished ad look to recreate; becomes the template thumbnail).
    const productBoxInputRef = useRef(null);
    const referenceBoxInputRef = useRef(null);
    const addMoreTestInputRef = useRef(null);
    const pendingGenerateRef = useRef(false);

    useEffect(() => () => {
        clearInterval(testProgressTimer.current);
        if (testPreview?.startsWith('blob:')) URL.revokeObjectURL(testPreview);
    }, [testPreview]);

    // ── Auto-detect {key} placeholders in the prompt as admin types ────────
    const detectedKeys = useMemo(() => {
        const matches = [...(String(prompt ?? '').matchAll(/\{([\w-]+)\}/g))].map(m => m[1]);
        return [...new Set(matches)];
    }, [prompt]);

    // ── Slug auto-fill (from English name, once on create) ─────────────────
    const slugAutoFilledRef = useRef(false);
    useEffect(() => {
        if (isEdit) return;
        if (slugAutoFilledRef.current) return;
        if (!nameEn.trim()) return;
        if (slug) return;
        const generated = slugify(nameEn);
        if (generated) {
            setSlug(generated);
            slugAutoFilledRef.current = true;
        }
    }, [nameEn, slug, isEdit]);

    const handleGenerateSlug = () => {
        const generated = slugify(nameEn);
        if (!generated) {
            notifications.show({
                title: t('aiTemplateForm.cannotGenerateSlug'),
                message: t('aiTemplateForm.cannotGenerateSlugMsg'),
                color: 'yellow',
            });
            return;
        }
        setSlug(generated);
        slugAutoFilledRef.current = true;
    };

    // ── Data loading ───────────────────────────────────────────────────────
    useEffect(() => {
        aiTemplateCategoryService.list()
            .then(r => setKnownCategories(r?.data || []))
            .catch(() => {});
        aiTemplateService.tags()
            .then(r => setAllTags((r?.data || []).map(tg => tg.name)))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!isEdit) return;
        aiTemplateService.get(id)
            .then(r => {
                const t = r?.data;
                if (!t) return;
                setNameEn(t.nameEn || t.name || '');
                setNameBn(t.nameBn || '');
                setSlug(t.slug || '');
                slugAutoFilledRef.current = true;
                setCategoryId(t.categoryId ? String(t.categoryId) : null);
                setPrompt(t.prompt || '');
                if (t.negativePrompt !== undefined) setNegativePrompt(t.negativePrompt || '');
                setStatus(t.status || 'active');
                setSortOrder(t.sortOrder || 0);
                setIsFree(t.isFree === true);
                setIsGlobal(t.isGlobal !== false); // null/undefined → global
                setUserNote(t.userNote || '');
                setUpdatedAt(t.updatedAt || null);
                setExistingThumbnail(t.thumbnail || null);
                setExistingRawImage(t.rawImage || null);
                setExistingRawImages(Array.isArray(t.rawImages) ? t.rawImages : (t.rawImage ? [t.rawImage] : []));
                setWatermarkEnabled(t.watermarkEnabled !== false);
                if (t.watermarkX !== undefined) setWatermarkX(Number(t.watermarkX));
                if (t.watermarkY !== undefined) setWatermarkY(Number(t.watermarkY));
                setRequireUserImage(String(t.requireUserImage) !== 'false');
                const validSizes = ['auto', 'portrait_2x3', 'square_1x1', 'landscape_3x2'];
                const savedSize = t.imageSize || 'portrait_2x3';
                if (validSizes.includes(savedSize)) {
                    setImageSize(savedSize);
                } else if (savedSize === 'portrait_4x5' || savedSize === 'auto') {
                    setImageSize('portrait_2x3');
                } else if (savedSize === 'landscape_16x9') {
                    setImageSize('landscape_3x2');
                } else {
                    setImageSize('portrait_2x3');
                }
                setAllowMultipleImages(!!t.allowMultipleImages);
                setRequiredImageCount(t.requiredImageCount || 1);
                setImageLabels(normalizeImageInputs(t.imageLabels));
                setGenders(Array.isArray(t.genders) ? t.genders.filter(Boolean) : []);
                setTemplateTags(Array.isArray(t.tags) ? t.tags.filter(Boolean) : []);
                try {
                    const rawFields = t.fields;
                    setFields(Array.isArray(rawFields) ? rawFields : (rawFields ? JSON.parse(rawFields) : []));
                } catch { setFields([]); }
            })
            .catch(() => notifications.show({ title: t('aiTemplateForm.error'), message: t('aiTemplateForm.failedToLoad'), color: 'red' }))
            .finally(() => setLoading(false));
    }, [id, isEdit]);

    // ── Thumbnail handlers ─────────────────────────────────────────────────
    const handleThumbnailChange = async (file) => {
        if (!file) return;
        const optimized = await optimizeImage(file, { targetBytes: 490_000 }).catch(() => file);
        setThumbnailFile(optimized);
        const reader = new FileReader();
        reader.onload = e => setThumbnailPreview(e.target.result);
        reader.readAsDataURL(optimized);
    };

    // Removing the SAVED example image needs a confirmation (ConfirmDialog, not the
    // browser's window.confirm) — without an image the template can't be saved.
    const [confirmRemoveThumb, setConfirmRemoveThumb] = useState(false);
    const doClearThumbnail = () => {
        setThumbnailFile(null);
        setThumbnailPreview(null);
        setExistingThumbnail(null);
    };
    const handleClearThumbnail = () => {
        if (thumbnailFile && existingThumbnail) {
            setThumbnailFile(null);
            setThumbnailPreview(null);
            return;
        }
        if (existingThumbnail && !thumbnailFile) {
            setConfirmRemoveThumb(true);
            return;
        }
        doClearThumbnail();
    };

    // ── Save ───────────────────────────────────────────────────────────────
    const validate = () => {
        if (!nameEn.trim())  return t('aiTemplateForm.validateNameEn');
        if (!categoryId)     return t('aiTemplateForm.validateCategory');
        if (!prompt.trim())  return t('aiTemplateForm.validatePrompt');
        if (!thumbnailFile && !existingThumbnail) return t('aiTemplateForm.validateThumbnail');
        return null;
    };

    // ── Unsaved-changes guard ──────────────────────────────────────────────
    // Snapshot the form once it has loaded; if the current form differs, warn before
    // the admin leaves the page (via the back/cancel buttons or a browser refresh/close).
    const [leaveTo, setLeaveTo] = useState(null);   // pending destination while confirming
    const [isDirty, setIsDirty]   = useState(false);
    const savedSnapshotRef = useRef('');
    const guardReadyRef    = useRef(false);

    const serializeForm = () => JSON.stringify({
        nameEn, nameBn, slug, categoryId, status, prompt, negativePrompt, imageSize,
        requireUserImage, allowMultipleImages, requiredImageCount, userNote,
        tags: templateTags, imageLabels, genders,
        fields: fields.map(f => ({ label: f.label, labelBn: f.labelBn, key: f.key, type: f.type, placeholder: f.placeholder, placeholderBn: f.placeholderBn, required: f.required, defaultColor: f.defaultColor, choices: f.choices, suggestions: f.suggestions, dateAuto: f.dateAuto, dateLang: f.dateLang, dateVariants: f.dateVariants, charLimit: f.charLimit, minLength: f.minLength })),
        thumb: thumbnailFile ? `file:${thumbnailFile.name}:${thumbnailFile.size}` : (thumbnailUrl || existingThumbnail || ''),
    });

    // Baseline snapshot once loading finishes (edit) / on mount (create).
    useEffect(() => {
        if (loading) return;
        savedSnapshotRef.current = serializeForm();
        guardReadyRef.current = true;
        setIsDirty(false);
    }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

    // Recompute dirty on any tracked change.
    useEffect(() => {
        if (!guardReadyRef.current) return;
        setIsDirty(serializeForm() !== savedSnapshotRef.current);
    }, [nameEn, nameBn, slug, categoryId, status, prompt, negativePrompt, imageSize, requireUserImage, allowMultipleImages, requiredImageCount, userNote, templateTags, imageLabels, genders, fields, thumbnailFile, thumbnailUrl, existingThumbnail]); // eslint-disable-line react-hooks/exhaustive-deps

    // Browser-level guard (refresh / tab close / typed URL).
    useEffect(() => {
        if (!isDirty) return;
        const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [isDirty]);

    // Leave the page — confirm first when there are unsaved changes.
    const attemptLeave = (to) => {
        if (isDirty) setLeaveTo(to);
        else navigate(to);
    };
    const markFormSaved = () => { savedSnapshotRef.current = serializeForm(); setIsDirty(false); };

    const handleSave = async () => {
        const error = validate();
        if (error) {
            notifications.show({ title: t('aiTemplateForm.missingInfo'), message: error, color: 'red' });
            return;
        }
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append('name', nameEn.trim());
            fd.append('nameBn', nameBn.trim());
            const finalSlug = (slug && slug.trim())
                || slugify(nameEn)
                || `template-${Date.now()}`;
            fd.append('slug', finalSlug);
            fd.append('categoryId', categoryId);
            fd.append('prompt', prompt.trim());
            fd.append('negativePrompt', negativePrompt.trim());
            fd.append('status', status);
            fd.append('sortOrder', String(sortOrder || 0));
            fd.append('isFree', String(isFree));
            fd.append('isGlobal', String(isGlobal));
            fd.append('userNote', sanitizeText(userNote, { maxLen: 500 })); // '' clears the note
            fd.append('photoSize', 'jersey_full');
            fd.append('fields', JSON.stringify(fields.map(({ _keyTouched, ...f }) => ({
                ...f,
                choices: (f.choices || []).map(({ _valueTouched, ...c }) => c),
            }))));
            fd.append('tags', JSON.stringify(templateTags));
            fd.append('genders', JSON.stringify(genders));
            fd.append('watermarkEnabled', String(watermarkEnabled));
            fd.append('watermarkX', String(watermarkX ?? 80));
            fd.append('watermarkY', String(watermarkY ?? 88));
            fd.append('requireUserImage', String(requireUserImage));
            fd.append('imageSize', imageSize);
            fd.append('allowMultipleImages', String(allowMultipleImages));
            // Image inputs = [{ label, required }]. Multi: the defined list.
            // Single: one input. None: empty.
            const imageInputs = !requireUserImage
                ? []
                : (allowMultipleImages
                    ? imageLabels.map(x => ({ label: (x.label || '').trim(), required: x.required !== false }))
                    : [{ label: (imageLabels[0]?.label || '').trim(), required: true }]);
            fd.append('requiredImageCount', String(allowMultipleImages ? Math.max(1, imageInputs.length) : 1));
            fd.append('imageLabels', JSON.stringify(imageInputs));
            if (thumbnailFile) fd.append('thumbnail', thumbnailFile);
            if (rawImageFile) fd.append('rawImage', rawImageFile);
            rawExtraImageFiles.forEach((f, i) => fd.append(`rawImage_${i + 2}`, f));

            if (isEdit) {
                const updated = await aiTemplateService.update(id, fd);
                const saved = updated?.data;
                if (saved && saved.requireUserImage !== undefined) {
                    setRequireUserImage(String(saved.requireUserImage) !== 'false');
                }
                markFormSaved(); // clean baseline so the guard doesn't fire after saving
                notifications.show({ title: t('aiTemplateForm.saved'), message: t('aiTemplateForm.templateUpdated'), color: 'green' });
            } else {
                await aiTemplateService.create(fd);
                setIsDirty(false); // creating navigates away; don't trigger the leave guard
                notifications.show({ title: t('aiTemplateForm.created'), message: t('aiTemplateForm.templateCreated'), color: 'green' });
                navigate('/admin/ai-templates');
            }
            // Invalidate the gallery's client-side cache so the change shows up
            // immediately instead of waiting out the 24h TTL.
            useAiTemplateCacheStore.getState().clear();
        } catch (err) {
            notifications.show({ title: t('aiTemplateForm.error'), message: err?.message || t('aiTemplateForm.saveFailed'), color: 'red' });
        } finally {
            setSaving(false);
        }
    };

    // ── Test generate core ─────────────────────────────────────────────────
    const doGenerate = async (fileToUse) => {
        const f = fileToUse || testFile;
        if (requireUserImage && !f) return;
        if (!prompt.trim()) return;

        setTestGenerating(true);
        setTestResult(null);
        setTestProgress(0);
        testProgressTimer.current = setInterval(() => {
            setTestProgress(p => (p >= 99 ? 99 : Math.min(p + 0.6, 99)));
        }, 60);

        try {
            const fd = new FormData();
            // Multi-image templates: merge every test photo into ONE grid image so the
            // AI receives a single composite reference (same as the user generate page —
            // one image sent = lower AI cost). If merging fails, fall back to sending
            // them separately as before.
            const allImages = [f, ...testExtraFiles].filter(Boolean);
            let mergedImage = null;
            if (allowMultipleImages && allImages.length > 1) {
                try { mergedImage = await mergeImagesToGrid(allImages); } catch { mergedImage = null; }
            }
            if (mergedImage) {
                fd.append('image', mergedImage);
            } else {
                if (f) fd.append('image', f);
                testExtraFiles.forEach((ef, i) => fd.append(`image_${i + 2}`, ef));
            }
            fd.append('size', imageSize);
            fd.append('dress', 'original');
            fd.append('dressCode', 'original');
            fd.append('bg', 'no_change');
            // Default per field (colorpicker → defaultColor, dropdown/options →
            // first choice) so an untouched field still reproduces the look.
            const fieldDefaultFor = (key) => {
                const fld = fields.find(f => f.key === key);
                if (!fld) return '';
                if (fld.type === 'colorpicker') return fld.defaultColor || '';
                if (fld.type === 'dropdown' || fld.type === 'options') {
                    const c = (fld.choices || [])[0];
                    return c?.value ?? c?.label ?? '';
                }
                if (fld.type === 'date') return todayISO();
                return '';
            };
            const allKeys = fields.length > 0 ? fields.map(f => f.key) : detectedKeys;
            let populatedPrompt = allKeys.reduce((p, key) => {
                let val = (testFieldValues[key] || '').toString().trim() || fieldDefaultFor(key);
                // Date fields hold an ISO date — render it as a human-readable date in
                // the field's language (auto mode always uses today's date).
                const fld = fields.find(f => f.key === key);
                if (fld?.type === 'date') val = formatPromptDate(fld.dateAuto !== false ? todayISO() : (val || todayISO()), fld.dateLang);
                const escaped = key.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
                return p.replace(new RegExp(`\\{${escaped}\\}`, 'g'), val || `{${key}}`);
            }, prompt.trim());
            // Auto-date variables ({date-bn} etc.) → today's date in each format
            for (const [k, v] of Object.entries(dateVariantValues())) {
                populatedPrompt = populatedPrompt.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
            }
            const fullPrompt = negativePrompt.trim()
                ? `${populatedPrompt}\n\nNEGATIVE (DO NOT include any of the following): ${negativePrompt.trim()}`
                : populatedPrompt;
            fd.append('extraGuide', fullPrompt);
            fd.append('extraGuideLabel', `[TEST] ${nameEn || 'template'}`);
            fd.append('displayName', `[TEST] ${nameEn || 'template'} - ${new Date().toISOString().slice(0, 19)}`);
            if (id) fd.append('aiTemplateId', String(id));
            fd.append('aiTemplateGeneration', 'true');
            fd.append('requireUserImage', String(requireUserImage));

            const response = await imageGenerationService.generateImage(fd);
            if (response?.data) {
                setTestProgress(100);
                setTestResult({ url: response.data.aiImage, id: response.data.id });
                notifications.show({ title: t('aiTemplateForm.testReady'), message: t('aiTemplateForm.testReadyMsg'), color: 'green' });
                if (!isEdit) setWizardStep(1);
            } else {
                throw new Error('No data');
            }
        } catch (err) {
            // AI model out of balance / rate-limited → hide the raw provider error.
            const message = isAiCapacityError(err)
                ? t('aiTemplateForm.generationUnavailable')
                : (err?.message || t('aiTemplateForm.couldNotGenerate'));
            notifications.show({ title: t('aiTemplateForm.testFailed'), message, color: 'red' });
        } finally {
            clearInterval(testProgressTimer.current);
            setTestGenerating(false);
        }
    };

    // The generated result lives on R2/CDN and can 404 for a moment right after
    // upload. Retry a couple of times with a cache-busting query before giving up,
    // so a freshly-generated image doesn't show as permanently broken.
    const handleResultImgError = (e) => {
        const el = e.currentTarget;
        const tries = Number(el.dataset.retries || 0);
        if (tries >= 3 || !testResult?.url) return;
        el.dataset.retries = String(tries + 1);
        setTimeout(() => {
            el.src = `${testResult.url}${testResult.url.includes('?') ? '&' : '?'}r=${tries + 1}`;
        }, 1200 * (tries + 1));
    };

    const handleTestFileSelected = async (f, memo = null) => {
        if (!f || !f.type.startsWith('image/')) {
            notifications.show({ title: t('aiTemplateForm.invalidFile'), message: t('aiTemplateForm.invalidFileMsg'), color: 'red' });
            return;
        }
        setOptimizingCount(c => c + 1);
        const optimized = await optimizeImage(f, { targetBytes: 490_000 }).catch(() => f);
        setOptimizingCount(c => c - 1);
        if (testPreview?.startsWith('blob:')) URL.revokeObjectURL(testPreview);
        const url = URL.createObjectURL(optimized);
        if (memo) cropMemoRef.current[url] = memo;
        setTestFile(optimized);
        setTestPreview(url);
        setTestResult(null);
        if (pendingGenerateRef.current) {
            pendingGenerateRef.current = false;
            if (fields.length === 0) await doGenerate(optimized);
        }
    };

    // Multi-photo test set: file a photo that came out of the crop stage — the first one becomes the
    // primary test photo, the rest fill the extra slots (same rule as the old direct add).
    const addTestPhoto = async (file, memo = null) => {
        if (!file) return;
        setOptimizingCount(c => c + 1);
        const optimized = await optimizeImage(file, { targetBytes: 490_000 }).catch(() => file);
        setOptimizingCount(c => c - 1);
        const preview = URL.createObjectURL(optimized);
        if (memo) cropMemoRef.current[preview] = memo;
        setTestResult(null);
        if (!testFile) {
            if (testPreview?.startsWith('blob:')) URL.revokeObjectURL(testPreview);
            setTestFile(optimized); setTestPreview(preview);
        } else {
            setTestExtraFiles(prev => [...prev, optimized]);
            setTestExtraPreviews(prev => [...prev, preview]);
        }
    };

    // Re-crop result: swap the photo that owned `oldUrl` (primary or an extra slot) for the newly cropped one.
    const replaceTestPhoto = async (oldUrl, file, memo) => {
        if (!file || !oldUrl) return;
        setOptimizingCount(c => c + 1);
        const optimized = await optimizeImage(file, { targetBytes: 490_000 }).catch(() => file);
        setOptimizingCount(c => c - 1);
        const url = URL.createObjectURL(optimized);
        delete cropMemoRef.current[oldUrl];
        if (memo) cropMemoRef.current[url] = memo;
        setTestResult(null);
        if (oldUrl === testPreview) {
            if (testPreview?.startsWith('blob:')) URL.revokeObjectURL(testPreview);
            setTestFile(optimized); setTestPreview(url);
        } else {
            const i = testExtraPreviews.indexOf(oldUrl);
            if (i < 0) { URL.revokeObjectURL(url); return; }
            URL.revokeObjectURL(oldUrl);
            setTestExtraFiles(prev => prev.map((f, j) => (j === i ? optimized : f)));
            setTestExtraPreviews(prev => prev.map((u, j) => (j === i ? url : u)));
        }
    };

    // Click a test-photo tile → re-open the crop stage with the FULL original image and the crop area chosen
    // last time (photos added without a crop step are simply cropped from their current file).
    const reCropPhoto = (previewUrl) => {
        if (!previewUrl) return;
        const memo = cropMemoRef.current[previewUrl];
        const current = previewUrl === testPreview ? testFile : testExtraFiles[testExtraPreviews.indexOf(previewUrl)];
        const original = memo?.original || current;
        if (!original) return;
        recropRef.current = { previewUrl };
        openCropStage(original, 'testReplace', memo?.crop || null);
    };

    // Preview URL of a reference model's photo if it is in the test set (the CROPPED version), else null.
    const previewOfModel = (key) => {
        const prefix = `${key}.`;
        if ((testFile?.name || '').startsWith(prefix)) return testPreview;
        const i = testExtraFiles.findIndex(f => (f?.name || '').startsWith(prefix));
        return i >= 0 ? testExtraPreviews[i] : null;
    };

    // Pick a built-in reference model photo as the test input (single mode). Fetches
    // the remote sample into a File and runs it through the same path as an upload.
    const [pickingModel, setPickingModel] = useState(null);
    // Once downloaded, a reference model File is reused for the rest of the session —
    // re-picking must not pay the proxy download again.
    const referenceFileCache = useRef(new Map());
    const fetchReferenceFile = async (model) => {
        const cached = referenceFileCache.current.get(model.key);
        if (cached) return cached;
        // Proxied through the backend to avoid CORS on the files host.
        const blob = await aiTemplateService.referenceImage(model.url);
        const ext = (blob?.type && blob.type.split('/')[1]) || 'webp';
        const file = new File([blob], `${model.key}.${ext}`, { type: blob?.type || 'image/webp' });
        referenceFileCache.current.set(model.key, file);
        return file;
    };
    const handlePickReferenceModel = async (model) => {
        if (pickingModel) return;
        setPickingModel(model.key);
        try {
            openTestCrop(await fetchReferenceFile(model)); // crop (or skip = whole image) before it becomes the test photo
        } catch {
            notifications.show({ title: t('aiTemplateForm.error'), message: t('aiTemplateForm.couldNotLoadImage'), color: 'red' });
        } finally {
            setPickingModel(null);
        }
    };

    // Is a reference model currently part of the test set? (files are named `${key}.ext`)
    const isModelSelected = (key) =>
        [testFile, ...testExtraFiles].filter(Boolean).some(f => (f?.name || '').startsWith(`${key}.`));

    // Multi-image mode: clicking a reference photo toggles it in the test set. The first
    // one becomes the primary; the rest fill the extra slots. Clicking again removes it.
    const handleToggleReferenceModelMulti = async (model) => {
        if (pickingModel) return;
        const prefix = `${model.key}.`;
        const inPrimary = (testFile?.name || '').startsWith(prefix);
        const extraIdx = testExtraFiles.findIndex(f => (f?.name || '').startsWith(prefix));

        if (inPrimary) {
            // Remove primary; promote the first extra (if any) to primary.
            if (testPreview?.startsWith('blob:')) URL.revokeObjectURL(testPreview);
            if (testExtraFiles.length) {
                const [nf, ...restF] = testExtraFiles;
                const [np, ...restP] = testExtraPreviews;
                setTestFile(nf); setTestPreview(np);
                setTestExtraFiles(restF); setTestExtraPreviews(restP);
            } else {
                setTestFile(null); setTestPreview(null);
            }
            setTestResult(null);
            return;
        }
        if (extraIdx >= 0) { removeTestExtraFile(extraIdx); return; }

        setPickingModel(model.key);
        try {
            openCropStage(await fetchReferenceFile(model), 'testPick'); // crop first; addTestPhoto() files it on apply / skip
        } catch {
            notifications.show({ title: t('aiTemplateForm.error'), message: t('aiTemplateForm.couldNotLoadImage'), color: 'red' });
        } finally {
            setPickingModel(null);
        }
    };

    const handleTestGenerate = () => {
        if (!prompt.trim()) return;
        if (requireUserImage && !testFile) {
            pendingGenerateRef.current = !allowMultipleImages;
            (allowMultipleImages ? addMoreTestInputRef : testFileInputRef).current?.click();
            return;
        }
        doGenerate();
    };

    const handleReplaceAndGenerate = () => {
        if (allowMultipleImages) {
            if (testPreview?.startsWith('blob:')) URL.revokeObjectURL(testPreview);
            setTestExtraPreviews(prev => { prev.forEach(u => URL.revokeObjectURL(u)); return []; });
            setTestFile(null);
            setTestPreview(null);
            setTestExtraFiles([]);
            setTestResult(null);
            if (wizardStep === 1) setWizardStep(0);
        } else {
            pendingGenerateRef.current = true;
            testFileInputRef.current?.click();
        }
    };

    const downloadTestResult = async () => {
        if (!testResult?.id) return;
        setDownloading(true);
        try {
            const blob = await imageGenerationService.downloadImage(testResult.id, 'ai');
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${slug || 'test'}-${Date.now()}.jpg`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            imageGenerationService.trackDownload(testResult.id).catch(() => {});
        } catch {
            notifications.show({ title: t('aiTemplateForm.error'), message: t('aiTemplateForm.downloadFailed'), color: 'red' });
        } finally {
            setDownloading(false);
        }
    };

    const useTestAsThumbnail = async () => {
        if (!testResult?.id) return;
        setSettingThumbnail(true);
        try {
            const blob = await imageGenerationService.downloadImage(testResult.id, 'ai');
            const f = new File([blob], `thumbnail-${Date.now()}.jpg`, { type: blob.type || 'image/jpeg' });
            setThumbnailFile(f);
            if (thumbnailPreview?.startsWith('blob:')) URL.revokeObjectURL(thumbnailPreview);
            setThumbnailPreview(URL.createObjectURL(f));
            setThumbnailUrl(testResult.url);
            if (testFile) setRawImageFile(testFile);
            setRawExtraImageFiles([...testExtraFiles]);
            // Once saved as the thumbnail it's shown in the Thumbnail card above, so reset the
            // whole test input — otherwise the Test Generate card shows a stale (often broken,
            // once its blob is gone) preview of the photo we just promoted to thumbnail.
            setTestResult(null);
            if (testPreview?.startsWith('blob:')) URL.revokeObjectURL(testPreview);
            testExtraPreviews.forEach(u => { if (u?.startsWith('blob:')) URL.revokeObjectURL(u); });
            setTestFile(null);
            setTestPreview(null);
            setTestExtraFiles([]);
            setTestExtraPreviews([]);
            // Note: the number of image inputs is defined by the Image Settings
            // list (label + required), not by how many test photos were uploaded.
            if (!isEdit) setWizardStep(2);
            notifications.show({
                title: t('aiTemplateForm.thumbnailSaved'),
                message: isEdit ? t('aiTemplateForm.thumbnailSavedEditMsg') : t('aiTemplateForm.thumbnailSavedCreateMsg'),
                color: 'green',
            });
        } catch {
            notifications.show({ title: t('aiTemplateForm.error'), message: t('aiTemplateForm.couldNotLoadImage'), color: 'red' });
        } finally {
            setSettingThumbnail(false);
        }
    };

    // ── Sync a single field's {key} into the prompt at the natural spot ─────
    const handleSyncField = async (field) => {
        const key = (field.key || '').trim();
        if (!key) {
            notifications.show({ title: t('aiTemplateForm.syncNoKeyTitle'), message: t('aiTemplateForm.syncNoKeyMsg'), color: 'red' });
            return;
        }
        if (prompt.includes(`{${key}}`)) return; // already synced
        setSyncingFieldId(field.id);
        try {
            const result = await aiTemplateService.syncField(prompt, key, field.label || '', field.type || 'text');
            const data = result?.data || result;
            if (typeof data?.prompt === 'string' && data.prompt.trim()) {
                setPrompt(data.prompt);
                notifications.show({ title: t('aiTemplateForm.syncedTitle'), message: t('aiTemplateForm.syncedMsg', { key }), color: 'grape' });
            }
        } catch (err) {
            const message = isAiCapacityError(err) ? t('aiTemplateForm.analysisUnavailable') : (err?.message || t('aiTemplateForm.syncFailedMsg'));
            notifications.show({ title: t('aiTemplateForm.syncFailedTitle'), message, color: 'red' });
        } finally {
            setSyncingFieldId(null);
        }
    };

    // ── AI: suggest one more choice for a choice-based field, from the prompt ─
    const handleSuggestChoice = async (field) => {
        if (!prompt.trim()) return;
        try {
            const result = await aiTemplateService.suggestChoice(
                prompt, field.key || '', field.label || '', field.type || 'options', field.choices || [],
            );
            const data = result?.data || result;
            const choice = data?.choice;
            if (!choice || (!choice.value && !choice.label)) {
                notifications.show({ title: t('aiTemplateForm.nothingToAdd'), message: t('aiTemplateForm.aiSuggestChoiceNothing'), color: 'blue' });
                return;
            }
            const normalized = field.type === 'colorpicker'
                ? { label: choice.value || choice.label, value: choice.value || choice.label }
                : { label: choice.label || '', labelBn: choice.labelBn || '', value: choice.value || choice.label || '' };
            updateField(field.id, 'choices', [...(field.choices || []), normalized]);
            notifications.show({ title: t('aiTemplateForm.aiSuggestChoiceAdded'), message: t('aiTemplateForm.aiSuggestChoiceAddedMsg'), color: 'grape' });
        } catch (err) {
            const message = isAiCapacityError(err) ? t('aiTemplateForm.analysisUnavailable') : (err?.message || t('aiTemplateForm.aiSuggestChoiceFailed'));
            notifications.show({ title: t('aiTemplateForm.analysisFailed'), message, color: 'red' });
        }
    };

    // ── AI: build ONE field from a plain-language instruction (modal) ────────
    const handleAddFieldFromInstruction = async () => {
        const instruction = addFieldInstruction.trim();
        if (!instruction) return;
        setAddingField(true);
        try {
            const existingKeys = fields.map(f => f.key).filter(Boolean);
            const result = await aiTemplateService.addField(prompt, instruction, existingKeys);
            const data = result?.data || result;
            const f = data?.field;
            if (f && (f.key || f.label)) {
                const newField = {
                    id: uid(),
                    label:         f.label || '',
                    labelBn:       f.labelBn || '',
                    key:           f.key || toKey(f.label),
                    type:          f.type || 'text',
                    placeholder:   f.placeholder || '',
                    placeholderBn: f.placeholderBn || '',
                    required:      !!f.required,
                    charLimit:     f.charLimit ?? ((f.type || 'text') === 'text' ? 50 : null),
                    minLength:     f.minLength ?? ((f.type || 'text') === 'text' ? 0 : null),
                    minValue:      f.minValue ?? null,
                    maxValue:      f.maxValue ?? null,
                    defaultColor:  f.defaultColor || '#000000',
                    choices:       Array.isArray(f.choices) ? f.choices : [],
                    suggestions:   Array.isArray(f.suggestions) ? f.suggestions : [],
                    dateAuto:      f.dateAuto !== false,
                    dateLang:      ['en', 'ar'].includes(f.dateLang) ? f.dateLang : 'bn',
                    _keyTouched:   true,
                };
                setFields(prev => [...prev, newField]);
                if (typeof data.prompt === 'string' && data.prompt.trim()) setPrompt(data.prompt);
                notifications.show({ title: t('aiTemplateForm.aiAddFieldAdded'), message: t('aiTemplateForm.aiAddFieldAddedMsg', { label: newField.label || newField.key }), color: 'grape' });
                setAddFieldModalOpen(false);
                setAddFieldInstruction('');
            } else {
                notifications.show({ title: t('aiTemplateForm.nothingToAdd'), message: t('aiTemplateForm.aiAddFieldNothingMsg'), color: 'blue' });
            }
        } catch (err) {
            const message = isAiCapacityError(err) ? t('aiTemplateForm.analysisUnavailable') : (err?.message || t('aiTemplateForm.aiAddFieldFailed'));
            notifications.show({ title: t('aiTemplateForm.analysisFailed'), message, color: 'red' });
        } finally {
            setAddingField(false);
        }
    };

    // ── AI: enhance the CURRENT prompt using this template's thumbnail ───────
    // Scans the thumbnail, enriches the prompt (keeping every {key}), refreshes the
    // negative prompt, and appends any dynamic fields the image shows that aren't defined.
    const getThumbnailFile = async () => {
        if (thumbnailFile) return thumbnailFile;
        const src = thumbnailPreview || thumbnailUrl || existingThumbnail;
        if (!src) return null;
        const toFile = (blob) => {
            const ext = (blob?.type && blob.type.split('/')[1]) || 'jpg';
            return new File([blob], `thumbnail.${ext}`, { type: blob?.type || 'image/jpeg' });
        };
        try {
            // cache:'reload' — the <img> preview cached this URL as an opaque no-cors
            // response; reusing it here would fail the CORS check.
            const res = await fetch(src, { mode: 'cors', cache: 'reload' });
            if (!res.ok) throw new Error(String(res.status));
            return toFile(await res.blob());
        } catch {
            // files.sohozkaj.com sends no CORS headers, so the direct fetch fails for
            // saved thumbnails — pull them through the backend proxy instead.
            try {
                return toFile(await aiTemplateService.referenceImage(src));
            } catch {
                return null;
            }
        }
    };

    // In resolve mode, auto-open the AI enhance modal pre-filled with the user's
    // complaint plus fix directives, so solving takes a single click instead of the
    // admin hunting for the right analysis to run. One-shot per visit — closing the
    // modal must not re-open it. Waits for the template load (prompt set) so the
    // enhance run has everything it needs.
    const resolveAutoOpenedRef = useRef(false);
    useEffect(() => {
        if (!resolveFeedbackId || !resolveCtx || resolveDone) return;
        if (resolveAutoOpenedRef.current || !prompt.trim()) return;
        resolveAutoOpenedRef.current = true;
        if (resolveGuidance) {
            setAddFieldInstruction(
                `User complaint (negative feedback): "${resolveGuidance}".\n` +
                'Fix ONLY this issue: strengthen just the sentence(s) about it with MANDATORY / HIGHEST PRIORITY wording so the image generator cannot ignore it. Copy every unrelated sentence word-for-word unchanged and keep every {key} placeholder exactly as it is.'
            );
        }
        setAddFieldModalOpen(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolveFeedbackId, resolveCtx, resolveDone, prompt]);

    // In resolve mode, running any AI prompt re-analysis marks the feedback solved.
    const markFeedbackSolved = async () => {
        if (!resolveFeedbackId || resolveDone) return;
        try {
            await aiTemplateService.resolveNegativeFeedback(resolveFeedbackId, true);
            setResolveDone(true);
            notifications.show({ title: t('aiTemplateFeedback.markedSolved'), message: '', color: 'green' });
        } catch { /* non-fatal — the report just stays pending */ }
    };

    const handleEnhancePrompt = async () => {
        if (!prompt.trim()) {
            notifications.show({ title: t('aiTemplateForm.enhanceFailedTitle'), message: t('aiTemplateForm.enhanceNeedPrompt'), color: 'blue' });
            return;
        }
        setEnhancingPrompt(true);
        try {
            const file = await getThumbnailFile();
            if (!file) {
                notifications.show({ title: t('aiTemplateForm.enhanceFailedTitle'), message: t('aiTemplateForm.enhanceNeedThumbnail'), color: 'blue' });
                return;
            }
            const existingKeys = fields.map(f => f.key).filter(Boolean);
            // In resolve mode, fold the user's complaint into the enhance instruction —
            // unless the visible instruction field already carries it (auto-prefill).
            const needsGuidance = resolveCtx && resolveGuidance && !addFieldInstruction.includes(resolveGuidance);
            const instruction = [addFieldInstruction.trim(), needsGuidance ? `Fix this user complaint about the result: "${resolveGuidance}".` : '']
                .filter(Boolean).join(' ');
            // Resolve mode = surgical fix: backend only touches the offending
            // sentence(s) instead of running the full prompt enrichment.
            const result = await aiTemplateService.enhancePrompt(file, prompt, negativePrompt, existingKeys, instruction, resolveCtx ? 'fix-issue' : '');
            const data = result?.data || result;

            if (typeof data?.prompt === 'string' && data.prompt.trim()) setPrompt(data.prompt);
            if (typeof data?.negativePrompt === 'string') setNegativePrompt(data.negativePrompt);

            const seen = new Set(existingKeys);
            const additions = (Array.isArray(data?.newFields) ? data.newFields : [])
                .filter(f => (f.key || f.label) && !seen.has(f.key))
                .map(f => ({
                    id: uid(),
                    label:         f.label || '',
                    labelBn:       f.labelBn || '',
                    key:           f.key || toKey(f.label),
                    type:          f.type || 'text',
                    placeholder:   f.placeholder || '',
                    placeholderBn: f.placeholderBn || '',
                    required:      !!f.required,
                    charLimit:     f.charLimit ?? ((f.type || 'text') === 'text' ? 50 : null),
                    minLength:     f.minLength ?? ((f.type || 'text') === 'text' ? 0 : null),
                    minValue:      f.minValue ?? null,
                    maxValue:      f.maxValue ?? null,
                    defaultColor:  f.defaultColor || '#000000',
                    choices:       Array.isArray(f.choices) ? f.choices : [],
                    suggestions:   Array.isArray(f.suggestions) ? f.suggestions : [],
                    dateAuto:      f.dateAuto !== false,
                    dateLang:      ['en', 'ar'].includes(f.dateLang) ? f.dateLang : 'bn',
                    _keyTouched:   true,
                }));
            if (additions.length) setFields(prev => [...prev, ...additions]);

            notifications.show({
                title: t('aiTemplateForm.enhanceDone'),
                message: additions.length
                    ? t('aiTemplateForm.enhanceDoneMsgFields', { count: additions.length })
                    : t('aiTemplateForm.enhanceDoneMsg'),
                color: 'green',
            });
            await markFeedbackSolved();
            setAddFieldModalOpen(false);
            setAddFieldInstruction('');
        } catch (err) {
            const message = isAiCapacityError(err) ? t('aiTemplateForm.analysisUnavailable') : (err?.message || t('aiTemplateForm.enhanceFailed'));
            notifications.show({ title: t('aiTemplateForm.enhanceFailedTitle'), message, color: 'red' });
        } finally {
            setEnhancingPrompt(false);
        }
    };

    // Convert every dropdown/options option VALUE (the prompt phrase, not the Bangla
    // label) into slash-token form via the same tokenizer. Kept single-line (comma
    // separated) since option values live in single-line inputs. Values already in
    // token form ("/…") or empty are skipped; a per-value failure keeps the original.
    // `sourceFields` lets callers tokenize a freshly-built field set (e.g. straight
    // from image analysis) instead of the current `fields` state, which updates
    // asynchronously; defaults to state so existing callers are unchanged.
    const tokenizeFieldValues = async (sourceFields = fields) => {
        const targets = sourceFields.filter(f => (f.type === 'dropdown' || f.type === 'options') && Array.isArray(f.choices) && f.choices.length);
        if (!targets.length) return;

        const tokenizeValue = async (val) => {
            const v = (val || '').toString().trim();
            if (!v || v.startsWith('/')) return null; // empty or already tokenized
            try {
                const r = await aiTemplateService.tokenizePrompt(v, []);
                const d = r?.data || r;
                const tv = typeof d?.prompt === 'string' ? d.prompt.trim() : '';
                return tv || null;
            } catch {
                return null;
            }
        };

        const updated = await Promise.all(sourceFields.map(async (f) => {
            if ((f.type !== 'dropdown' && f.type !== 'options') || !Array.isArray(f.choices) || !f.choices.length) return f;
            const choices = await Promise.all(f.choices.map(async (c) => {
                const tv = await tokenizeValue(c.value);
                return tv ? { ...c, value: tv } : c;
            }));
            return { ...f, choices };
        }));
        setFields(updated);
    };

    // ── Migrate an existing DETAILED prose prompt → SHORT TOKEN prompt ───────
    // Converts whatever is in the prompt box into the new slash-token form while
    // preserving EVERY dynamic-field {key} placeholder (no field is dropped).
    const handleTokenizePrompt = async () => {
        if (!prompt.trim()) {
            notifications.show({ title: t('aiTemplateForm.tokenizeFailedTitle'), message: t('aiTemplateForm.enhanceNeedPrompt'), color: 'blue' });
            return;
        }
        setTokenizingPrompt(true);
        try {
            const existingKeys = fields.map(f => f.key).filter(Boolean);
            const result = await aiTemplateService.tokenizePrompt(prompt, existingKeys);
            const data = result?.data || result;
            const raw = typeof data?.prompt === 'string' ? data.prompt.trim() : '';
            if (!raw) throw new Error(t('aiTemplateForm.tokenizeFailed'));

            // Guard: never migrate if it would silently lose a field placeholder.
            const dropped = existingKeys.filter(k => !raw.includes(`{${k}}`));
            if (dropped.length) {
                notifications.show({ title: t('aiTemplateForm.tokenizeFailedTitle'), message: t('aiTemplateForm.tokenizeDroppedFields', { keys: dropped.join(', ') }), color: 'red' });
                return;
            }
            // Neutralize the descriptor on dropdown/options {field} tokens so the user's
            // selected value drives the output (not a value baked into the token by the
            // AI). Colorpicker/other tokens keep their descriptor. Then one token per line.
            const selfDescribingKeys = fields
                .filter(f => f.type === 'dropdown' || f.type === 'options')
                .map(f => f.key).filter(Boolean);
            setPrompt(tokensToLines(alignFieldTokens(raw, selfDescribingKeys)));

            // Also convert each dropdown/options option VALUE (the prompt text, not the
            // Bangla label) into the same slash-token form, so the phrase injected into
            // the {field} token stays uniform tokens. Values already in token form (start
            // with "/") and empty ones are left as-is. Run in parallel; failures keep the
            // original value.
            await tokenizeFieldValues();

            notifications.show({ title: t('aiTemplateForm.tokenizeDone'), message: t('aiTemplateForm.tokenizeDoneMsg'), color: 'green' });
        } catch (err) {
            const message = isAiCapacityError(err) ? t('aiTemplateForm.analysisUnavailable') : (err?.message || t('aiTemplateForm.tokenizeFailed'));
            notifications.show({ title: t('aiTemplateForm.tokenizeFailedTitle'), message, color: 'red' });
        } finally {
            setTokenizingPrompt(false);
        }
    };

    // ── AI prompt analysis (per section) ───────────────────────────────────
    // ② Dynamic Fields: detect the dynamic fields AND rewrite the prompt so the
    // matching {key} placeholders are inserted where they belong.
    const handleAnalyzeFields = async () => {
        if (!prompt.trim()) return;
        setAnalyzingFields(true);
        try {
            const result = await aiTemplateService.analyzePrompt(prompt, knownCategories, thumbnailUrl || existingThumbnail, resolveCtx ? resolveGuidance : null);
            const data = result?.data || result;
            await markFeedbackSolved();

            if (Array.isArray(data.fields) && data.fields.length) {
                setFields(data.fields.map(f => ({
                    id:           uid(),
                    label:        f.label || '',
                    labelBn:      f.labelBn || '',
                    key:          f.key || toKey(f.label),
                    type:         f.type || 'text',
                    placeholder:  f.placeholder || '',
                    placeholderBn: f.placeholderBn || '',
                    required:     !!f.required,
                    charLimit:    f.charLimit ?? ((f.type || 'text') === 'text' ? 50 : null),
                    minLength:    f.minLength ?? ((f.type || 'text') === 'text' ? 0 : null),
                    minValue:     f.minValue ?? null,
                    maxValue:     f.maxValue ?? null,
                    defaultColor: f.defaultColor || '#000000',
                    choices:      Array.isArray(f.choices) ? f.choices : [],
                    suggestions:  Array.isArray(f.suggestions) ? f.suggestions : [],
                    dateAuto:     f.dateAuto !== false,
                    dateLang:     ['en', 'ar'].includes(f.dateLang) ? f.dateLang : 'bn',
                    _keyTouched:  true,
                })));
                // Insert the {key} placeholders into the prompt.
                if (data.prompt) setPrompt(typeof data.prompt === 'string' ? data.prompt : JSON.stringify(data.prompt));
                notifications.show({ title: t('aiTemplateForm.fieldsReady'), message: t('aiTemplateForm.fieldsReadyMsg', { count: data.fields.length }), color: 'grape' });
            } else {
                setFields([]);
                notifications.show({ title: t('aiTemplateForm.noFields'), message: t('aiTemplateForm.noFieldsMsg'), color: 'blue' });
            }
        } catch {
            notifications.show({ title: t('aiTemplateForm.analysisFailed'), message: t('aiTemplateForm.couldNotAnalyzePrompt'), color: 'red' });
        } finally {
            setAnalyzingFields(false);
        }
    };

    // ③ Basic Info: fill Name (En/Bn), best Category, and regenerate the Slug.
    const handleAnalyzeBasicInfo = async () => {
        if (!prompt.trim()) return;
        setAnalyzingBasic(true);
        try {
            const result = await aiTemplateService.analyzePrompt(prompt, knownCategories, thumbnailUrl || existingThumbnail);
            const data = result?.data || result;

            if (data.nameEn) setNameEn(data.nameEn);
            if (data.nameBn) setNameBn(data.nameBn);

            if (data.categorySlug) {
                const matched = knownCategories.find(c => c.slug === data.categorySlug);
                if (matched) setCategoryId(String(matched.id));
            }

            if (data.nameEn) setSlug(slugify(data.nameEn));
            if (Array.isArray(data.tags) && data.tags.length) mergeTags(data.tags);

            notifications.show({ title: t('aiTemplateForm.basicInfoReady'), message: t('aiTemplateForm.basicInfoReadyMsg'), color: 'grape' });
        } catch {
            notifications.show({ title: t('aiTemplateForm.analysisFailed'), message: t('aiTemplateForm.couldNotAnalyzePrompt'), color: 'red' });
        } finally {
            setAnalyzingBasic(false);
        }
    };

    // Negative Prompt: derive an "avoid" list from the prompt, formatted as slash
    // tokens one per line (same token style as the main prompt).
    const handleAnalyzeNegative = async () => {
        if (!prompt.trim()) return;
        setAnalyzingNegative(true);
        try {
            const result = await aiTemplateService.analyzePrompt(prompt, knownCategories, thumbnailUrl || existingThumbnail);
            const data = result?.data || result;
            const neg = toSlashTokenList(data.negativePrompt || '');
            if (neg) {
                setNegativePrompt(neg);
                notifications.show({ title: t('aiTemplateForm.negativePromptReady'), message: t('aiTemplateForm.negativePromptReadyMsg'), color: 'grape' });
            } else {
                notifications.show({ title: t('aiTemplateForm.nothingToAdd'), message: t('aiTemplateForm.nothingToAddMsg'), color: 'blue' });
            }
        } catch {
            notifications.show({ title: t('aiTemplateForm.analysisFailed'), message: t('aiTemplateForm.couldNotAnalyzePrompt'), color: 'red' });
        } finally {
            setAnalyzingNegative(false);
        }
    };

    // Convert whatever "avoid" phrases are already in the negative box into slash
    // tokens, one per line — purely local, no AI call.
    const handleTokenizeNegative = () => {
        if (!negativePrompt.trim()) {
            notifications.show({ title: t('aiTemplateForm.tokenizeFailedTitle'), message: t('aiTemplateForm.enhanceNeedPrompt'), color: 'blue' });
            return;
        }
        const next = toSlashTokenList(negativePrompt);
        if (!next) return;
        setNegativePrompt(next);
        notifications.show({ title: t('aiTemplateForm.tokenizeDone'), message: t('aiTemplateForm.tokenizeDoneMsg'), color: 'green' });
    };

    // ── Crop + rotate stage — a newly selected image opens it first ────────────
    // Shared by the image-path upload ('imagePath') and the prompt-path test
    // image ('testImage'); `cropTarget` routes the result to the right handler.
    const openCropStage = (f, target, initial = null) => {
        if (!f || !f.type.startsWith('image/')) {
            notifications.show({ title: t('aiTemplateForm.invalidFile'), message: t('aiTemplateForm.invalidFileMsg'), color: 'red' });
            return;
        }
        pendingCropFileRef.current = f;
        setCropTarget(target);
        setCropInitial(initial);
        const reader = new FileReader();
        reader.onload = (e) => { setCropName(f.name || 'image.jpg'); setCropSrc(e.target.result); };
        reader.readAsDataURL(f);
    };
    const openImageCrop = (f) => openCropStage(f, 'imagePath');
    const openTestCrop  = (f) => openCropStage(f, 'testImage');

    const routeCroppedFile = (file, meta) => {
        // Remember the ORIGINAL + the crop area (null = whole image) so the tile can re-open it later.
        const memo = { original: pendingCropFileRef.current, crop: meta?.crop || null };
        if (cropTarget === 'testImage') handleTestFileSelected(file, memo);
        else if (cropTarget === 'testPick') addTestPhoto(file, memo);
        else if (cropTarget === 'testReplace') replaceTestPhoto(recropRef.current?.previewUrl, file, memo);
        else commitImagePathFile(file);
    };
    // Several photos picked at once → they go through the crop stage one after another.
    const openNextQueuedCrop = () => {
        const next = cropQueueRef.current.shift();
        if (next) setTimeout(() => openCropStage(next, 'testPick'), 60);
    };
    const handleCropApply = (croppedFile, meta) => { setCropSrc(null); routeCroppedFile(croppedFile, meta); setCropTarget(null); openNextQueuedCrop(); };
    const handleCropSkip = () => { setCropSrc(null); if (pendingCropFileRef.current) routeCroppedFile(pendingCropFileRef.current); setCropTarget(null); openNextQueuedCrop(); };
    const handleCropError = () => { setCropSrc(null); if (pendingCropFileRef.current) routeCroppedFile(pendingCropFileRef.current); setCropTarget(null); openNextQueuedCrop(); };
    const handleCropClose = () => {
        setCropSrc(null);
        // Closing without cropping cancels a queued auto-generate on the test path (and any waiting photos).
        if (cropTarget === 'testImage') pendingGenerateRef.current = false;
        cropQueueRef.current = [];
        setCropTarget(null);
    };

    // ── Image path: (1) stage the cropped image (no auto-analyze) ───────────
    const commitImagePathFile = async (file) => {
        if (!file || !file.type.startsWith('image/')) {
            notifications.show({ title: t('aiTemplateForm.invalidFile'), message: t('aiTemplateForm.invalidFileMsg'), color: 'red' });
            return;
        }
        setOptimizingCount(c => c + 1);
        const optimized = await optimizeImage(file, { targetBytes: 490_000 }).catch(() => file);
        setOptimizingCount(c => c - 1);
        // The uploaded example becomes the template thumbnail directly.
        setThumbnailFile(optimized);
        if (thumbnailPreview?.startsWith('blob:')) URL.revokeObjectURL(thumbnailPreview);
        setThumbnailPreview(URL.createObjectURL(optimized));
        setPreviewIndex(0);
    };

    // ── Product mode: two dedicated dropzones ───────────────────────────────
    // The REFERENCE design image is stored as the thumbnail (it becomes the template
    // thumbnail and drives the design); the PRODUCT image is stored as the single
    // analysis extra. handleImagePathAnalyze then sends [reference, product] with
    // mode 'product' — the backend recreates the reference design around the product.
    const setReferenceImage = async (file) => {
        if (!file || !file.type.startsWith('image/')) {
            notifications.show({ title: t('aiTemplateForm.invalidFile'), message: t('aiTemplateForm.invalidFileMsg'), color: 'red' });
            return;
        }
        setOptimizingCount(c => c + 1);
        const optimized = await optimizeImage(file, { targetBytes: 490_000 }).catch(() => file);
        setOptimizingCount(c => c - 1);
        setThumbnailFile(optimized);
        if (thumbnailPreview?.startsWith('blob:')) URL.revokeObjectURL(thumbnailPreview);
        setThumbnailPreview(URL.createObjectURL(optimized));
    };
    const setProductImage = async (file) => {
        if (!file || !file.type.startsWith('image/')) {
            notifications.show({ title: t('aiTemplateForm.invalidFile'), message: t('aiTemplateForm.invalidFileMsg'), color: 'red' });
            return;
        }
        setOptimizingCount(c => c + 1);
        const optimized = await optimizeImage(file, { targetBytes: 490_000 }).catch(() => file);
        setOptimizingCount(c => c - 1);
        setAnalysisExtraPreviews(prev => { prev.forEach(u => u?.startsWith('blob:') && URL.revokeObjectURL(u)); return [URL.createObjectURL(optimized)]; });
        setAnalysisExtraFiles([optimized]);
    };

    // ── Extra example images (for richer AI analysis / pose merging) ────────
    const addAnalysisExtraFiles = async (fileList) => {
        const files = Array.from(fileList || []).filter(f => f?.type?.startsWith('image/'));
        if (!files.length) return;
        setOptimizingCount(c => c + 1);
        const optimized = await Promise.all(files.map(f => optimizeImage(f, { targetBytes: 490_000 }).catch(() => f)));
        setOptimizingCount(c => c - 1);
        setAnalysisExtraFiles(prev => [...prev, ...optimized].slice(0, 5)); // thumbnail + up to 5 extras = 6 total
        setAnalysisExtraPreviews(prev => [...prev, ...optimized.map(f => URL.createObjectURL(f))].slice(0, 5));
    };
    const removeAnalysisExtra = (idx) => {
        setAnalysisExtraPreviews(prev => { const u = prev[idx]; if (u?.startsWith('blob:')) URL.revokeObjectURL(u); return prev.filter((_, i) => i !== idx); });
        setAnalysisExtraFiles(prev => prev.filter((_, i) => i !== idx));
        setPreviewIndex(0); // fall back to the primary image after a removal
    };
    // First selected file becomes the thumbnail (cropped); the rest are extras.
    // Don't pre-filter the primary by MIME type — some files report an empty
    // type; openImageCrop() validates and shows an error if it isn't an image.
    const handleImagePathFiles = (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        const [first, ...rest] = files;
        openImageCrop(first);
        const extras = rest.filter(f => f?.type?.startsWith('image/'));
        if (extras.length) addAnalysisExtraFiles(extras);
    };

    // Admin picked which path to use the pasted image(s) for (from the paste chooser).
    const choosePasteDestination = (mode) => {
        const imgs = pastedImages;
        setPastedImages(null);
        if (!imgs || !imgs.length) return;
        setCreationMode(mode); // 'image', 'product' or 'product-model'
        handleImagePathFiles(imgs);
    };

    // Ctrl+V / Cmd+V: paste an image from the clipboard to start (or extend) the
    // image path — same behaviour as choosing a file. Text paste is left alone.
    useEffect(() => {
        if (isEdit) return;
        if (creationMode !== 'choose' && creationMode !== 'image' && creationMode !== 'product' && creationMode !== 'product-model') return;
        const onPaste = (e) => {
            const items = e.clipboardData?.items;
            if (!items) return;
            const imgs = [];
            for (const it of items) {
                if (it.kind === 'file' && it.type.startsWith('image/')) {
                    const f = it.getAsFile();
                    if (f) imgs.push(f);
                }
            }
            if (!imgs.length) return; // no image on the clipboard → don't hijack the paste
            e.preventDefault();
            if (creationMode === 'choose') {
                // Not committed to a path yet → ask which one to use the pasted image for.
                setPastedImages(imgs);
            } else if ((creationMode === 'image' || creationMode === 'product' || creationMode === 'product-model') && (thumbnailFile || thumbnailPreview)) {
                addAnalysisExtraFiles(imgs);
            } else {
                handleImagePathFiles(imgs);
            }
        };
        window.addEventListener('paste', onPaste);
        return () => window.removeEventListener('paste', onPaste);
    }, [isEdit, creationMode, thumbnailFile, thumbnailPreview]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Image path: (2) admin clicks Generate → AI builds the template ──────
    const handleImagePathAnalyze = async () => {
        const file = thumbnailFile;
        if (!file) return;
        const isProduct = creationMode === 'product';
        const isProductModel = creationMode === 'product-model';
        // Gender is auto-detected from the human image for product+model, so we don't
        // force a target audience — same as the product path.
        const analyzeMode = isProduct ? 'product' : isProductModel ? 'product-model' : '';
        setAnalyzingImage(true);
        try {
            // Send the thumbnail plus any extra example images so the AI can merge
            // what varies across them (e.g. different poses) into dynamic fields.
            // `mode: 'product'`       → product-ad template prompt.
            // `mode: 'product-model'` → combine a human model (male/female/child,
            //                           auto-detected) with the product.
            // Several photos are merged client-side into ONE grid image (same as
            // generation) so the vision call pays for a single image. `mergedCount`
            // tells the backend how many photos the grid holds so its prompt still
            // treats the panels as separate examples (and keeps the reference/product
            // roles in product mode). If merging fails, send them separately as before.
            const allFiles = [file, ...analysisExtraFiles];
            let filesToSend = allFiles;
            let mergedCount = 0;
            if (allFiles.length > 1) {
                try { filesToSend = [await mergeImagesToGrid(allFiles)]; mergedCount = allFiles.length; }
                catch { filesToSend = allFiles; mergedCount = 0; }
            }
            const result = await aiTemplateService.analyzeImage(filesToSend, knownCategories, analysisNote.trim(), analyzeMode, (isProduct || isProductModel) ? [] : genders, mergedCount, analysisFieldTypes);
            const data = result?.data || result;

            if (isProduct) {
                // Product templates always take exactly one product image.
                setRequireUserImage(true);
                setAllowMultipleImages(false);
                setImageLabels([{ label: 'upload_product_image', required: true }]);
            } else if (isProductModel) {
                // Product + model templates take two images: the model (person) and
                // the product, so the AI can place the product on/with the model.
                setRequireUserImage(true);
                setAllowMultipleImages(true);
                setImageLabels([
                    { label: 'upload_model_image', required: true },
                    { label: 'upload_product_image', required: true },
                ]);
            }

            if (data.nameEn) setNameEn(data.nameEn);
            if (data.nameBn) setNameBn(data.nameBn);
            // Format the generated prompt like the "টোকেন রূপান্তর" button does: one
            // slash-token per line, with dropdown/options field tokens neutralised so the
            // user's picked value drives the output (see tokensToLines / alignFieldTokens).
            if (data.prompt) {
                const promptStr = typeof data.prompt === 'string' ? data.prompt : JSON.stringify(data.prompt);
                const selfDescribingKeys = (Array.isArray(data.fields) ? data.fields : [])
                    .filter(f => f.type === 'dropdown' || f.type === 'options')
                    .map(f => f.key || toKey(f.label)).filter(Boolean);
                setPrompt(tokensToLines(alignFieldTokens(promptStr, selfDescribingKeys)));
            }
            // Negative prompt → slash tokens, one per line (matches the negative panel's
            // tokenizer); falls back to the raw text if nothing tokenised.
            if (data.negativePrompt) setNegativePrompt(toSlashTokenList(data.negativePrompt) || data.negativePrompt);
            if (data.imageSize && IMAGE_SIZES.some(s => s.value === data.imageSize)) setImageSize(data.imageSize);
            if (Array.isArray(data.tags) && data.tags.length) mergeTags(data.tags);

            if (data.categorySlug) {
                const matched = knownCategories.find(c => c.slug === data.categorySlug);
                if (matched) setCategoryId(String(matched.id));
            }

            if (Array.isArray(data.fields) && data.fields.length) {
                const built = data.fields.map(f => ({
                    id:            uid(),
                    label:         f.label || '',
                    labelBn:       f.labelBn || '',
                    key:           f.key || toKey(f.label),
                    type:          f.type || 'text',
                    placeholder:   f.placeholder || '',
                    placeholderBn: f.placeholderBn || '',
                    required:      !!f.required,
                    charLimit:     f.charLimit ?? ((f.type || 'text') === 'text' ? 50 : null),
                    minLength:     f.minLength ?? ((f.type || 'text') === 'text' ? 0 : null),
                    minValue:      f.minValue ?? null,
                    maxValue:      f.maxValue ?? null,
                    defaultColor:  f.defaultColor || '#000000',
                    choices:       Array.isArray(f.choices) ? f.choices : [],
                    suggestions:   Array.isArray(f.suggestions) ? f.suggestions : [],
                    dateAuto:      f.dateAuto !== false,
                    dateLang:      ['en', 'ar'].includes(f.dateLang) ? f.dateLang : 'bn',
                    _keyTouched:   true,
                }));
                setFields(built);
                // Tokenise each dropdown/options VALUE too (same as the tokenize button),
                // so option values come back as slash-tokens rather than prose. Awaited so
                // the values are tokenised before the "done" toast; failures keep originals.
                await tokenizeFieldValues(built);
            }

            if (!slug.trim() && data.nameEn) setSlug(slugify(data.nameEn));

            setWizardStep(2);
            notifications.show({ title: t('aiTemplateForm.templateReady'), message: t('aiTemplateForm.templateReadyMsg'), color: 'grape' });
        } catch (err) {
            const message = isAiCapacityError(err)
                ? t('aiTemplateForm.analysisUnavailable')
                : (err?.message || t('aiTemplateForm.couldNotAnalyze'));
            notifications.show({ title: t('aiTemplateForm.analysisFailed'), message, color: 'red' });
        } finally {
            setAnalyzingImage(false);
        }
    };

    if (loading) return (
        <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-zinc-200 border-t-primary rounded-full animate-spin" />
        </div>
    );

    const previewSrc = thumbnailPreview || existingThumbnail;
    const promptReady = prompt.trim().length > 0;

    // helpers for multi-test strip
    const removeTestExtraFile = (idx) => {
        setTestExtraPreviews(prev => { URL.revokeObjectURL(prev[idx]); return prev.filter((_, i) => i !== idx); });
        setTestExtraFiles(prev => prev.filter((_, i) => i !== idx));
        setTestResult(null);
    };
    const clearTestPrimary = () => {
        if (testPreview?.startsWith('blob:')) URL.revokeObjectURL(testPreview);
        setTestFile(null); setTestPreview(null); setTestResult(null);
    };

    // ── Shared sub-sections ────────────────────────────────────────────────

    // Insert a {tag} at the prompt textarea's cursor, keeping focus and caret.
    const insertPromptTag = (tag) => {
        const textarea = promptRef.current;
        if (!textarea) return;
        const start = textarea.selectionStart ?? prompt.length;
        const end = textarea.selectionEnd ?? prompt.length;
        const newVal = prompt.slice(0, start) + tag + prompt.slice(end);
        const nextCursor = start + tag.length;
        setPrompt(newVal);
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(nextCursor, nextCursor);
        }, 0);
    };

    // Date variables selected on auto-date fields — offered as prompt chips.
    const autoDateVariants = DATE_VARIANTS.filter(v =>
        fields.some(f => f.type === 'date' && f.dateAuto !== false && (f.dateVariants || ['date-en-bn']).includes(v.key)));

    const promptBody = (
        <>
            <div className="relative">
                <textarea
                    ref={promptRef}
                    placeholder={t('aiTemplateForm.promptPlaceholder')}
                    value={prompt}
                    onChange={e => setPrompt(e.target.value)}
                    rows={16}
                    className="w-full px-3 py-2 pb-7 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40 resize-y overflow-y-auto min-h-[280px] max-h-[85vh]"
                />
                {/* Live token estimate — pinned bottom-right, left of the resize grip */}
                <span
                    className="pointer-events-none absolute bottom-2 right-4 px-1.5 py-0.5 rounded-md bg-zinc-100/90 dark:bg-zinc-700/90 text-[11px] font-medium text-zinc-500 dark:text-zinc-300 backdrop-blur-sm"
                    title={t('aiTemplateForm.tokenCountHint') || 'Estimated prompt tokens'}
                >
                    {i18n.language === 'bn' ? toBnDigits(estimateTokens(prompt)) : estimateTokens(prompt)} {t('aiTemplateForm.tokens') || 'tokens'}
                </span>
            </div>
            <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{t('aiTemplateForm.promptHelper')}</p>
            {(fields.filter(f => f.key).length > 0 || autoDateVariants.length > 0) && (
                <div className="mt-2">
                    {/* Collapsible {key} placeholder chips */}
                    <button
                        type="button"
                        onClick={() => setKeyChipsOpen(o => !o)}
                        className="inline-flex items-center gap-1 text-xs text-zinc-500 dark:text-zinc-400 hover:text-primary transition-colors mb-1"
                    >
                        <IconChevronDown size={13} className={`transition-transform ${keyChipsOpen ? '' : '-rotate-90'}`} />
                        {t('aiTemplateForm.clickToInsert')}
                    </button>
                    {keyChipsOpen && (
                    <div className="flex flex-wrap gap-1">
                        {fields.filter(f => f.key).map(f => (
                            <button
                                key={f.id}
                                className="px-1.5 py-0.5 rounded text-xs font-mono bg-primary/10 text-primary dark:bg-primary/20 cursor-pointer hover:bg-primary/20 dark:hover:bg-primary/30 transition-colors"
                                onMouseDown={(e) => {
                                    // Prevent the button from stealing focus from the textarea.
                                    // Without this, blur fires on the textarea → selectionStart
                                    // resets to 0 and the textarea scrolls to the top.
                                    e.preventDefault();
                                }}
                                onClick={() => insertPromptTag(`{${f.key}}`)}
                            >
                                {`{${f.key}}`}
                            </button>
                        ))}
                        {/* Auto-date variables — amber to set them apart from field keys;
                            tooltip shows what today's date renders as in that format */}
                        {autoDateVariants.map(v => (
                            <button
                                key={v.key}
                                title={v.format(todayISO())}
                                className="px-1.5 py-0.5 rounded text-xs font-mono bg-amber-500/10 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400 cursor-pointer hover:bg-amber-500/20 dark:hover:bg-amber-500/30 transition-colors"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => insertPromptTag(`{${v.key}}`)}
                            >
                                {`{${v.key}}`}
                            </button>
                        ))}
                    </div>
                    )}
                </div>
            )}
        </>
    );

    // Header action for the Prompt section — opens the AI enhance-prompt modal.
    const promptAiBtn = (
        <button
            onClick={() => setAddFieldModalOpen(true)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold bg-primary/10 hover:bg-primary/20 text-primary dark:bg-primary/20 dark:hover:bg-primary/30 rounded-lg transition-colors"
        >
            <IconSparkles size={13} /> {t('aiTemplateForm.enhancePromptBtn')}
        </button>
    );

    // Migrate an existing detailed prose prompt into the new short token form.
    // Disabled until the box has content; keeps every {key} field intact.
    const promptTokenizeBtn = (
        <button
            onClick={handleTokenizePrompt}
            disabled={!prompt.trim() || tokenizingPrompt}
            title={t('aiTemplateForm.tokenizeTooltip')}
            className="inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 text-xs font-semibold bg-violet-100 hover:bg-violet-200 text-violet-700 dark:bg-violet-500/20 dark:hover:bg-violet-500/30 dark:text-violet-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
            {tokenizingPrompt
                ? <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>
                : <IconWand size={13} />}
            {t('aiTemplateForm.tokenizeBtn')}
        </button>
    );

    const renderPromptCard = () => (
        <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-4 sm:p-5">
            <div className="mb-3">
                <span className="font-semibold text-zinc-900 dark:text-zinc-100">{t('aiTemplateForm.aiPrompt')}</span>
            </div>
            {promptBody}
        </div>
    );

    // Convert the negative "avoid" list into slash tokens, one per line (local only).
    const negativeTokenizeBtn = (
        <button
            onClick={handleTokenizeNegative}
            disabled={!negativePrompt.trim()}
            title={t('aiTemplateForm.tokenizeTooltip')}
            className="inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 text-xs font-semibold bg-violet-100 hover:bg-violet-200 text-violet-700 dark:bg-violet-500/20 dark:hover:bg-violet-500/30 dark:text-violet-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
            <IconWand size={13} />
            {t('aiTemplateForm.tokenizeBtn')}
        </button>
    );

    const negativeClearBtn = negativePrompt.trim() ? (
        <button
            onClick={() => setNegativePrompt('')}
            className="text-xs text-zinc-400 hover:text-red-500 transition-colors"
        >
            {t('aiTemplateForm.clear')}
        </button>
    ) : null;

    const negativeBody = (
        <>
            <textarea
                placeholder={t('aiTemplateForm.negativePlaceholder')}
                value={negativePrompt}
                onChange={e => setNegativePrompt(e.target.value)}
                rows={5}
                className="w-full px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none min-h-[100px]"
            />
        </>
    );

    const NegativePromptCard = (
        <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-4 sm:p-5">
            <div className="flex justify-between items-start gap-2 mb-2">
                <div>
                    <p className="font-semibold text-sm text-zinc-900 dark:text-zinc-100">{t('aiTemplateForm.negativePrompt')}</p>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">{t('aiTemplateForm.negativeHelper')}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">{negativeTokenizeBtn}{negativeClearBtn}</div>
            </div>
            {negativeBody}
        </div>
    );

    // Per-section "Analyze" button — disabled until the prompt has content.
    const analyzeSectionBtn = (onClick, busy) => (
        <button
            onClick={onClick}
            disabled={!prompt.trim() || busy}
            title={!prompt.trim() ? t('aiTemplateForm.writePromptFirstTooltip') : t('aiTemplateForm.fillFromPromptTooltip')}
            className="inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 text-xs font-medium bg-primary hover:bg-primary/90 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
        >
            {busy
                ? <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> {t('aiTemplateForm.analyzing')}</>
                : <><IconSparkles size={13} /> {t('aiTemplateForm.analyze')}</>}
        </button>
    );

    const addFieldBtn = (
        <button
            onClick={addField}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-primary/10 hover:bg-primary/20 text-primary dark:bg-primary/20 dark:hover:bg-primary/30 rounded-lg transition-colors flex-shrink-0"
        >
            <IconPlus size={13} /> {t('aiTemplateForm.addField')}
        </button>
    );

    // EN/BN switcher — decides which language every field-LABEL input edits.
    const labelLangSwitcher = (
        <div
            className="inline-flex items-center rounded-lg border border-zinc-200 dark:border-zinc-700 overflow-hidden flex-shrink-0"
            title={t('aiTemplateForm.labelLangHint')}
        >
            {['bn', 'en'].map(lng => (
                <button
                    key={lng}
                    type="button"
                    onClick={() => setLabelLang(lng)}
                    className={`px-2 py-1 text-xs font-semibold transition-colors ${labelLang === lng ? 'bg-primary text-white' : 'bg-transparent text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800'}`}
                >
                    {lng === 'bn' ? t('aiTemplateForm.langBn') : t('aiTemplateForm.langEn')}
                </button>
            ))}
        </div>
    );

    // Quick status toggle in the page header — draft ⟷ publish (active) only;
    // archiving stays in the Basic Info status select. Same `status` state as
    // that select (both stay in sync); saving still goes through Update.
    const headerStatusSwitcher = (
        <div
            className="inline-flex items-center rounded-lg border border-zinc-200 dark:border-zinc-700 overflow-hidden flex-shrink-0"
            title={t('aiTemplateForm.status')}
        >
            <button
                type="button"
                onClick={() => setStatus('draft')}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold transition-colors ${status === 'draft'
                    ? 'bg-orange-400 text-white'
                    : 'bg-transparent text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800'}`}
            >
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${status === 'draft' ? 'bg-white' : 'bg-orange-400'}`} />
                {t('aiTemplateForm.statusDraft')}
            </button>
            <button
                type="button"
                onClick={() => setStatus('active')}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold transition-colors ${status === 'active'
                    ? 'bg-emerald-500 text-white'
                    : 'bg-transparent text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800'}`}
            >
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${status === 'active' ? 'bg-white' : 'bg-emerald-500'}`} />
                {t('aiTemplateForm.statusPublish')}
            </button>
        </div>
    );

    const activeField = fields.find(f => f.id === activeFieldId) || fields[0] || null;
    const dynamicFieldsBody = (
        <div className="grid grid-cols-1 md:grid-cols-[240px_minmax(0,1fr)] gap-3 items-start">
            {/* Field list (master) */}
            <div className="min-w-0">
                <p className="text-xs font-semibold text-zinc-600 dark:text-zinc-300 mb-2">{t('aiTemplateForm.fieldsListCount', { count: fields.length })}</p>
                {fields.length === 0 && (
                    <p className="text-xs text-zinc-500 dark:text-zinc-400 text-center py-3">
                        {t('aiTemplateForm.noFieldsHint')}
                    </p>
                )}
                <DndContext sensors={fieldSensors} collisionDetection={closestCenter} onDragEnd={handleFieldDragEnd}>
                    <SortableContext items={fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
                        <div className="flex flex-col gap-1.5">
                            {fields.map(field => (
                                <SortableFieldListItem
                                    key={field.id}
                                    field={field}
                                    selected={activeField?.id === field.id}
                                    onSelect={() => setActiveFieldId(field.id)}
                                    onDelete={() => removeField(field.id)}
                                    labelLang={labelLang}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
                {/* Quick-add preset field types */}
                <div className="mt-3 pt-3 border-t border-dashed border-zinc-200 dark:border-zinc-700">
                    <span className="block text-xs text-zinc-500 dark:text-zinc-400 mb-1.5">{t('aiTemplateForm.quickAdd')}</span>
                    <div className="flex flex-wrap gap-1.5">
                        {FIELD_PRESETS.map(p => (
                            <button
                                key={p.id}
                                onClick={() => addPresetField(p)}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border border-primary/30 text-primary hover:bg-primary/10 dark:hover:bg-primary/20 transition-colors"
                            >
                                <IconPlus size={11} /> {p.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            {/* Field editor (detail). Wrapped in its own inert Dnd context purely so the
                card's useSortable hook has a provider — dragging happens in the list. */}
            <div className="min-w-0">
                {activeField ? (
                    <DndContext collisionDetection={closestCenter} onDragEnd={() => {}}>
                        <SortableContext items={[activeField.id]} strategy={verticalListSortingStrategy}>
                            <SortableFieldCard
                                key={activeField.id}
                                field={activeField}
                                idx={fields.findIndex(f => f.id === activeField.id)}
                                copiedKey={copiedKey}
                                setCopiedKey={setCopiedKey}
                                updateField={updateField}
                                removeField={removeField}
                                onApplyJson={applyFieldJson}
                                prompt={prompt}
                                onSync={handleSyncField}
                                syncing={syncingFieldId === activeField.id}
                                onSuggestChoice={handleSuggestChoice}
                                labelLang={labelLang}
                            />
                        </SortableContext>
                    </DndContext>
                ) : (
                    <div className="min-h-[120px] flex items-center justify-center text-xs text-zinc-400 dark:text-zinc-500 border border-dashed border-zinc-200 dark:border-zinc-700 rounded-lg">
                        {t('aiTemplateForm.noFieldsHint')}
                    </div>
                )}
            </div>
        </div>
    );

    // ── Test-panel field renderer — renders each field by its type ──────────
    const renderTestField = (field) => {
        const val = testFieldValues[field.key] || '';
        const set = (v) => setTestFieldValues(prev => ({ ...prev, [field.key]: v }));
        // In the 2-column field grid, colour pickers and long textareas take a full row;
        // all other inputs sit two-per-row.
        const spanCls = `group ${(field.type === 'colorpicker' || field.type === 'textarea') ? 'col-span-2' : ''}`;
        const labelEl = (
            <div className="flex items-center justify-between gap-1 mb-0.5">
                <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 truncate">
                    {((labelLang === 'bn' && field.labelBn) ? field.labelBn : field.label) || field.key}
                </label>
                <button
                    type="button"
                    onClick={() => removeField(field.id)}
                    className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 p-0.5 rounded text-zinc-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-opacity flex-shrink-0"
                    title={t('aiTemplateForm.deleteFieldTooltip')}
                >
                    <IconTrash size={12} />
                </button>
            </div>
        );
        if (field.type === 'colorpicker') {
            return (
                <div key={field.id} className={spanCls}>
                    {labelEl}
                    <ColorSwatchPicker
                        size="sm"
                        value={val}
                        defaultColor={field.defaultColor}
                        swatches={(field.choices || []).map(c => c.value).filter(Boolean)}
                        onChange={set}
                    />
                </div>
            );
        }
        if (field.type === 'dropdown') {
            return (
                <div key={field.id} className={spanCls}>
                    {labelEl}
                    <Select value={val || undefined} onValueChange={v => set(v)}>
                        <SelectTrigger className="w-full h-9 text-xs">
                            <SelectValue placeholder={t('aiTemplateForm.selectOption')} />
                        </SelectTrigger>
                        <SelectContent>
                            {(field.choices || []).filter(c => c.value).map((c, i) => (
                                <SelectItem key={i} value={c.value}>{((labelLang === 'bn' && c.labelBn) ? c.labelBn : c.label) || c.value}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            );
        }
        if (field.type === 'options') {
            return (
                <div key={field.id} className={spanCls}>
                    {labelEl}
                    <div className="flex flex-wrap gap-1.5">
                        {(field.choices || []).map((c, i) => (
                            <button
                                key={i}
                                type="button"
                                onClick={() => set(c.value)}
                                className={`px-2 py-1 rounded-md text-xs font-medium border transition-colors ${val === c.value
                                    ? 'bg-primary border-primary text-white'
                                    : 'border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:border-primary/50'}`}
                            >
                                {((labelLang === 'bn' && c.labelBn) ? c.labelBn : c.label) || c.value}
                            </button>
                        ))}
                    </div>
                </div>
            );
        }
        // Quick-fill chips from the admin-defined suggestion list (text/textarea).
        const suggestionChips = (field.suggestions || []).filter(s => (s || '').trim()).length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
                {(field.suggestions || []).filter(s => (s || '').trim()).map((s, i) => (
                    <button
                        key={i}
                        type="button"
                        onClick={() => set(s)}
                        className={`px-2 py-0.5 rounded-md text-[11px] font-medium border transition-colors ${val === s
                            ? 'bg-primary border-primary text-white'
                            : 'border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:border-primary/50'}`}
                    >
                        {s}
                    </button>
                ))}
            </div>
        );
        if (field.type === 'date') {
            return (
                <div key={field.id} className={spanCls}>
                    {labelEl}
                    {field.dateAuto !== false ? (
                        <div className="flex items-center gap-1.5 px-2 py-1.5 border border-zinc-200 dark:border-zinc-700 rounded-lg text-xs bg-zinc-50 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300">
                            <IconCalendar size={13} className="text-primary flex-shrink-0" />
                            {formatPromptDate(todayISO(), field.dateLang)}
                        </div>
                    ) : (
                        <input type="date" className={inputXsCls} value={val || todayISO()} onChange={e => set(e.target.value)} />
                    )}
                </div>
            );
        }
        if (field.type === 'textarea') {
            return (
                <div key={field.id} className={spanCls}>
                    {labelEl}
                    <textarea className={inputXsCls + ' resize-none'} rows={2} placeholder={field.placeholder || ''} value={val} onChange={e => set(e.target.value)} />
                    {suggestionChips}
                </div>
            );
        }
        if (field.type === 'number') {
            return (
                <div key={field.id} className={spanCls}>
                    {labelEl}
                    <input type="number" className={inputXsCls} placeholder={field.placeholder || ''} value={val} onChange={e => set(e.target.value)} />
                </div>
            );
        }
        return (
            <div key={field.id} className={spanCls}>
                {labelEl}
                <input
                    type="text"
                    placeholder={field.placeholder || t('aiTemplateForm.enterField', { field: field.label || field.key })}
                    className={inputXsCls}
                    value={val}
                    onChange={e => set(e.target.value)}
                    autoComplete={field.key}
                    name={field.key}
                />
                {suggestionChips}
            </div>
        );
    };

    // ── Multi-image upload strip (shared) ──────────────────────────────────
    // ── Reference model picker — built-in sample photos to test-generate with ──
    const ReferenceModelPicker = (
        <div className="mb-3">
            <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1.5">
                {t(allowMultipleImages ? 'aiTemplateForm.pickReferenceModels' : 'aiTemplateForm.pickReferenceModel')}
            </p>
            <div className="grid grid-cols-4 gap-2">
                {REFERENCE_MODELS.map(m => {
                    const selected = isModelSelected(m.key);
                    return (
                        <button
                            key={m.key}
                            type="button"
                            onClick={() => {
                                // Already in the test set → re-open its crop (full original + last selection).
                                const pv = previewOfModel(m.key);
                                if (pv) reCropPhoto(pv);
                                else if (allowMultipleImages) handleToggleReferenceModelMulti(m);
                                else handlePickReferenceModel(m);
                            }}
                            disabled={!!pickingModel}
                            title={selected ? t('aiTemplateForm.recropHint') : m.label}
                            className={`relative w-full aspect-[3/4] rounded-lg overflow-hidden border-2 transition-colors disabled:opacity-60 ${selected ? 'border-primary ring-2 ring-primary/40' : 'border-zinc-200 dark:border-zinc-700 hover:border-primary'}`}
                        >
                            {/* Selected → show the CROPPED photo here (temporary); the original returns when deselected. */}
                            <img src={(selected && previewOfModel(m.key)) || m.url} alt={m.label} loading={selected ? undefined : 'lazy'} className="w-full h-full object-cover" />
                            {selected && (
                                <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center shadow">
                                    <IconCheck size={12} />
                                </span>
                            )}
                            {selected && (
                                <span
                                    role="button"
                                    tabIndex={0}
                                    title={t('common.delete') || 'Delete'}
                                    onClick={(e) => { e.stopPropagation(); if (allowMultipleImages) handleToggleReferenceModelMulti(m); else clearTestPrimary(); }}
                                    className="absolute top-1 left-1 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow"
                                >
                                    <IconX size={12} />
                                </span>
                            )}
                            {pickingModel === m.key && (
                                <span className="absolute inset-0 flex items-center justify-center bg-black/40">
                                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                </span>
                            )}
                        </button>
                    );
                })}
                {/* Custom-uploaded test photos (not reference models) — shown as tiles so the
                    admin can see and remove what they uploaded. */}
                {(() => {
                    const isRef = (f) => REFERENCE_MODELS.some(m => (f?.name || '').startsWith(`${m.key}.`));
                    const customs = [];
                    if (testFile && !isRef(testFile)) customs.push({ url: testPreview, remove: clearTestPrimary });
                    testExtraFiles.forEach((f, i) => { if (!isRef(f)) customs.push({ url: testExtraPreviews[i], remove: () => removeTestExtraFile(i) }); });
                    return customs.map((c, i) => (
                        <div
                            key={`custom-${i}`}
                            onClick={() => reCropPhoto(c.url)}
                            title={t('aiTemplateForm.recropHint')}
                            className="relative w-full aspect-[3/4] rounded-lg overflow-hidden border-2 border-primary ring-2 ring-primary/40 cursor-pointer"
                        >
                            {c.url && <img src={c.url} alt="" className="w-full h-full object-cover" />}
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); c.remove(); }}
                                title={t('common.delete') || 'Delete'}
                                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow"
                            >
                                <IconX size={12} />
                            </button>
                        </div>
                    ));
                })()}
                {/* Custom upload tile — always the last option */}
                <button
                    type="button"
                    onClick={() => (allowMultipleImages ? addMoreTestInputRef : testFileInputRef).current?.click()}
                    disabled={!!pickingModel}
                    title={t('aiTemplateForm.uploadTestPhoto')}
                    className="w-full aspect-[3/4] rounded-lg border-2 border-dashed border-primary/40 bg-orange-50 dark:bg-orange-900/20 flex flex-col items-center justify-center gap-1 text-primary hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors disabled:opacity-60"
                >
                    <IconUpload size={20} />
                    <span className="text-[10px] font-semibold">{t('aiTemplateForm.photoOne')}</span>
                </button>
            </div>
        </div>
    );

    const MultiImageStrip = (
        <div className="mb-4 p-2 rounded-lg bg-orange-50/60 dark:bg-orange-900/10 border border-primary/30">
            {/* Offer built-in sample photos until the primary photo is chosen */}
            {!testFile && ReferenceModelPicker}
            <p className="text-xs font-semibold text-primary mb-1.5">
                {t('aiTemplateForm.uploadTestPhotos')}
                {(testFile ? 1 : 0) + testExtraFiles.length > 0
                    ? t('aiTemplateForm.photoCountSuffix', { count: (testFile ? 1 : 0) + testExtraFiles.length })
                    : ''}
            </p>
            <div className="flex gap-1.5 flex-nowrap overflow-x-auto">
                {/* Primary slot */}
                {testFile ? (
                    <div className="relative flex-shrink-0">
                        <img src={testPreview} alt={t('aiTemplateForm.primaryPhotoAlt')} title={t('aiTemplateForm.recropHint')} onClick={() => reCropPhoto(testPreview)} className="w-[52px] h-[52px] rounded object-cover border-2 border-primary cursor-pointer" />
                        <button
                            className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-red-600 text-white flex items-center justify-center z-[2]"
                            onClick={clearTestPrimary}
                        >
                            <IconTrash size={8} />
                        </button>
                    </div>
                ) : optimizingCount > 0 ? (
                    <div className="w-[52px] h-[52px] flex-shrink-0 rounded border-2 border-dashed border-primary/40 bg-orange-50 dark:bg-orange-900/20 flex flex-col items-center justify-center gap-1">
                        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        <span className="text-[9px] text-primary font-medium">...</span>
                    </div>
                ) : (
                    <button
                        onClick={() => (allowMultipleImages ? addMoreTestInputRef : testFileInputRef).current?.click()}
                        className="w-[52px] h-[52px] flex-shrink-0 rounded border-2 border-dashed border-primary/40 bg-orange-50 dark:bg-orange-900/20 flex flex-col items-center justify-center gap-0.5 cursor-pointer hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors"
                    >
                        <IconUpload size={14} className="text-primary" />
                        <span className="text-[9px] text-primary font-semibold">{t('aiTemplateForm.photoOne')}</span>
                    </button>
                )}
                {/* Extra slots */}
                {testExtraPreviews.map((url, idx) => (
                    <div key={idx} className="relative flex-shrink-0">
                        <img src={url} alt={t('aiTemplateForm.photoNAlt', { n: idx + 2 })} title={t('aiTemplateForm.recropHint')} onClick={() => reCropPhoto(url)} className="w-[52px] h-[52px] rounded object-cover border-2 border-zinc-200 dark:border-zinc-700 cursor-pointer" />
                        <button
                            className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-red-600 text-white flex items-center justify-center z-[2]"
                            onClick={() => removeTestExtraFile(idx)}
                        >
                            <IconTrash size={8} />
                        </button>
                    </div>
                ))}
                {/* Optimizing loader slots */}
                {optimizingCount > 0 && Array.from({ length: optimizingCount }).map((_, i) => (
                    <div key={`opt-${i}`} className="w-[52px] h-[52px] flex-shrink-0 rounded border-2 border-dashed border-primary/40 bg-orange-50 dark:bg-orange-900/20 flex flex-col items-center justify-center gap-1">
                        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        <span className="text-[9px] text-primary font-medium">...</span>
                    </div>
                ))}
                {/* Add-more slot */}
                {testFile && optimizingCount === 0 && (
                    <button
                        onClick={() => addMoreTestInputRef.current?.click()}
                        className="w-[52px] h-[52px] flex-shrink-0 rounded border-2 border-dashed border-zinc-200 dark:border-zinc-700 flex flex-col items-center justify-center gap-0.5 cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                    >
                        <IconPlus size={16} className="text-zinc-400" />
                        <span className="text-[9px] text-zinc-400 font-medium">{t('aiTemplateForm.more')}</span>
                    </button>
                )}
            </div>
        </div>
    );

    // ── Generating shimmer ─────────────────────────────────────────────────
    const GeneratingShimmer = (
        <div className="mb-4 relative rounded overflow-hidden bg-zinc-900">
            {testPreview ? (
                <img
                    src={testPreview}
                    alt=""
                    className="atg-gen-photo block w-full h-auto"
                />
            ) : (
                <div className="min-h-[200px]" />
            )}
            <div className="atg-gen-glow absolute inset-0" />
            <div className="atg-gen-shimmer" />
            <div className="absolute bottom-0 left-0 right-0 px-4 pb-3.5 pt-6 flex flex-col items-center gap-1.5 bg-gradient-to-t from-black/80 to-transparent">
                <div className="flex items-center gap-1.5">
                    <div className="flex gap-0.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce [animation-delay:0ms]" />
                        <span className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce [animation-delay:150ms]" />
                        <span className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce [animation-delay:300ms]" />
                    </div>
                    <span className="font-bold text-white text-sm [text-shadow:_0_1px_8px_rgba(0,0,0,0.9)]">
                        {t('aiTemplateForm.generating')}
                    </span>
                </div>
            </div>
        </div>
    );

    // ── Gender / audience picker (multi-select chips) ──────────────────────
    const toggleGender = (value) =>
        setGenders(prev => prev.includes(value) ? prev.filter(g => g !== value) : [...prev, value]);
    // Bare gender chips — shared between the create-flow picker and Basic Info (edit)
    const GenderChips = (
        <div className="flex flex-wrap gap-1.5">
            {GENDER_OPTIONS.map(opt => {
                const active = genders.includes(opt.value);
                return (
                    <button
                        key={opt.value}
                        type="button"
                        onClick={() => toggleGender(opt.value)}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                            active
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                        }`}
                    >
                        {t(opt.i18n)}
                    </button>
                );
            })}
        </div>
    );
    const GenderPicker = (
        <div className="mb-4">
            <label className="block text-xs font-medium mb-1.5 text-zinc-700 dark:text-zinc-300">{t('aiTemplateForm.genderLabel')}</label>
            {GenderChips}
            <p className="text-[11px] text-zinc-400 dark:text-zinc-500 mt-1">{t('aiTemplateForm.genderHint')}</p>

            {/* Field kinds the AI analyzer should turn into dynamic fields */}
            <label className="block text-xs font-medium mb-1.5 mt-3 text-zinc-700 dark:text-zinc-300">{t('aiTemplateForm.fieldTypesLabel')}</label>
            <div className="flex flex-wrap gap-1.5">
                {ANALYZE_FIELD_TYPE_OPTIONS.map(opt => {
                    const active = analysisFieldTypes.includes(opt.value);
                    return (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => setAnalysisFieldTypes(prev => prev.includes(opt.value) ? prev.filter(v => v !== opt.value) : [...prev, opt.value])}
                            className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                                active
                                    ? 'border-primary bg-primary/10 text-primary'
                                    : 'border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                            }`}
                        >
                            {t(opt.i18n)}
                        </button>
                    );
                })}
            </div>
            <p className="text-[11px] text-zinc-400 dark:text-zinc-500 mt-1">{t('aiTemplateForm.fieldTypesHint')}</p>
        </div>
    );

    // ── Toggle pill (User image + multiple images) ─────────────────────────
    const TogglePill = (
        <div className="flex items-center gap-4 mb-4 p-2.5 rounded-lg bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700">
            <label className="flex items-center gap-2 cursor-pointer select-none">
                <span className="relative inline-block w-8 h-4">
                    <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={requireUserImage}
                        onChange={e => {
                            setRequireUserImage(e.currentTarget.checked);
                            if (!e.currentTarget.checked) setAllowMultipleImages(false);
                        }}
                    />
                    <span className="absolute inset-0 rounded-full bg-zinc-300 dark:bg-zinc-600 peer-checked:bg-primary transition-colors" />
                    <span className="absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white transition-transform peer-checked:translate-x-4" />
                </span>
                <span className="text-xs text-zinc-700 dark:text-zinc-300">{t('aiTemplateForm.userImage')}</span>
            </label>
            {requireUserImage && (
                <>
                    <div className="w-px self-stretch bg-zinc-200 dark:border-zinc-700" />
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                        <span className="relative inline-block w-8 h-4">
                            <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={allowMultipleImages}
                                onChange={e => {
                                    const on = e.currentTarget.checked;
                                    setAllowMultipleImages(on);
                                    if (on) {
                                        // Seed at least two labeled inputs so the admin can define them.
                                        setImageLabels(prev => prev.length >= 2 ? prev : [
                                            prev[0] || { label: '', required: true },
                                            { label: '', required: true },
                                        ]);
                                    } else {
                                        setTestExtraPreviews(prev => { prev.forEach(u => URL.revokeObjectURL(u)); return []; });
                                        setTestExtraFiles([]);
                                        setRequiredImageCount(1);
                                        setTestResult(null);
                                    }
                                }}
                            />
                            <span className="absolute inset-0 rounded-full bg-zinc-300 dark:bg-zinc-600 peer-checked:bg-primary transition-colors" />
                            <span className="absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white transition-transform peer-checked:translate-x-4" />
                        </span>
                        <span className="text-xs text-zinc-700 dark:text-zinc-300">{t('aiTemplateForm.multipleImages')}</span>
                    </label>
                </>
            )}
        </div>
    );

    return (
        <div className="p-3 sm:p-6">
            {/* Hidden file inputs */}
            <input
                ref={thumbnailInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) handleThumbnailChange(f);
                    e.target.value = '';
                }}
            />
            <input
                ref={testFileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (f) openTestCrop(f);
                }}
            />
            <input
                ref={addMoreTestInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                    // Snapshot BEFORE clearing the input (clearing empties the live FileList).
                    const raw = Array.from(e.target.files || []).filter(f => f.type.startsWith('image/'));
                    e.target.value = '';
                    if (!raw.length) return;
                    // Every picked photo opens the crop stage (crop, or skip = whole image), one after another.
                    const [first, ...rest] = raw;
                    cropQueueRef.current = rest;
                    openCropStage(first, 'testPick');
                }}
            />

            {/* Resolve-mode banner — arrived from the Negative Feedback page to fix a report. */}
            {resolveFeedbackId && (
                <div className="mb-5 rounded-xl border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-4">
                    <div className="flex items-start gap-3">
                        {resolveCtx?.aiImagePath && (
                            <img src={resolveCtx.aiImagePath} alt="" className="w-16 h-16 rounded-lg object-cover border border-amber-200 dark:border-amber-800 flex-shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[13px] font-bold text-amber-800 dark:text-amber-300">{t('aiTemplateFeedback.resolveBanner')}</span>
                                {resolveDone
                                    ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-500/15 text-emerald-700 dark:text-emerald-400">{t('aiTemplateFeedback.statusSolved')}</span>
                                    : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-500/15 text-amber-700 dark:text-amber-400">{t('aiTemplateFeedback.statusPending')}</span>}
                            </div>
                            {resolveCtx?.reason && <p className="mt-1 text-[13px] font-semibold text-amber-900 dark:text-amber-200">{resolveCtx.reason}</p>}
                            {resolveCtx?.note && <p className="mt-0.5 text-[12.5px] text-amber-800/90 dark:text-amber-300/80 whitespace-pre-wrap break-words">“{resolveCtx.note}”</p>}
                            <p className="mt-1.5 text-[12px] text-amber-700/80 dark:text-amber-400/70">{t('aiTemplateFeedback.resolveBannerHint')}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Header — title (left), inline stepper (page-centered), save (right).
                Equal flex-1 spacers on both sides keep the stepper centered on the
                page regardless of the title / save-button widths. */}
            <div className="flex items-center gap-4 mb-5">
                <div className="flex items-start gap-2 flex-1 min-w-0">
                    <button
                        onClick={() => attemptLeave('/admin/ai-templates')}
                        className="mt-1 p-1.5 rounded-lg text-zinc-500 dark:text-zinc-400 hover:text-primary hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors flex-shrink-0"
                    >
                        <IconArrowLeft size={20} />
                    </button>
                    <PageHeader
                        title={isEdit ? ((i18n.language === 'bn' && nameBn) ? nameBn : (nameEn || nameBn || t('aiTemplateForm.editTitle'))) : t('aiTemplateForm.newTitle')}
                        subtitle={isEdit
                            ? `${t('aiTemplateForm.editTitle')}${updatedAt ? ` · ${t('aiTemplateForm.lastUpdated')} ${new Date(updatedAt).toLocaleString(i18n.language === 'bn' ? 'bn-BD' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}`
                            : ''}
                    />
                </div>

                {/* Wizard step indicator — create only, once a path is chosen.
                    Bulk mode has its own progress UI, so no stepper there. */}
                {!isEdit && creationMode !== 'choose' && creationMode !== 'bulk' && (
                    <div className="hidden md:flex justify-center flex-shrink-0">
                        {(creationMode === 'image' || creationMode === 'product' || creationMode === 'product-model')
                            ? <WizardStepper
                                inline
                                active={wizardStep === 0 ? 0 : 1}
                                steps={[
                                    { label: t('aiTemplateForm.stepUploadImage'), desc: t('aiTemplateForm.stepUploadImageDesc') },
                                    { label: t('aiTemplateForm.stepTemplateDetails'), desc: t('aiTemplateForm.stepReviewSave') },
                                ]}
                            />
                            : <WizardStepper
                                inline
                                active={wizardStep}
                                steps={[
                                    { label: t('aiTemplateForm.stepWritePrompt'), desc: t('aiTemplateForm.stepWritePromptDesc') },
                                    { label: t('aiTemplateForm.stepReviewResult'), desc: t('aiTemplateForm.stepReviewResultDesc') },
                                    { label: t('aiTemplateForm.stepTemplateDetails'), desc: t('aiTemplateForm.stepTemplateDetailsAiDesc') },
                                ]}
                            />}
                    </div>
                )}

                <div className="flex-1 flex justify-end items-center gap-2">
                    {(isEdit || wizardStep === 2) && (
                        <>
                            {/* Quick status change — mirrors the Basic Info status select */}
                            {headerStatusSwitcher}
                            {/* Global label language — controls which language's labels are
                                edited across all sections (dynamic fields, image input). */}
                            {labelLangSwitcher}
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="inline-flex items-center gap-1.5 px-4 py-2.5 text-[13px] font-semibold bg-primary hover:bg-primary/90 text-white rounded-lg disabled:opacity-60 transition-colors flex-shrink-0"
                            >
                                {saving
                                    ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                                    : <IconDeviceFloppy size={16} />}
                                {isEdit ? t('aiTemplateForm.updateTemplate') : t('aiTemplateForm.createTemplate')}
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* ── Choose path (create only) ── */}
            {!isEdit && creationMode === 'choose' && (
                <div className="max-w-6xl mx-auto">
                    <p className="text-center text-zinc-500 dark:text-zinc-400 text-sm mb-6">
                        {t('aiTemplateForm.howStart')}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* Path A: upload an image */}
                        <button
                            onClick={() => chooseImageInputRef.current?.click()}
                            className="group text-left bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-5 hover:border-primary hover:shadow-sm transition-all"
                        >
                            <div className="w-11 h-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3 group-hover:bg-primary group-hover:text-white transition-colors">
                                <IconPhoto size={22} />
                            </div>
                            <p className="font-semibold text-zinc-900 dark:text-zinc-100 mb-1">{t('aiTemplateForm.uploadImage')}</p>
                            <p className="text-sm text-zinc-500 dark:text-zinc-400">
                                {t('aiTemplateForm.uploadImageDesc')}
                            </p>
                            <span className="inline-flex items-center gap-1 mt-3 text-xs font-medium text-primary">
                                <IconWand size={13} /> {t('aiTemplateForm.aiFillsEverything')}
                            </span>
                        </button>

                        {/* Path B: write a prompt */}
                        <button
                            onClick={() => setCreationMode('prompt')}
                            className="group text-left bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-5 hover:border-primary hover:shadow-sm transition-all"
                        >
                            <div className="w-11 h-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3 group-hover:bg-primary group-hover:text-white transition-colors">
                                <IconSparkles size={22} />
                            </div>
                            <p className="font-semibold text-zinc-900 dark:text-zinc-100 mb-1">{t('aiTemplateForm.writePrompt')}</p>
                            <p className="text-sm text-zinc-500 dark:text-zinc-400">
                                {t('aiTemplateForm.writePromptDesc')}
                            </p>
                            <span className="inline-flex items-center gap-1 mt-3 text-xs font-medium text-primary">
                                <IconSparkles size={13} /> {t('aiTemplateForm.generateFromPrompt')}
                            </span>
                        </button>

                        {/* Path C: product image → product-ad template */}
                        <button
                            onClick={() => setCreationMode('product')}
                            className="group text-left bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-5 hover:border-primary hover:shadow-sm transition-all"
                        >
                            <div className="w-11 h-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3 group-hover:bg-primary group-hover:text-white transition-colors">
                                <IconShoppingBag size={22} />
                            </div>
                            <p className="font-semibold text-zinc-900 dark:text-zinc-100 mb-1">{t('aiTemplateForm.productImage')}</p>
                            <p className="text-sm text-zinc-500 dark:text-zinc-400">
                                {t('aiTemplateForm.productImageDesc')}
                            </p>
                            <span className="inline-flex items-center gap-1 mt-3 text-xs font-medium text-primary">
                                <IconWand size={13} /> {t('aiTemplateForm.aiBuildsProductAd')}
                            </span>
                        </button>

                        {/* Path D: product + model → combine a human model with the product */}
                        <button
                            onClick={() => productModelInputRef.current?.click()}
                            className="group text-left bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-5 hover:border-primary hover:shadow-sm transition-all"
                        >
                            <div className="w-11 h-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3 group-hover:bg-primary group-hover:text-white transition-colors">
                                <IconUsers size={22} />
                            </div>
                            <p className="font-semibold text-zinc-900 dark:text-zinc-100 mb-1">{t('aiTemplateForm.productModel')}</p>
                            <p className="text-sm text-zinc-500 dark:text-zinc-400">
                                {t('aiTemplateForm.productModelDesc')}
                            </p>
                            <span className="inline-flex items-center gap-1 mt-3 text-xs font-medium text-primary">
                                <IconWand size={13} /> {t('aiTemplateForm.aiBuildsProductModel')}
                            </span>
                        </button>

                        {/* Path E: bulk — many images → auto-created draft templates */}
                        <button
                            onClick={() => bulkImageInputRef.current?.click()}
                            className="group text-left bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-5 hover:border-primary hover:shadow-sm transition-all"
                        >
                            <div className="w-11 h-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3 group-hover:bg-primary group-hover:text-white transition-colors">
                                <IconPhotoPlus size={22} />
                            </div>
                            <p className="font-semibold text-zinc-900 dark:text-zinc-100 mb-1">{t('aiTemplateForm.bulkUpload')}</p>
                            <p className="text-sm text-zinc-500 dark:text-zinc-400">
                                {t('aiTemplateForm.bulkUploadDesc', { max: MAX_BULK_IMAGES })}
                            </p>
                            <span className="inline-flex items-center gap-1 mt-3 text-xs font-medium text-primary">
                                <IconWand size={13} /> {t('aiTemplateForm.bulkBadge')}
                            </span>
                        </button>
                    </div>

                    {/* Hidden input for the image path (multiple allowed) */}
                    <input
                        ref={chooseImageInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                            // Snapshot into an array FIRST — resetting value below clears
                            // the live FileList (same object), which would zero out `files`
                            // before we read it and silently skip the crop with no error.
                            const files = Array.from(e.target.files || []);
                            e.target.value = '';
                            if (files.length) { setCreationMode('image'); handleImagePathFiles(files); }
                        }}
                    />
                    {/* Hidden input for the product-image path */}
                    <input
                        ref={productImageInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            e.target.value = '';
                            if (files.length) { setCreationMode('product'); handleImagePathFiles(files); }
                        }}
                    />
                    {/* Hidden input for the product + model path (upload the human model
                        and product example images together) */}
                    <input
                        ref={productModelInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            e.target.value = '';
                            if (files.length) { setCreationMode('product-model'); handleImagePathFiles(files); }
                        }}
                    />
                    {/* Hidden input for the bulk path — each image becomes a draft template */}
                    <input
                        ref={bulkImageInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            e.target.value = '';
                            if (!files.length) return;
                            if (files.length > MAX_BULK_IMAGES) {
                                notifications.show({
                                    title: t('aiTemplateForm.bulkUpload'),
                                    message: t('aiTemplateForm.bulkTrimmed', { max: MAX_BULK_IMAGES }),
                                    color: 'yellow',
                                });
                            }
                            setBulkFiles(files.slice(0, MAX_BULK_IMAGES));
                            setCreationMode('bulk');
                        }}
                    />
                </div>
            )}

            {/* ── Bulk path (create only): analyze every image and save each as a draft ── */}
            {!isEdit && creationMode === 'bulk' && bulkFiles?.length > 0 && (
                <BulkTemplateCreatePanel
                    key={`${bulkFiles.length}-${bulkFiles[0]?.name}-${bulkFiles[0]?.lastModified}`}
                    files={bulkFiles}
                    categories={knownCategories}
                    onBack={() => { setBulkFiles(null); setCreationMode('choose'); }}
                />
            )}

            {/* Pasted-image destination chooser: which path to use the pasted image(s) for */}
            {pastedImages && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setPastedImages(null)}>
                    <div className="w-full max-w-md bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-5" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-1">
                            <h3 className="text-[15px] font-semibold text-zinc-900 dark:text-zinc-100">{t('aiTemplateForm.pasteUseAsTitle')}</h3>
                            <button type="button" onClick={() => setPastedImages(null)} className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 text-xl leading-none">&times;</button>
                        </div>
                        <p className="text-[13px] text-zinc-500 dark:text-zinc-400 mb-4">{t('aiTemplateForm.pasteUseAsDesc')}</p>
                        <div className="grid grid-cols-3 gap-3">
                            <button
                                type="button"
                                onClick={() => choosePasteDestination('image')}
                                className="group flex flex-col items-center gap-2 rounded-xl border border-zinc-200 dark:border-zinc-700 p-4 hover:border-primary hover:bg-primary/5 transition-colors"
                            >
                                <span className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors">
                                    <IconPhoto size={20} />
                                </span>
                                <span className="text-[13px] font-semibold text-zinc-800 dark:text-zinc-100 text-center">{t('aiTemplateForm.uploadImage')}</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => choosePasteDestination('product')}
                                className="group flex flex-col items-center gap-2 rounded-xl border border-zinc-200 dark:border-zinc-700 p-4 hover:border-primary hover:bg-primary/5 transition-colors"
                            >
                                <span className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors">
                                    <IconShoppingBag size={20} />
                                </span>
                                <span className="text-[13px] font-semibold text-zinc-800 dark:text-zinc-100 text-center">{t('aiTemplateForm.productImage')}</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => choosePasteDestination('product-model')}
                                className="group flex flex-col items-center gap-2 rounded-xl border border-zinc-200 dark:border-zinc-700 p-4 hover:border-primary hover:bg-primary/5 transition-colors"
                            >
                                <span className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors">
                                    <IconUsers size={20} />
                                </span>
                                <span className="text-[13px] font-semibold text-zinc-800 dark:text-zinc-100 text-center">{t('aiTemplateForm.productModel')}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Product path: two dedicated dropzones (product + reference design) ── */}
            {!isEdit && creationMode === 'product' && wizardStep === 0 && (
                <div className="max-w-3xl mx-auto">
                    {analyzingImage ? (
                        <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-8 flex flex-col items-center justify-center text-center gap-3 min-h-[200px]">
                            <span className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                            <p className="text-sm font-semibold text-primary">{t('aiTemplateForm.analyzingImage')}</p>
                            <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed">{t('aiTemplateForm.analyzingImageDesc')}</p>
                        </div>
                    ) : (
                        <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-5 flex flex-col gap-4">
                            <p className="text-sm text-zinc-600 dark:text-zinc-300">{t('aiTemplateForm.productTwoBoxHint')}</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {/* Product image */}
                                <div>
                                    <label className="block text-xs font-semibold text-zinc-700 dark:text-zinc-200 mb-1.5">{t('aiTemplateForm.productMainImage')}</label>
                                    <button type="button" onClick={() => productBoxInputRef.current?.click()} className="w-full aspect-square rounded-xl border-2 border-dashed border-zinc-300 dark:border-zinc-600 overflow-hidden flex items-center justify-center bg-zinc-50 dark:bg-zinc-800 hover:border-primary transition-colors">
                                        {analysisExtraPreviews[0] ? (
                                            <img src={analysisExtraPreviews[0]} alt="" className="w-full h-full object-contain" />
                                        ) : (
                                            <span className="flex flex-col items-center gap-1.5 text-zinc-400">
                                                <IconShoppingBag size={26} />
                                                <span className="text-xs font-medium">{t('aiTemplateForm.clickToUpload')}</span>
                                            </span>
                                        )}
                                    </button>
                                    <p className="text-[11px] text-zinc-400 dark:text-zinc-500 mt-1">{t('aiTemplateForm.productMainImageHint')}</p>
                                </div>
                                {/* Reference design */}
                                <div>
                                    <label className="block text-xs font-semibold text-zinc-700 dark:text-zinc-200 mb-1.5">{t('aiTemplateForm.referenceDesignImage')}</label>
                                    <button type="button" onClick={() => referenceBoxInputRef.current?.click()} className="w-full aspect-square rounded-xl border-2 border-dashed border-zinc-300 dark:border-zinc-600 overflow-hidden flex items-center justify-center bg-zinc-50 dark:bg-zinc-800 hover:border-primary transition-colors">
                                        {thumbnailPreview ? (
                                            <img src={thumbnailPreview} alt="" className="w-full h-full object-contain" />
                                        ) : (
                                            <span className="flex flex-col items-center gap-1.5 text-zinc-400">
                                                <IconPhoto size={26} />
                                                <span className="text-xs font-medium">{t('aiTemplateForm.clickToUpload')}</span>
                                            </span>
                                        )}
                                    </button>
                                    <p className="text-[11px] text-zinc-400 dark:text-zinc-500 mt-1">{t('aiTemplateForm.referenceDesignImageHint')}</p>
                                </div>
                            </div>

                            {/* Optional description */}
                            <div>
                                <label className="flex items-center gap-1.5 text-xs font-medium mb-1 text-zinc-600 dark:text-zinc-400">{t('aiTemplateForm.analysisNoteLabel')}</label>
                                <textarea
                                    value={analysisNote}
                                    onChange={(e) => setAnalysisNote(e.target.value)}
                                    rows={2}
                                    placeholder={t('aiTemplateForm.analysisNotePlaceholder')}
                                    className="w-full px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                                />
                            </div>

                            <button
                                onClick={handleImagePathAnalyze}
                                disabled={!thumbnailFile || analysisExtraFiles.length === 0 || optimizingCount > 0}
                                className="w-full inline-flex items-center justify-center gap-1.5 px-5 py-2.5 text-sm font-semibold bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <IconSparkles size={16} /> {t('aiTemplateForm.generate')}
                            </button>
                            <div className="flex items-center justify-center text-xs">
                                <button onClick={() => setCreationMode('choose')} className="text-zinc-500 dark:text-zinc-400 hover:text-primary transition-colors">← {t('aiTemplateForm.backToOptions')}</button>
                            </div>
                        </div>
                    )}

                    <input ref={productBoxInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) setProductImage(f); }} />
                    <input ref={referenceBoxInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) setReferenceImage(f); }} />
                </div>
            )}

            {/* ── Image path: analyzing / upload state (also product-model path) ── */}
            {!isEdit && (creationMode === 'image' || creationMode === 'product-model') && wizardStep === 0 && (
                <div className={thumbnailPreview ? 'max-w-5xl mx-auto' : 'max-w-md mx-auto'}>
                    {thumbnailPreview ? (
                        /* Image on the LEFT; the RIGHT panel shows either the analyzing loader
                           or the controls — same two-column layout in both states. */
                        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 items-start">
                            {/* Left: example image preview */}
                            <div className="lg:col-span-3 bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-3">
                                <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-2 px-1">{t('aiTemplateForm.exampleImageHeading')}</p>
                                <div className="rounded-lg overflow-hidden bg-zinc-100 dark:bg-zinc-800">
                                    <img src={(previewIndex === 0 ? thumbnailPreview : analysisExtraPreviews[previewIndex - 1]) || thumbnailPreview} alt={t('aiTemplateForm.uploadedExampleAlt')} className="w-full h-auto block" />
                                </div>
                            </div>

                            {/* Right: sticky panel — loader while analyzing, else the controls */}
                            {analyzingImage ? (
                                <div className="lg:col-span-2 lg:sticky lg:top-4 bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-5 flex flex-col items-center justify-center text-center gap-3 min-h-[200px]">
                                    <span className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                                    <p className="text-sm font-semibold text-primary">{t('aiTemplateForm.analyzingImage')}</p>
                                    <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed">
                                        {t('aiTemplateForm.analyzingImageDesc')}
                                    </p>
                                </div>
                            ) : (
                            <div className="lg:col-span-2 lg:sticky lg:top-4 bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-5 flex flex-col gap-4">
                                <p className="text-sm text-zinc-600 dark:text-zinc-300">
                                    {t('aiTemplateForm.readyToGenerate')}
                                </p>

                                {/* All uploaded images — click a thumbnail to preview it large on the
                                    left. The first one (badge) becomes the template thumbnail. */}
                                <div>
                                    <div className="flex flex-wrap gap-2 mb-1.5">
                                        {[thumbnailPreview, ...analysisExtraPreviews].map((src, i) => {
                                            const isActive = previewIndex === i;
                                            const isPrimary = i === 0;
                                            return (
                                                <div key={i} className="relative">
                                                    <button
                                                        type="button"
                                                        onClick={() => setPreviewIndex(i)}
                                                        className={`block w-14 h-14 rounded-lg overflow-hidden border-2 transition-colors ${isActive ? 'border-primary ring-2 ring-primary/30' : 'border-zinc-200 dark:border-zinc-700 hover:border-primary/60'}`}
                                                    >
                                                        <img src={src} alt="" className="w-full h-full object-cover" />
                                                    </button>
                                                    {isPrimary ? (
                                                        <span className="absolute -top-1.5 -left-1.5 bg-primary text-white text-[8px] font-semibold px-1 py-0.5 rounded-full leading-none pointer-events-none">
                                                            {t('aiTemplateForm.mainImageBadge')}
                                                        </span>
                                                    ) : (
                                                        <button
                                                            onClick={() => removeAnalysisExtra(i - 1)}
                                                            className="absolute -top-1.5 -right-1.5 w-4 h-4 flex items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
                                                        >
                                                            <IconX size={10} />
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        {analysisExtraFiles.length < 5 && (
                                            <button
                                                onClick={() => addMorePathInputRef.current?.click()}
                                                title={t('aiTemplateForm.addMorePhotos')}
                                                className="w-14 h-14 flex items-center justify-center rounded-lg border border-dashed border-zinc-300 dark:border-zinc-600 text-zinc-400 hover:text-primary hover:border-primary transition-colors"
                                            >
                                                <IconPlus size={16} />
                                            </button>
                                        )}
                                    </div>
                                    <p className="text-[11px] text-zinc-400 dark:text-zinc-500">{t('aiTemplateForm.addMorePosesHint')}</p>
                                </div>

                                {/* Secondary options (description + audience) — collapsed by default */}
                                <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 overflow-hidden">
                                    <button
                                        type="button"
                                        onClick={() => setAnalysisOptionsOpen(o => !o)}
                                        className="w-full flex items-center justify-between px-3 py-2.5 text-xs font-medium text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors"
                                    >
                                        <span className="flex items-center gap-1.5">
                                            {t('aiTemplateForm.analysisMoreOptions')}
                                            <span className="text-[10px] font-normal text-zinc-400 dark:text-zinc-500 bg-zinc-100 dark:bg-zinc-800 rounded-full px-1.5 py-0.5">
                                                {t('aiTemplateForm.analysisNoteOptional')}
                                            </span>
                                            {analysisNote.trim() && (
                                                <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                                            )}
                                        </span>
                                        <IconChevronDown size={15} className={`transition-transform ${analysisOptionsOpen ? '' : '-rotate-90'}`} />
                                    </button>

                                    {analysisOptionsOpen && (
                                        <div className="px-3 pb-3 pt-1 flex flex-col gap-4 border-t border-zinc-100 dark:border-zinc-800">
                                            {/* Optional description — steers the AI analysis, never required */}
                                            <div>
                                                <label className="flex items-center gap-1.5 text-xs font-medium mb-1 text-zinc-600 dark:text-zinc-400">
                                                    {t('aiTemplateForm.analysisNoteLabel')}
                                                </label>
                                                <textarea
                                                    value={analysisNote}
                                                    onChange={(e) => setAnalysisNote(e.target.value)}
                                                    rows={3}
                                                    placeholder={t('aiTemplateForm.analysisNotePlaceholder')}
                                                    className="w-full px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                                                />
                                            </div>

                                            {/* Target gender(s) — makes the generated prompt fit the chosen
                                                audience. Skipped for product mode (no person) and for
                                                product+model (gender auto-detected from the human image). */}
                                            {creationMode !== 'product' && creationMode !== 'product-model' && GenderPicker}
                                        </div>
                                    )}
                                </div>

                                <button
                                    onClick={handleImagePathAnalyze}
                                    className="w-full inline-flex items-center justify-center gap-1.5 px-5 py-2.5 text-sm font-semibold bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
                                >
                                    <IconSparkles size={16} /> {t('aiTemplateForm.generate')}
                                </button>
                                <div className="flex items-center justify-between text-xs">
                                    <button
                                        onClick={() => imagePathInputRef.current?.click()}
                                        className="text-zinc-500 dark:text-zinc-400 hover:text-primary transition-colors"
                                    >
                                        {t('aiTemplateForm.chooseDifferent')}
                                    </button>
                                    <button
                                        onClick={() => setCreationMode('choose')}
                                        className="text-zinc-500 dark:text-zinc-400 hover:text-primary transition-colors"
                                    >
                                        ← {t('aiTemplateForm.backToOptions')}
                                    </button>
                                </div>
                            </div>
                            )}
                        </div>
                    ) : (
                        /* No image yet — centered upload prompt */
                        <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-6 text-center">
                            <p className="text-sm text-zinc-600 dark:text-zinc-300 mb-4">
                                {t('aiTemplateForm.uploadExampleHint')}
                            </p>
                            <button
                                onClick={() => imagePathInputRef.current?.click()}
                                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
                            >
                                <IconUpload size={16} /> {t('aiTemplateForm.chooseImage')}
                            </button>
                            <button
                                onClick={() => setCreationMode('choose')}
                                className="block mx-auto mt-3 text-xs text-zinc-500 dark:text-zinc-400 hover:text-primary transition-colors"
                            >
                                ← {t('aiTemplateForm.backToOptions')}
                            </button>
                        </div>
                    )}
                    <input
                        ref={imagePathInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            e.target.value = '';
                            if (files.length) handleImagePathFiles(files);
                        }}
                    />
                    {/* Add more example images (kept as analysis-only extras) */}
                    <input
                        ref={addMorePathInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            e.target.value = '';
                            if (files.length) addAnalysisExtraFiles(files);
                        }}
                    />
                </div>
            )}

            {/* ── Step 0: Write & Generate (prompt path) ── */}
            {creationMode === 'prompt' && wizardStep === 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                    {/* Left panel (40%): Generate Thumbnail card */}
                    <div className="flex flex-col gap-4 lg:col-span-2">
                        <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-4 sm:p-5">
                            <p className="font-semibold text-zinc-900 dark:text-zinc-100 mb-3">{t('aiTemplateForm.generateThumbnail')}</p>

                            {/* Image Ratio selector */}
                            <div className="mb-3">
                                <label className="block text-sm font-medium mb-1 text-zinc-700 dark:text-zinc-300">{t('aiTemplateForm.imageRatio')}</label>
                                <Select value={imageSize} onValueChange={v => setImageSize(v || 'portrait_2x3')}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {IMAGE_SIZES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Toggles */}
                            {TogglePill}

                            {/* Gender / audience */}
                            {GenderPicker}

                            {/* Multi-image strip */}
                            {allowMultipleImages && requireUserImage && !testGenerating && (
                                <div className="mb-4 p-2 rounded-lg bg-orange-50/60 dark:bg-orange-900/10 border border-primary/30">
                                    {ReferenceModelPicker}
                                </div>
                            )}

                            {/* Single-mode reference picker — always shown so the admin can pick /
                                replace the test photo (the picked sample is highlighted). */}
                            {!allowMultipleImages && requireUserImage && !testResult && !testGenerating && (
                                <div className="mb-4 p-2 rounded-lg bg-orange-50/60 dark:bg-orange-900/10 border border-primary/30">
                                    {ReferenceModelPicker}
                                </div>
                            )}

                            {/* Generating shimmer */}
                            {testGenerating && GeneratingShimmer}

                            {/* Dynamic field test value inputs */}
                            {detectedKeys.length > 0 && !testResult && !testGenerating && (
                                <div className="flex flex-col gap-2 mb-4">
                                    <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400">{t('aiTemplateForm.fillTestValues')}</p>
                                    {detectedKeys.map(key => (
                                        <div key={key}>
                                            <label className="block text-xs font-medium mb-0.5 text-zinc-600 dark:text-zinc-400">
                                                {key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                            </label>
                                            <input
                                                type="text"
                                                placeholder={t('aiTemplateForm.enterField', { field: key })}
                                                className={inputXsCls}
                                                value={testFieldValues[key] || ''}
                                                onChange={e => setTestFieldValues(prev => ({ ...prev, [key]: e.target.value }))}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}

                            <hr className="border-zinc-200 dark:border-zinc-700 mb-3" />

                            <button
                                className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium bg-primary hover:bg-primary/90 text-white rounded-lg disabled:opacity-50 transition-colors"
                                onClick={handleTestGenerate}
                                disabled={testGenerating || !promptReady}
                            >
                                {testGenerating
                                    ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    : <IconSparkles size={16} />}
                                {t('aiTemplateForm.generateThumbnail')}
                            </button>

                            {!promptReady && (
                                <div className="mt-2 p-2 rounded bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
                                    <p className="text-xs text-yellow-700 dark:text-yellow-400">{t('aiTemplateForm.writeAiPromptFirst')}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right panel: Prompt card + detected placeholders */}
                    <div className="flex flex-col gap-4 lg:col-span-3">
                        {renderPromptCard()}
                        {NegativePromptCard}
                        {detectedKeys.length > 0 && (
                            <div className="bg-orange-50/60 dark:bg-orange-900/10 rounded-lg border border-primary/30 p-3">
                                <p className="text-xs font-medium text-primary mb-1.5">{t('aiTemplateForm.detectedPlaceholders')}</p>
                                <div className="flex flex-wrap gap-1">
                                    {detectedKeys.map(key => (
                                        <span key={key} className="px-1.5 py-0.5 rounded text-xs font-mono bg-primary/10 dark:bg-primary/20 text-primary">
                                            {`{${key}}`}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="p-3 rounded-lg bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-700">
                            <p className="text-sm text-zinc-600 dark:text-zinc-300">
                                {t('aiTemplateForm.runTestHintLead')} <strong className="text-zinc-800 dark:text-zinc-100">{t('aiTemplateForm.useAsThumbnailContinue')}</strong> {t('aiTemplateForm.runTestHintTail')}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Step 1: Review Result ── */}
            {wizardStep === 1 && (
                <div className="max-w-5xl mx-auto">
                    {/* Same two-column layout as the image-upload step: result on the LEFT,
                        all actions in a sticky panel on the RIGHT (no scrolling to reach them). */}
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 items-start">
                        {/* Left: generated result */}
                        <div className="lg:col-span-3 bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-3">
                            <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-2 px-1">{t('aiTemplateForm.generatedThumbnail')}</p>
                            <div className="rounded-lg overflow-hidden bg-zinc-100 dark:bg-zinc-800 max-h-[calc(100vh-220px)] overflow-y-auto">
                                {testGenerating ? (
                                    /* Regenerating — show a loading state instead of a broken/empty image */
                                    <div className="min-h-[320px] flex flex-col items-center justify-center gap-3 text-center px-4">
                                        <span className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                                        <p className="text-sm font-semibold text-primary">{t('aiTemplateForm.generating')}</p>
                                    </div>
                                ) : testResult?.url ? (
                                    <img src={testResult.url} alt={t('aiTemplateForm.generatedAlt')} onError={handleResultImgError} className="w-full h-auto block" />
                                ) : (
                                    <div className="min-h-[320px]" />
                                )}
                            </div>
                        </div>

                        {/* Right: actions — sticky so they stay in view beside a tall image */}
                        <div className="lg:col-span-2 lg:sticky lg:top-4 bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-5 flex flex-col gap-4">
                            <p className="text-sm text-zinc-600 dark:text-zinc-300">
                                {t('aiTemplateForm.happyWithResult')}
                            </p>

                            {/* Primary action — accept the result and continue */}
                            <button
                                className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold bg-primary hover:bg-primary/90 text-white rounded-lg disabled:opacity-50 transition-colors shadow-sm shadow-primary/30"
                                onClick={useTestAsThumbnail}
                                disabled={settingThumbnail || testGenerating || downloading}
                            >
                                {settingThumbnail
                                    ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> {t('aiTemplateForm.savingThumbnail')}</>
                                    : <><IconCheck size={16} /> {t('aiTemplateForm.useAsThumbnailContinue')}</>}
                            </button>

                            {/* Secondary actions — merged regenerate (segmented) + download */}
                            <div className="grid grid-cols-2 gap-2">
                                {/* Segmented amber control: main = regenerate (same photo),
                                    attached segment = replace the photo, then regenerate. */}
                                <div className="flex rounded-lg overflow-hidden border border-amber-200 dark:border-amber-900/40 divide-x divide-amber-200 dark:divide-amber-900/40">
                                    <button
                                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-2.5 text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/20 dark:hover:bg-amber-900/40 disabled:opacity-50 transition-colors"
                                        onClick={() => doGenerate()}
                                        disabled={testGenerating || settingThumbnail || downloading}
                                    >
                                        {testGenerating
                                            ? <span className="w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                                            : <IconRefresh size={16} />}
                                        {testGenerating ? t('aiTemplateForm.generating') : t('aiTemplateForm.regenerate')}
                                    </button>
                                    <button
                                        title={t('aiTemplateForm.replaceAndRegenerate')}
                                        aria-label={t('aiTemplateForm.replaceAndRegenerate')}
                                        className="flex-shrink-0 inline-flex items-center justify-center px-3 text-amber-700 dark:text-amber-400 bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/20 dark:hover:bg-amber-900/40 disabled:opacity-50 transition-colors"
                                        onClick={handleReplaceAndGenerate}
                                        disabled={testGenerating || settingThumbnail || downloading}
                                    >
                                        <IconUpload size={16} />
                                    </button>
                                </div>
                                <button
                                    className="inline-flex items-center justify-center gap-1.5 px-2 py-2.5 text-xs font-medium text-slate-700 dark:text-slate-300 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 rounded-lg disabled:opacity-50 transition-colors"
                                    onClick={downloadTestResult}
                                    disabled={downloading || settingThumbnail || testGenerating}
                                >
                                    {downloading
                                        ? <span className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                                        : <IconDownload size={16} />}
                                    {downloading ? t('aiTemplateForm.downloading') : t('aiTemplateForm.download')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Step 2 / Edit: Full form ── */}
            {(isEdit || wizardStep === 2) && (
                <div className="grid grid-cols-1 lg:grid-cols-5 xl:grid-cols-12 gap-4">
                    {/* Part 1: Thumbnail (collapsible) */}
                    <div className="flex flex-col gap-3 lg:col-span-2 xl:col-span-3 min-w-0">

                        {/* Thumbnail card — minimal label; upload/replace revealed on hover.
                            Collapsible so the tall image can get out of the way. */}
                        <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-4">
                            <button
                                type="button"
                                onClick={() => setThumbCollapsed(c => !c)}
                                className={`w-full flex items-center justify-between gap-2 text-left ${thumbCollapsed ? '' : 'mb-2'}`}
                            >
                                <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">{t('aiTemplateForm.templateThumbnail')}</span>
                                <IconChevronDown size={16} className={`text-zinc-400 transition-transform ${thumbCollapsed ? '-rotate-90' : ''}`} />
                            </button>
                            {!thumbCollapsed && (<>
                            {previewSrc ? (
                                <div className="group relative rounded overflow-hidden">
                                    <img
                                        src={previewSrc}
                                        alt={t('aiTemplateForm.thumbnailAlt')}
                                        className="w-full h-auto block"
                                    />
                                    {/* Delete — always available, top-right */}
                                    <button
                                        className="absolute top-1.5 right-1.5 p-1 rounded bg-red-600 text-white hover:bg-red-700 transition-colors z-10"
                                        onClick={handleClearThumbnail}
                                    >
                                        <IconTrash size={16} />
                                    </button>
                                    {/* Replace — appears on hover over the image */}
                                    <button
                                        onClick={() => thumbnailInputRef.current?.click()}
                                        className="absolute inset-0 flex items-center justify-center gap-1.5 bg-black/45 text-white text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <IconUpload size={16} /> {t('aiTemplateForm.replaceImage')}
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => thumbnailInputRef.current?.click()}
                                    className="group w-full h-[280px] rounded border-2 border-dashed border-zinc-200 dark:border-zinc-700 hover:border-primary flex flex-col items-center justify-center gap-2 bg-zinc-50 dark:bg-zinc-800 hover:bg-primary/5 transition-colors"
                                >
                                    <IconPhoto size={42} className="text-zinc-400 group-hover:hidden" />
                                    <span className="text-sm text-zinc-400 group-hover:hidden">{t('aiTemplateForm.noImageUploaded')}</span>
                                    <IconUpload size={42} className="text-primary hidden group-hover:block" />
                                    <span className="text-sm font-semibold text-primary hidden group-hover:block">{t('aiTemplateForm.uploadImageBtn')}</span>
                                </button>
                            )}

                            </>)}
                        </div>
                    </div>

                    {/* Part 2: Test Generate — its own column so the options get real width */}
                    <div className="flex flex-col gap-3 lg:col-span-3 xl:col-span-3 min-w-0">
                        <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-4">
                            <div className="mb-2">
                                <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('aiTemplateForm.testGenerate')}</p>
                                <p className="text-xs text-zinc-500 dark:text-zinc-400">{t('aiTemplateForm.testGenerateDesc')}</p>
                            </div>

                        {/* Settings — ratio + toggles grouped together for easy management */}
                        <div className="mb-4 rounded-lg border border-zinc-200 dark:border-zinc-700 divide-y divide-zinc-200 dark:divide-zinc-700 overflow-hidden">
                            {/* Ratio row */}
                            <div className="flex items-center justify-between gap-2 px-2.5 py-1.5">
                                <label className="text-xs font-medium text-zinc-600 dark:text-zinc-300">{t('aiTemplateForm.imageRatio')}</label>
                                <div className="w-44">
                                    <Select value={imageSize} onValueChange={v => setImageSize(v || 'portrait_2x3')}>
                                        <SelectTrigger className="h-7 text-xs">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {IMAGE_SIZES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            {/* User-image toggle row */}
                            <div className="flex items-center justify-between gap-2 px-2.5 py-1.5">
                                <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">{t('aiTemplateForm.userImage')}</span>
                                <label className="cursor-pointer select-none">
                                    <span className="relative inline-block w-8 h-4 align-middle">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={requireUserImage}
                                            onChange={e => {
                                                setRequireUserImage(e.currentTarget.checked);
                                                if (!e.currentTarget.checked) setAllowMultipleImages(false);
                                            }}
                                        />
                                        <span className="absolute inset-0 rounded-full bg-zinc-300 dark:bg-zinc-600 peer-checked:bg-primary transition-colors" />
                                        <span className="absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white transition-transform peer-checked:translate-x-4" />
                                    </span>
                                </label>
                            </div>
                            {/* Multiple-images toggle row — only when user image is required */}
                            {requireUserImage && (
                                <div className="flex items-center justify-between gap-2 px-2.5 py-1.5">
                                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">{t('aiTemplateForm.multipleImages')}</span>
                                    <label className="cursor-pointer select-none">
                                        <span className="relative inline-block w-8 h-4 align-middle">
                                            <input
                                                type="checkbox"
                                                className="sr-only peer"
                                                checked={allowMultipleImages}
                                                onChange={e => {
                                                    const on = e.currentTarget.checked;
                                                    setAllowMultipleImages(on);
                                                    if (on) {
                                                        setImageLabels(prev => prev.length >= 2 ? prev : [
                                                            prev[0] || { label: '', required: true },
                                                            { label: '', required: true },
                                                        ]);
                                                    } else {
                                                        setTestExtraPreviews(prev => { prev.forEach(u => URL.revokeObjectURL(u)); return []; });
                                                        setTestExtraFiles([]);
                                                        setRequiredImageCount(1);
                                                        setTestResult(null);
                                                    }
                                                }}
                                            />
                                            <span className="absolute inset-0 rounded-full bg-zinc-300 dark:bg-zinc-600 peer-checked:bg-primary transition-colors" />
                                            <span className="absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white transition-transform peer-checked:translate-x-4" />
                                        </span>
                                    </label>
                                </div>
                            )}
                        </div>

                        {/* Multi-image strip */}
                        {allowMultipleImages && requireUserImage && !testGenerating && (
                            <div className="mb-4 p-2 rounded-lg bg-orange-50/60 dark:bg-orange-900/10 border border-primary/30">
                                {ReferenceModelPicker}
                            </div>
                        )}

                        {/* Single-mode reference picker — always shown (same as multi mode) so
                            the admin can pick/replace the test photo; the picked sample is
                            highlighted. Stays visible after a result too, so the admin can
                            switch the photo and regenerate without extra steps. */}
                        {!allowMultipleImages && requireUserImage && !testGenerating && (
                            <div className="mb-4 p-2 rounded-lg bg-orange-50/60 dark:bg-orange-900/10 border border-primary/30">
                                {ReferenceModelPicker}
                            </div>
                        )}

                        {/* Single-mode result image (after generation) */}
                        {!allowMultipleImages && testResult && !testGenerating && (
                            <div className="mb-4">
                                <img src={testResult.url} onError={handleResultImgError} className="w-full h-auto rounded bg-zinc-100 dark:bg-zinc-800" />
                            </div>
                        )}

                        {/* Single-mode dynamic fields — always visible (also after a result, and
                            for templates without a user photo) so the admin can tweak and regenerate */}
                        {!allowMultipleImages && fields.length > 0 && !testGenerating && (
                            <div className="grid grid-cols-2 gap-2 mb-4">
                                {fields.map(renderTestField)}
                            </div>
                        )}

                        {/* Multi-mode: show generated result */}
                        {allowMultipleImages && testResult && !testGenerating && (
                            <div className="mb-4">
                                <img
                                    src={testResult.url}
                                    onError={handleResultImgError}
                                    className="w-full h-auto rounded bg-zinc-100 dark:bg-zinc-800"
                                />
                            </div>
                        )}

                        {/* Dynamic fields — multi-mode, always visible (also after a result) */}
                        {allowMultipleImages && fields.length > 0 && !testGenerating && (
                            <div className="grid grid-cols-2 gap-2 mb-4">
                                {fields.map(renderTestField)}
                            </div>
                        )}

                        {/* Generating shimmer */}
                        {testGenerating && GeneratingShimmer}

                        {/* Post-generation actions */}
                        {testResult && !testGenerating && (
                            <div className="flex flex-col gap-1.5 mb-4">
                                {thumbnailUrl && thumbnailUrl === testResult.url ? (
                                    /* Already the saved thumbnail — compact confirmation badge */
                                    <div className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-900/40 rounded-lg">
                                        <IconCheck size={14} /> {t('aiTemplateForm.thumbnailAlreadySet')}
                                    </div>
                                ) : (
                                    <button
                                        className="w-full inline-flex items-center justify-center gap-1 px-3 py-2 text-xs font-semibold bg-primary hover:bg-primary/90 text-white rounded-lg disabled:opacity-50 transition-colors"
                                        onClick={useTestAsThumbnail}
                                        disabled={settingThumbnail || downloading}
                                    >
                                        {settingThumbnail
                                            ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            : <IconPhoto size={14} />}
                                        {settingThumbnail ? t('aiTemplateForm.saving') : (isEdit ? t('aiTemplateForm.setAsThumbnail') : t('aiTemplateForm.saveAsThumbnailContinue'))}
                                    </button>
                                )}
                                {/* Compact icon row: replace · regenerate · download */}
                                <div className="grid grid-cols-3 gap-1.5">
                                    <button
                                        title={t('aiTemplateForm.replaceAndGenerate')}
                                        className="inline-flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] font-medium text-zinc-600 dark:text-zinc-300 bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 rounded-md disabled:opacity-50 transition-colors"
                                        onClick={handleReplaceAndGenerate}
                                        disabled={settingThumbnail || downloading}
                                    >
                                        <IconUpload size={14} /> {t('aiTemplateForm.replace')}
                                    </button>
                                    <button
                                        title={t('aiTemplateForm.generateAgain')}
                                        className="inline-flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] font-medium text-amber-700 dark:text-amber-400 bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/20 dark:hover:bg-amber-900/40 rounded-md disabled:opacity-50 transition-colors"
                                        onClick={() => doGenerate()}
                                        disabled={settingThumbnail || downloading}
                                    >
                                        <IconRefresh size={14} /> {t('aiTemplateForm.regenerate')}
                                    </button>
                                    <button
                                        title={t('aiTemplateForm.download')}
                                        className="inline-flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] font-medium text-slate-600 dark:text-slate-300 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-md disabled:opacity-50 transition-colors"
                                        onClick={downloadTestResult}
                                        disabled={downloading || settingThumbnail}
                                    >
                                        {downloading
                                            ? <span className="w-3.5 h-3.5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                                            : <IconDownload size={14} />}
                                        {t('aiTemplateForm.download')}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Result image for no-user-image templates */}
                        {!requireUserImage && testResult && !testGenerating && (
                            <div className="mb-4">
                                <img
                                    src={testResult.url}
                                    onError={handleResultImgError}
                                    className="w-full h-auto rounded bg-zinc-100 dark:bg-zinc-800"
                                />
                            </div>
                        )}

                        {/* Dynamic field inputs for no-image templates — always visible */}
                        {!requireUserImage && fields.length > 0 && !testGenerating && (
                            <div className="grid grid-cols-2 gap-2 mb-4">
                                {fields.map(renderTestField)}
                            </div>
                        )}

                        <hr className="border-zinc-200 dark:border-zinc-700 mb-3" />

                        <button
                            className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium bg-primary hover:bg-primary/90 text-white rounded-lg disabled:opacity-50 transition-colors"
                            onClick={handleTestGenerate}
                            disabled={testGenerating || !promptReady}
                        >
                            {testGenerating
                                ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                : <IconFlask size={16} />}
                            {testFile && !testResult ? t('aiTemplateForm.generate') : t('aiTemplateForm.runTestGenerate')}
                        </button>

                        {!promptReady && (
                            <div className="mt-2 p-2 rounded bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
                                <p className="text-xs text-yellow-700 dark:text-yellow-400">{t('aiTemplateForm.writeAiPromptFirst')}</p>
                            </div>
                        )}
                        </div>

                    </div>

                    {/* Part 3: ONE tabbed card — Prompt / Dynamic Fields / Basic Info /
                        Image Settings. Panels stay mounted so nothing resets on switch. */}
                    <div className="flex flex-col gap-3 lg:col-span-5 xl:col-span-6 min-w-0">
                        <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700">
                            {/* Tab bar */}
                            <div className="flex items-center gap-1 px-2 border-b border-zinc-200 dark:border-zinc-700 overflow-x-auto overflow-y-hidden">
                                {[
                                    { key: 'prompt', n: 1, label: t('aiTemplateForm.promptFieldsTab') },
                                    { key: 'basic',  n: 2, label: t('aiTemplateForm.basicImageTab') },
                                ].map(tab => (
                                    <button
                                        key={tab.key}
                                        type="button"
                                        onClick={() => setDetailsTab(tab.key)}
                                        className={`inline-flex items-center gap-2 px-3 py-2.5 text-[13px] font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors ${detailsTab === tab.key
                                            ? 'border-primary text-primary'
                                            : 'border-transparent text-zinc-500 dark:text-zinc-400 hover:text-zinc-800 dark:hover:text-zinc-200'}`}
                                    >
                                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold ${detailsTab === tab.key ? 'bg-primary text-white' : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400'}`}>
                                            {i18n.language === 'bn' ? '০১২৩৪৫৬৭৮৯'[tab.n] : tab.n}
                                        </span>
                                        {tab.label}
                                    </button>
                                ))}
                            </div>

                            {/* Prompt panel — main prompt (wide) + negative (narrow) side by side */}
                            <div className={detailsTab === 'prompt' ? 'p-4' : 'hidden'}>
                                <div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-4 items-start">
                                    <div className="min-w-0">
                                        <div className="mb-3">
                                            <div className="mb-2.5">
                                                <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('aiTemplateForm.writeThePrompt')}</p>
                                                <p className="text-xs text-zinc-500 dark:text-zinc-400">{t('aiTemplateForm.writeThePromptSub')}</p>
                                            </div>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                {promptTokenizeBtn}
                                                {promptAiBtn}
                                            </div>
                                        </div>
                                        {promptBody}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="mb-3">
                                            <div className="mb-2.5">
                                                <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('aiTemplateForm.negativePrompt')}</p>
                                                <p className="text-xs text-zinc-500 dark:text-zinc-400">{t('aiTemplateForm.negativeHelper')}</p>
                                            </div>
                                            <div className="flex items-center gap-2 flex-wrap">{negativeTokenizeBtn}{analyzeSectionBtn(handleAnalyzeNegative, analyzingNegative)}{negativeClearBtn}</div>
                                        </div>
                                        {negativeBody}
                                    </div>
                                </div>

                                {/* Dynamic Fields — stacked below the prompt in the same tab */}
                                <div className="mt-4 pt-4 border-t border-zinc-200 dark:border-zinc-700">
                                    <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
                                        <div className="min-w-0">
                                            <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('aiTemplateForm.dynamicFields')}</p>
                                            <p className="text-xs text-zinc-500 dark:text-zinc-400">{t('aiTemplateForm.dynamicFieldsSubLead')}{' '}<span className="font-mono bg-primary/10 dark:bg-primary/20 text-primary px-1 rounded text-xs">&#123;key&#125;</span>{' '}{t('aiTemplateForm.dynamicFieldsSubTail')}</p>
                                        </div>
                                        <div className="flex items-center gap-2">{analyzeSectionBtn(handleAnalyzeFields, analyzingFields)}{addFieldBtn}</div>
                                    </div>
                                    {dynamicFieldsBody}
                                </div>
                            </div>

                            {/* Basic Info panel */}
                            <div className={detailsTab === 'basic' ? 'p-4' : 'hidden'}>
                                <div className="flex items-center justify-between gap-2 mb-2">
                                    <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('aiTemplateForm.basicInfo')}</p>
                                    {analyzeSectionBtn(handleAnalyzeBasicInfo, analyzingBasic)}
                                </div>
                            <div className="flex flex-col gap-3">
                                {/* Name + Slug — one row keeps the section short */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {/* Name — active language only (toggle in the header) */}
                                <div>
                                    {labelLang === 'bn' ? (
                                        <>
                                            <label className="block text-sm font-medium mb-1 text-zinc-700 dark:text-zinc-300">{t('aiTemplateForm.nameBangla')}</label>
                                            <input
                                                type="text"
                                                placeholder={t('aiTemplateForm.nameBanglaPlaceholder')}
                                                className={inputCls}
                                                value={nameBn}
                                                onChange={e => setNameBn(e.target.value)}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <label className="block text-sm font-medium mb-1 text-zinc-700 dark:text-zinc-300">
                                                {t('aiTemplateForm.nameEnglish')} <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                placeholder={t('aiTemplateForm.nameEnglishPlaceholder')}
                                                className={inputCls}
                                                value={nameEn}
                                                onChange={e => setNameEn(e.target.value)}
                                            />
                                        </>
                                    )}
                                </div>

                                {/* Slug */}
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-zinc-700 dark:text-zinc-300">{t('aiTemplateForm.slug')}</label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            placeholder={t('aiTemplateForm.slugPlaceholder')}
                                            className={inputCls + ' pr-9'}
                                            value={slug}
                                            onChange={e => setSlug(e.target.value)}
                                        />
                                        <button
                                            title={t('aiTemplateForm.generateSlugTooltip')}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-primary hover:bg-primary/10 dark:hover:bg-primary/20 transition-colors disabled:opacity-40"
                                            onClick={handleGenerateSlug}
                                            disabled={!nameEn.trim()}
                                        >
                                            <IconWand size={14} />
                                        </button>
                                    </div>
                                </div>
                                </div>

                                {/* Category + Status */}
                                <div className="flex gap-2">
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium mb-1 text-zinc-700 dark:text-zinc-300">
                                            {t('aiTemplateForm.category')} <span className="text-red-500">*</span>
                                        </label>
                                        <SearchableSelect
                                            value={categoryId ? String(categoryId) : ''}
                                            onChange={v => setCategoryId(v || null)}
                                            options={knownCategories.map(c => ({
                                                value: String(c.id),
                                                label: c.nameBn ? `${c.name} — ${c.nameBn}` : c.name,
                                            }))}
                                            placeholder={t('aiTemplateForm.selectCategory')}
                                            searchPlaceholder={t('aiTemplateForm.searchCategory')}
                                            emptyText={t('aiTemplateForm.noCategoriesFound')}
                                            className="rounded-lg border-zinc-200 dark:border-zinc-700 focus:ring-primary/40"
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium mb-1 text-zinc-700 dark:text-zinc-300">{t('aiTemplateForm.status')}</label>
                                        <Select value={status} onValueChange={v => setStatus(v)}>
                                            <SelectTrigger className="w-full">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="active">{t('aiTemplateForm.statusActive')}</SelectItem>
                                                <SelectItem value="draft">{t('aiTemplateForm.statusDraft')}</SelectItem>
                                                <SelectItem value="archived">{t('aiTemplateForm.statusArchived')}</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                {/* Free + Global toggles — side by side to keep the section short */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {/* Free vs paid — free templates skip credit deduction on generate */}
                                <label className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 cursor-pointer select-none">
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{t('aiTemplateForm.freeLabel')}</p>
                                        <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{t('aiTemplateForm.freeHint')}</p>
                                    </div>
                                    <span className="relative inline-block w-9 h-5 flex-shrink-0">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={isFree}
                                            onChange={e => setIsFree(e.currentTarget.checked)}
                                        />
                                        <span className="absolute inset-0 rounded-full bg-zinc-300 dark:bg-zinc-600 peer-checked:bg-emerald-500 transition-colors" />
                                        <span className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform peer-checked:translate-x-4" />
                                    </span>
                                </label>

                                {/* Global vs BD-only — OFF hides the template from users outside Bangladesh */}
                                <label className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 cursor-pointer select-none">
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{t('aiTemplateForm.globalLabel')}</p>
                                        <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{t('aiTemplateForm.globalHint')}</p>
                                    </div>
                                    <span className="relative inline-block w-9 h-5 flex-shrink-0">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={isGlobal}
                                            onChange={e => setIsGlobal(e.currentTarget.checked)}
                                        />
                                        <span className="absolute inset-0 rounded-full bg-zinc-300 dark:bg-zinc-600 peer-checked:bg-emerald-500 transition-colors" />
                                        <span className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform peer-checked:translate-x-4" />
                                    </span>
                                </label>
                                </div>

                                {/* Gender / audience — drives the gallery gender filter */}
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-zinc-700 dark:text-zinc-300">{t('aiTemplateForm.genderLabel')}</label>
                                    {GenderChips}
                                    <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1">{t('aiTemplateForm.genderHint')}</p>
                                </div>

                                {/* Tags (optional) — used for search */}
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-zinc-700 dark:text-zinc-300">
                                        {t('aiTemplateForm.tags')} <span className="text-xs font-normal text-zinc-400">{t('aiTemplateForm.tagsHint')}</span>
                                    </label>
                                    <div className="flex flex-wrap items-center gap-1.5 px-2 py-1.5 border border-zinc-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800 focus-within:ring-2 focus-within:ring-primary/40">
                                        {templateTags.map(tag => (
                                            <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary dark:bg-primary/20">
                                                {tag}
                                                <button type="button" onClick={() => removeTag(tag)} className="hover:text-red-500 transition-colors">
                                                    <IconX size={11} />
                                                </button>
                                            </span>
                                        ))}
                                        <input
                                            type="text"
                                            list="ai-template-tag-suggestions"
                                            value={tagInput}
                                            onChange={e => setTagInput(e.target.value)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(tagInput); }
                                                else if (e.key === 'Backspace' && !tagInput && templateTags.length) removeTag(templateTags[templateTags.length - 1]);
                                            }}
                                            onBlur={() => tagInput.trim() && addTag(tagInput)}
                                            placeholder={templateTags.length ? t('aiTemplateForm.addAnotherTag') : t('aiTemplateForm.typeTagPressEnter')}
                                            className="flex-1 min-w-[140px] bg-transparent text-sm outline-none text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400"
                                        />
                                        <datalist id="ai-template-tag-suggestions">
                                            {allTags.filter(t => !templateTags.some(x => x.toLowerCase() === t.toLowerCase())).map(t => <option key={t} value={t} />)}
                                        </datalist>
                                    </div>
                                </div>

                            </div>

                            {/* Image Settings — stacked below basic info in the same tab */}
                            <div className="mt-4 pt-4 border-t border-zinc-200 dark:border-zinc-700">
                            <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mb-2">{t('aiTemplateForm.imageSettings')}</p>
                            <div className="flex flex-col gap-3">
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-zinc-700 dark:text-zinc-300">{t('aiTemplateForm.imageRatio')}</label>
                                    <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-1">{t('aiTemplateForm.aspectRatioDesc')}</p>
                                    <Select value={imageSize} onValueChange={v => setImageSize(v || 'portrait_2x3')}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {IMAGE_SIZES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <label className="flex items-start gap-3 cursor-pointer">
                                        <span className="relative inline-block w-9 h-5 mt-0.5">
                                            <input
                                                type="checkbox"
                                                className="sr-only peer"
                                                checked={requireUserImage}
                                                onChange={e => {
                                                    setRequireUserImage(e.currentTarget.checked);
                                                    if (!e.currentTarget.checked) setAllowMultipleImages(false);
                                                }}
                                            />
                                            <span className="absolute inset-0 rounded-full bg-zinc-300 dark:bg-zinc-600 peer-checked:bg-primary transition-colors" />
                                            <span className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform peer-checked:translate-x-4" />
                                        </span>
                                        <div>
                                            <span className="text-sm text-zinc-700 dark:text-zinc-300 font-medium">{t('aiTemplateForm.requireUserPhoto')}</span>
                                            <p className="text-xs text-zinc-500 dark:text-zinc-400">{t('aiTemplateForm.requireUserPhotoDesc')}</p>
                                        </div>
                                    </label>
                                </div>

                                {/* Single-image label — pick a key from the labels file; the
                                    displayed option text follows the global label language. */}
                                {requireUserImage && !allowMultipleImages && (
                                    <div>
                                        <label className="block text-sm font-medium mb-1 text-zinc-700 dark:text-zinc-300">{t('aiTemplateForm.imageInputLabel')}</label>
                                        <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-2">
                                            {t('aiTemplateForm.imageInputLabelDesc')}
                                        </p>
                                        <div className="flex">{renderImageLabelControl(0, imageLabels[0])}</div>
                                    </div>
                                )}

                                {/* Multi-image inputs — each: label + required + delete */}
                                {requireUserImage && allowMultipleImages && (
                                    <div>
                                        <label className="block text-sm font-medium mb-1 text-zinc-700 dark:text-zinc-300">{t('aiTemplateForm.imageInputs')}</label>
                                        <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-2">
                                            {t('aiTemplateForm.imageInputsDesc', { max: MAX_IMAGE_INPUTS })}
                                        </p>
                                        <div className="flex flex-col gap-2">
                                            {imageLabels.map((input, i) => (
                                                <div key={i} className="flex items-center gap-2">
                                                    {renderImageLabelControl(i, input)}
                                                    <label className="flex items-center gap-1.5 cursor-pointer select-none flex-shrink-0" title={t('aiTemplateForm.mustUploadTooltip')}>
                                                        <span className="relative inline-block w-9 h-5">
                                                            <input
                                                                type="checkbox"
                                                                className="sr-only peer"
                                                                checked={input.required !== false}
                                                                onChange={e => updateImageInput(i, { required: e.currentTarget.checked })}
                                                            />
                                                            <span className="absolute inset-0 rounded-full bg-zinc-300 dark:bg-zinc-600 peer-checked:bg-primary transition-colors" />
                                                            <span className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform peer-checked:translate-x-4" />
                                                        </span>
                                                        <span className="text-xs text-zinc-600 dark:text-zinc-400">{t('aiTemplateForm.required')}</span>
                                                    </label>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeImageInput(i)}
                                                        disabled={imageLabels.length <= 1}
                                                        className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
                                                        title={t('aiTemplateForm.deleteImageInputTooltip')}
                                                    >
                                                        <IconTrash size={15} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                        {imageLabels.length < MAX_IMAGE_INPUTS && (
                                            <button
                                                type="button"
                                                onClick={addImageInput}
                                                className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-primary/10 hover:bg-primary/20 text-primary dark:bg-primary/20 dark:hover:bg-primary/30 rounded-lg transition-colors"
                                            >
                                                <IconPlus size={13} /> {t('aiTemplateForm.addImageInput')}
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* Optional user note — shown to users right under the photo-upload box on this template's page */}
                                {requireUserImage && (
                                    <div>
                                        <label className="block text-sm font-medium mb-1 text-zinc-700 dark:text-zinc-300">
                                            {t('aiTemplateForm.userNote')} <span className="text-xs font-normal text-zinc-400">{t('aiTemplateForm.userNoteOptional')}</span>
                                        </label>
                                        <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-2">{t('aiTemplateForm.userNoteHint')}</p>
                                        <textarea
                                            value={userNote}
                                            onChange={e => setUserNote(e.target.value)}
                                            maxLength={500}
                                            rows={3}
                                            placeholder={t('aiTemplateForm.userNotePlaceholder')}
                                            className="w-full px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40 resize-y"
                                        />
                                        <p className="mt-1 text-[11px] text-right text-zinc-400 dark:text-zinc-500 tabular-nums">{userNote.length}/500</p>
                                    </div>
                                )}
                            </div>
                            </div>
                        </div>
                        </div>

                        <hr className="border-zinc-200 dark:border-zinc-700" />
                        <div className="flex justify-end gap-2 pb-2">
                            <button
                                className={btnSecondaryCls}
                                onClick={() => attemptLeave('/admin/ai-templates')}
                            >
                                {t('aiTemplateForm.cancel')}
                            </button>
                            <button
                                className={btnPrimaryCls}
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving
                                    ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                                    : <IconDeviceFloppy size={16} />}
                                {isEdit ? t('aiTemplateForm.updateTemplate') : t('aiTemplateForm.createTemplate')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Shimmer keyframes */}
            <style>{`
                @keyframes atg-shimmer-sweep {
                    0%   { transform: translateX(-120%) skewX(-12deg); }
                    100% { transform: translateX(280%) skewX(-12deg); }
                }
                @keyframes atg-glow-pulse {
                    0%, 100% { opacity: 0.28; }
                    50%       { opacity: 0.52; }
                }
                .atg-gen-photo {
                    filter: blur(10px) brightness(0.62) saturate(1.15);
                }
                .atg-gen-glow {
                    background: radial-gradient(ellipse at 50% 42%, rgba(230,138,0,0.5) 0%, rgba(180,90,0,0.3) 48%, transparent 72%);
                    animation: atg-glow-pulse 3s ease-in-out infinite;
                }
                .atg-gen-shimmer {
                    position: absolute;
                    top: 0; bottom: 0; left: 0;
                    width: 60%;
                    background: linear-gradient(
                        105deg,
                        transparent 15%,
                        rgba(255,255,255,0.09) 40%,
                        rgba(255,210,150,0.13) 52%,
                        rgba(255,255,255,0.06) 64%,
                        transparent 82%
                    );
                    animation: atg-shimmer-sweep 2.2s ease-in-out infinite;
                }
            `}</style>

            {/* AI: enhance the current prompt from this template's thumbnail */}
            <Dialog open={addFieldModalOpen} onOpenChange={(o) => { if (!enhancingPrompt) { setAddFieldModalOpen(o); if (!o) setAddFieldInstruction(''); } }}>
                <DialogContent className={`${resolveCtx ? 'sm:max-w-4xl' : 'sm:max-w-2xl'} rounded-2xl`} showClose={false}>
                    {/* Red close button */}
                    <button
                        type="button"
                        onClick={() => { if (!enhancingPrompt) { setAddFieldModalOpen(false); setAddFieldInstruction(''); } }}
                        disabled={enhancingPrompt}
                        aria-label={t('common.close')}
                        className="absolute right-4 top-4 z-10 w-8 h-8 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-colors disabled:opacity-50"
                    >
                        <IconX size={16} />
                    </button>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-base">
                            <IconSparkles size={18} className="text-primary" /> {t('aiTemplateForm.enhancePromptTitle')}
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400 -mt-1">{t('aiTemplateForm.enhancePromptDesc')}</p>

                    <div className="flex flex-col sm:flex-row gap-4">
                        {resolveCtx ? (
                            /* Resolve mode: the user's uploaded photo next to the generated
                               result they complained about, so the admin sees the problem. */
                            <div className="flex-shrink-0 flex gap-3">
                                {resolveCtx.uploadedImagePath && (
                                    <figure className="w-40">
                                        <img src={resolveCtx.uploadedImagePath} alt="" className="w-40 h-52 rounded-lg object-cover bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700" />
                                        <figcaption className="mt-1 text-[11px] text-center text-zinc-500 dark:text-zinc-400">{t('aiTemplateFeedback.originalImage')}</figcaption>
                                    </figure>
                                )}
                                {resolveCtx.aiImagePath && (
                                    <figure className="w-40">
                                        <img src={resolveCtx.aiImagePath} alt="" className="w-40 h-52 rounded-lg object-cover bg-zinc-100 dark:bg-zinc-800 border border-amber-300 dark:border-amber-700" />
                                        <figcaption className="mt-1 text-[11px] text-center text-zinc-500 dark:text-zinc-400">{t('aiTemplateFeedback.generatedImage')}</figcaption>
                                    </figure>
                                )}
                            </div>
                        ) : (thumbnailPreview || thumbnailUrl || existingThumbnail) && (
                            /* Thumbnail being scanned */
                            <div className="flex-shrink-0 w-28 h-28 rounded-lg overflow-hidden bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700">
                                <img src={thumbnailPreview || thumbnailUrl || existingThumbnail} alt="" className="w-full h-full object-cover" />
                            </div>
                        )}
                        <div className="flex-1 min-w-0">
                            <label className="block text-xs font-medium mb-1 text-zinc-600 dark:text-zinc-400">
                                {t('aiTemplateForm.analysisNoteLabel')}
                                <span className="ml-1.5 text-[10px] font-normal text-zinc-400 dark:text-zinc-500 bg-zinc-100 dark:bg-zinc-800 rounded-full px-1.5 py-0.5">
                                    {t('aiTemplateForm.analysisNoteOptional')}
                                </span>
                            </label>
                            <textarea
                                autoFocus
                                rows={resolveCtx ? 8 : 4}
                                placeholder={t('aiTemplateForm.enhancePromptNotePlaceholder')}
                                value={addFieldInstruction}
                                onChange={e => setAddFieldInstruction(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleEnhancePrompt(); }}
                                className="w-full px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                            />
                        </div>
                    </div>

                    <div className="flex items-center justify-end gap-2 mt-1">
                        <button
                            onClick={() => { setAddFieldModalOpen(false); setAddFieldInstruction(''); }}
                            disabled={enhancingPrompt}
                            className={btnSecondaryCls}
                        >
                            {t('aiTemplateForm.cancel')}
                        </button>
                        <button
                            onClick={handleEnhancePrompt}
                            disabled={enhancingPrompt || !prompt.trim()}
                            className={btnPrimaryCls}
                        >
                            {enhancingPrompt
                                ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> {t('aiTemplateForm.enhancing')}</>
                                : <><IconSparkles size={15} /> {t('aiTemplateForm.enhancePromptSubmit')}</>}
                        </button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Unsaved-changes confirmation before leaving the page */}
            <ConfirmDialog
                open={!!leaveTo}
                onClose={() => setLeaveTo(null)}
                onConfirm={() => { const to = leaveTo; setIsDirty(false); setLeaveTo(null); navigate(to); }}
                variant="danger"
                title={t('aiTemplateForm.unsavedTitle')}
                description={t('aiTemplateForm.unsavedDesc')}
                confirmLabel={t('aiTemplateForm.unsavedLeave')}
                cancelLabel={t('aiTemplateForm.unsavedStay')}
            />

            {/* Remove-saved-example confirmation (replaces window.confirm) */}
            <ConfirmDialog
                open={confirmRemoveThumb}
                onClose={() => setConfirmRemoveThumb(false)}
                onConfirm={() => { setConfirmRemoveThumb(false); doClearThumbnail(); }}
                variant="danger"
                title={t('aiTemplateForm.removeExampleConfirm')}
                description={t('aiTemplateForm.removeExampleConfirmDetail')}
            />

            {/* Crop + rotate stage for the image-path upload (same as user panel) */}
            {cropSrc && (
                <div className="fixed inset-0 z-[200] flex flex-col bg-white dark:bg-zinc-900" style={{ height: '100dvh' }}>
                    <CropStage
                        imageSrc={cropSrc}
                        fileName={cropName}
                        isMobile={isMobile}
                        t={t}
                        aspect={null}
                        hint=""
                        initialCrop={cropInitial}
                        showSkip
                        onClose={handleCropClose}
                        onApply={handleCropApply}
                        onSkip={handleCropSkip}
                        onError={handleCropError}
                    />
                </div>
            )}
        </div>
    );
}
